// ============================================
// FILE: src/components/SharkBase.jsx
// SharkBase — AI Pentest Agent
// Design: v3 (full rewrite)
// ============================================

import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { SecureAPI } from '../config.js';

// ── Constants ─────────────────────────────────────────────────
const SEV = {
  CRITICAL: { c:'rgba(252,99,99,.95)',  bg:'rgba(252,99,99,.09)',  bd:'rgba(252,99,99,.24)'  },
  HIGH:     { c:'rgba(251,150,60,.95)', bg:'rgba(251,150,60,.09)', bd:'rgba(251,150,60,.2)'  },
  MEDIUM:   { c:'rgba(240, 200, 22, 0.88)', bg:'rgba(250,210,40,.07)', bd:'rgba(250, 211, 40, 0.53)' },
  LOW:      { c:'#22c55e',bg:'var(--greenb)',bd:'rgba(22, 101, 52, 0.07)' },
  INFO:     { c:'rgba(125,211,252,.5)', bg:'rgba(125,211,252,.03)',bd:'rgba(125,211,252,.1)'  },
};
const TOOL_ICON = {
  // ── Startup (2 steps only) ────────────────────────────────
  'Setup':                 '✓',
  'Auth detection':        '⊕',

  // ── GitHub scanner ────────────────────────────────────────
  'Inspect':               '⬡',
  'Clone':                 '⊕',
  'Sandbox':               '⊞',
  'Walk codebase':         '⊕',
  'Dep scan':              '◈',
  'Scan 25%':              '◈',
  'Scan 50%':              '◈',
  'Scan 75%':              '◈',
  'Analyze':               '◉',
  'Summarize':             '⊙',
  sast_scan:               '◈',

  // ── Recon ─────────────────────────────────────────────────
  deep_study_site:         '◉',
  fingerprint_app:         '◉',
  fetch_page:              '⬡',
  browser_fetch:           '⬡',
  crawl_site:              '⊕',
  check_headers:           '⊞',
  check_tls:               '⌁',
  check_source_maps:       '◈',
  dns_enumerate:           '⊛',
  enumerate_files:         '⊘',
  check_exposed_file:      '⊘',
  scan_js_bundle:          '◈',

  // ── Active ────────────────────────────────────────────────
  test_sqli:               '⌬',
  test_xss:                '⌬',
  test_ssrf:               '⌬',
  test_lfi:                '⌬',
  test_csrf:               '⌬',
  test_xxe:                '⌬',
  test_auth_bypass:        '⌬',
  test_idor:               '⌬',
  test_jwt:                '⌬',
  test_cors:               '⊗',
  test_race_condition:     '⌬',
  test_host_header:        '⌬',
  test_header_injection:   '⌬',
  test_prototype_pollution:'⌬',
  test_open_redirect:      '⌬',
  test_business_logic:     '⌬',
  test_graphql:            '⇌',
  probe_api_endpoint:      '⇌',

  // ── Auth ──────────────────────────────────────────────────
  create_test_account:     '⊙',
  create_second_account:   '⊙',
  login_with_credentials:  '⊙',

  // ── Chain / verify ────────────────────────────────────────
  verify_finding:          '⊗',
  analyze_response:        '◈',
  check_callback_hits:     '⊘',
  run_chain:               '⊕',
  parallel_scan:           '⊞',
  dispatch_subagents:      '⊞',
  recon_extended:          '⊛',

  // ── Report ────────────────────────────────────────────────
  finish_report:           '⊙',
};

const TOOL_LABEL = {
  // ── Startup (2 steps only) ────────────────────────────────
  'Setup':                 'Setting up',
  'Auth detection':        'Auth detection',

  // ── GitHub scanner ────────────────────────────────────────
  'Inspect':               'Inspecting repository',
  'Clone':                 'Cloning repository',
  'Sandbox':               'Spinning up sandbox',
  'Walk codebase':         'Walking file tree',
  'Dep scan':              'Dependency scan',
  'Scan 25%':              'Scanning files (25%)',
  'Scan 50%':              'Scanning files (50%)',
  'Scan 75%':              'Scanning files (75%)',
  'Analyze':               'Analysing findings',
  'Summarize':             'Writing report',
  sast_scan:               'Code scan',

  // ── Recon ─────────────────────────────────────────────────
  deep_study_site:         'Studying the site',
  fingerprint_app:         'Reading stack',
  fetch_page:              'Fetching page',
  browser_fetch:           'Loading page',
  crawl_site:              'Crawling site',
  check_headers:           'Security headers',
  check_tls:               'TLS / HTTPS',
  check_source_maps:       'Source maps',
  dns_enumerate:           'DNS records',
  enumerate_files:         'Sensitive files',
  check_exposed_file:      'Exposed file check',
  scan_js_bundle:          'JS bundle scan',

  // ── Active ────────────────────────────────────────────────
  test_sqli:               'SQL injection',
  test_xss:                'XSS',
  test_ssrf:               'SSRF',
  test_lfi:                'Path traversal',
  test_csrf:               'CSRF',
  test_xxe:                'XML injection',
  test_auth_bypass:        'Auth bypass',
  test_idor:               'Access control',
  test_jwt:                'JWT analysis',
  test_cors:               'CORS policy',
  test_race_condition:     'Race conditions',
  test_host_header:        'Host header',
  test_header_injection:   'Header injection',
  test_prototype_pollution:'Prototype pollution',
  test_open_redirect:      'Open redirect',
  test_business_logic:     'Business logic',
  test_graphql:            'GraphQL',
  probe_api_endpoint:      'API endpoint',

  // ── Auth ──────────────────────────────────────────────────
  create_test_account:     'Creating test account',
  create_second_account:   'Creating second account',
  login_with_credentials:  'Signing in',

  // ── Chain / verify ────────────────────────────────────────
  verify_finding:          'Verifying result',
  analyze_response:        'Analysing response',
  check_callback_hits:     'Checking callbacks',
  run_chain:               'Running attack chain',
  parallel_scan:           'Parallel checks',
  dispatch_subagents:      'Running checks',
  recon_extended:          'Extended recon',

  // ── Report ────────────────────────────────────────────────
  finish_report:           'Compiling report',
};

const SCANNING_LABEL = {
  fetch_page:               (arg) => `Fetching ${arg || 'page'}…`,
  check_headers:            (arg) => `Checking security headers on ${arg || 'site'}…`,
  check_tls:                (arg) => `Checking TLS cert for ${arg || 'domain'}…`,
  scan_js_bundle:           (arg) => `Scanning JS files for secrets…`,
  check_exposed_file:       (arg) => `Probing for exposed files…`,
  enumerate_files:          (arg) => `Checking sensitive paths on ${arg || 'site'}…`,
  dns_enumerate:            (arg) => `Looking up DNS records for ${arg || 'domain'}…`,
  probe_api_endpoint:       (arg) => `Probing ${arg || 'API endpoint'}…`,
  test_cors:                (arg) => `Testing CORS on ${arg || 'site'}…`,
  test_sqli:                (arg) => `Testing SQL injection…`,
  test_xss:                 (arg) => `Testing for XSS…`,
  test_ssrf:                (arg) => `Testing for SSRF…`,
  test_auth_bypass:         (arg) => `Checking for auth bypass…`,
  test_idor:                (arg) => `Testing access control…`,
  test_lfi:                 (arg) => `Testing path traversal…`,
  test_csrf:                (arg) => `Testing CSRF protection…`,
  test_jwt:                 (arg) => `Analysing JWT token…`,
  test_race_condition:      (arg) => `Testing for race conditions…`,
  test_host_header:         (arg) => `Testing host header…`,
  test_header_injection:    (arg) => `Testing header injection…`,
  test_prototype_pollution: (arg) => `Testing prototype pollution…`,
  test_xxe:                 (arg) => `Testing XML injection…`,
  finish_report:            ()    => `Putting together your report…`,
  browser_fetch:            (arg) => `Loading ${arg || 'page'}…`,
  crawl_site:               (arg) => `Crawling ${arg || 'site'}…`,
  fingerprint_app:          (arg) => `Reading ${arg || 'site'} stack…`,
  parallel_scan:            ()    => `Running checks…`,
  dispatch_subagents:       ()    => `Sub agents working…`,
  analyze_response:         ()    => `Looking for issues to chain…`,
  test_business_logic:      (arg) => `Testing business logic…`,
  test_graphql:             (arg) => `Auditing GraphQL…`,
  recon_extended:           (arg) => `Running extended recon on ${arg || 'domain'}…`,
  verify_finding:           (arg) => `Verifying finding…`,
  create_test_account:      (arg) => `Creating a test account…`,
  create_second_account:    ()    => `Creating second account…`,
  login_with_credentials:   (arg) => `Signing in…`,
  check_callback_hits:      ()    => `Checking for blind callbacks…`,
  run_chain:                ()    => `Running checks…`,
  deep_study_site:          (arg) => `Reading ${arg || 'site'}…`,
};

function scoreColor(s) {
  return s >= 70 ? 'rgba(110,231,183,.9)' : s >= 50 ? 'rgba(250,210,40,.85)' : s >= 30 ? 'rgba(251,150,60,.9)' : 'rgba(252,99,99,.9)';
}
function scoreLabel(s) {
  return s >= 70 ? 'Secure' : s >= 50 ? 'Low Risk' : s >= 30 ? 'High Risk' : 'Critical';
}

// ── CSS ───────────────────────────────────────────────────────
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=Geist+Mono:wght@300;400;500;600&family=Geist:wght@300;400;500;600;700&display=swap');

*,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}

:root{
  --bg:#ffffff;
  --bg1:#f8f8f8;
  --bg2:#f0f0f0;
  --bg3:#e4e4e4;
  --bd:rgba(0,0,0,.08);
  --bdh:rgba(0,0,0,.15);
  --t1:#0a0a0a;
  --t2:rgba(10,10,10,.6);
  --t3:rgba(10,10,10,.35);
  --t4:rgba(10,10,10,.12);
  --ac:#0a0a0a;
  --acb:rgba(10,10,10,.08);
  --green:#16a34a;
  --greenb:#dcfce7;
  --green-text:#15803d;
  --red:#dc2626;
  --redb:#fee2e2;
  --red-text:#b91c1c;
  --orange:#ea580c;
  --orangeb:#ffedd5;
  --orange-text:#c2410c;
  --yellow:#ca8a04;
  --yellowb:#fef9c3;
  --yellow-text:#a16207;
  --blue:#2563eb;
  --blueb:#dbeafe;
  --blue-text:#1d4ed8;
  --serif:'Instrument Serif',Georgia,serif;
  --sans:'Geist',sans-serif;
  --mono:'Geist Mono',monospace;
  --shadow-sm:0 1px 3px rgba(0,0,0,.06),0 1px 2px rgba(0,0,0,.04);
  --shadow-md:0 4px 16px rgba(0,0,0,.08),0 2px 6px rgba(0,0,0,.04);
  --shadow-lg:0 12px 40px rgba(0,0,0,.10),0 4px 12px rgba(0,0,0,.06);
}

html,body,#root{height:100%;background:var(--bg);}
body{font-family:var(--sans);color:var(--t1);-webkit-font-smoothing:antialiased;font-size:15px;}

/* ── Keyframes ─────────────────────────────── */
@keyframes spin       { to{transform:rotate(360deg)} }
@keyframes fadeUp     { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
@keyframes fadeIn     { from{opacity:0} to{opacity:1} }
@keyframes shimmer {
  0%  { background-position:200% center }
  100%{ background-position:-200% center }
}
@keyframes blurPulse {
  0%,100%{ filter:blur(0px);  opacity:1   }
  50%    { filter:blur(.8px); opacity:.72 }
}
@keyframes skelMove {
  0%  { background-position:200% 0 }
  100%{ background-position:-200% 0 }
}
@keyframes rowIn {
  from{ opacity:0; transform:translateX(-4px) }
  to  { opacity:1; transform:translateX(0) }
}
@keyframes msgIn {
  from{ opacity:0; transform:translateY(4px) }
  to  { opacity:1; transform:translateY(0) }
}
@keyframes livePulse {
  0%,100%{ opacity:.5; transform:scale(1)   }
  50%    { opacity:1;  transform:scale(1.3) }
}
@keyframes scanMove {
  0%  { transform:translateY(-100%) }
  100%{ transform:translateY(500%)  }
}
@keyframes progBreath {
  0%,100%{ opacity:.65 }
  50%    { opacity:1   }
}
@keyframes orbFloat {
  0%,100%{ transform:translate(0,0)        }
  50%    { transform:translate(14px,-10px) }
}
@keyframes pulse {
  0%,100%{ opacity:1; transform:scale(1)   }
  50%    { opacity:.6;transform:scale(.96) }
}
@keyframes blurAppear {
  from{ opacity:0; filter:blur(12px); transform:scale(.97) }
  to  { opacity:1; filter:blur(0px);  transform:scale(1)   }
}
@keyframes blurFadeOut {
  from{ opacity:1; filter:blur(0px);  transform:scale(1)   }
  to  { opacity:0; filter:blur(10px); transform:scale(.98) }
}
@keyframes wordIn {
  from{ opacity:0; filter:blur(6px) }
  to  { opacity:1; filter:blur(0px) }
}
@keyframes inputSlideUp {
  from{ opacity:0; filter:blur(8px); transform:translateY(14px) scale(.98) }
  to  { opacity:1; filter:blur(0px); transform:translateY(0)    scale(1)   }
}
@keyframes chipFadeIn {
  from{ opacity:0; filter:blur(4px); transform:translateY(6px) }
  to  { opacity:1; filter:blur(0px); transform:translateY(0)   }
}
@keyframes slideInLeft {
  from{ opacity:0; transform:translateX(-16px) }
  to  { opacity:1; transform:translateX(0) }
}
@keyframes slideOutLeft {
  from{ opacity:1; transform:translateX(0) }
  to  { opacity:0; transform:translateX(-16px) }
}
@keyframes sheetUp {
  from{ opacity:0; transform:translateY(100%) }
  to  { opacity:1; transform:translateY(0) }
}
@keyframes sheetDown {
  from{ opacity:1; transform:translateY(0) }
  to  { opacity:0; transform:translateY(100%) }
}
  @keyframes severityFlash {
  0%   { transform: scale(1);    filter: brightness(1);   }
  30%  { transform: scale(1.15); filter: brightness(1.4); }
  60%  { transform: scale(1.05); filter: brightness(1.2); }
  100% { transform: scale(1);    filter: brightness(1);   }
}
/* ── Shimmer utilities ── */
.shimmer-text {
  background:linear-gradient(90deg,var(--t2) 20%,var(--t1) 48%,var(--t3) 52%,var(--t2) 80%);
  background-size:200% auto;
  -webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;
  animation:shimmer 2.4s linear infinite;
}
.blur-shimmer{ animation:blurPulse 1.8s ease-in-out infinite; }
/* False positive removal — slides right and shrinks away */
@keyframes fpRemove {
  0%   { opacity: 1; transform: translateX(0);    max-height: 200px; }
  100% { opacity: 0; transform: translateX(10px); max-height: 0;     }
}

/* New finding appearing — existing animation you likely already have */
@keyframes blurAppear {
  from {
    opacity:   0;
    filter:    blur(4px);
    transform: translateY(4px);
  }
  to {
    opacity:   1;
    filter:    blur(0);
    transform: translateY(0);
  }
}
/* ── Background ── */
.sb-orb{ position:fixed;border-radius:50%;filter:blur(120px);pointer-events:none;z-index:0; }
.sb-orb-1{ width:500px;height:500px;background:rgba(0,0,0,.012);top:-200px;right:-140px; }
.sb-orb-2{ width:400px;height:400px;background:rgba(0,0,0,.008);bottom:-120px;left:-80px; }
.sb-grid{
  position:fixed;inset:0;z-index:0;pointer-events:none;
  background-image:
    linear-gradient(rgba(0,0,0,.03) 1px,transparent 1px),
    linear-gradient(90deg,rgba(0,0,0,.03) 1px,transparent 1px);
  background-size:48px 48px;
  mask-image:radial-gradient(ellipse 80% 80% at 50% 40%,black 30%,transparent 100%);
  -webkit-mask-image:radial-gradient(ellipse 80% 80% at 50% 40%,black 30%,transparent 100%);
}

/* ── Shell ── */
.sb-shell{ display:flex;flex-direction:column;height:100vh;overflow:hidden;position:relative;z-index:1; }

/* ── Nav ── */
.sb-nav{
  height:56px;display:flex;align-items:center;gap:10px;
  padding:0 20px;border-bottom:1px solid var(--bd);flex-shrink:0;
  background:rgba(255,255,255,.95);backdrop-filter:blur(20px);
  position:relative;z-index:100;
}
.sb-nav::after{
  content:'';position:absolute;bottom:0;left:0;right:0;height:1px;
  background:linear-gradient(90deg,transparent,rgba(0,0,0,.06),transparent);
}
.sb-logo{
  width:26px;height:26px;overflow:hidden;
  display:flex;align-items:center;justify-content:center;flex-shrink:0;
}
.sb-logo img{ width:100%;height:100%;object-fit:contain; }
.sb-brand{ font-family:var(--serif);font-weight:700;font-size:17px;line-height:1;color:var(--t1); }
.sb-ver{ font-family:var(--mono);font-size:10px;color:var(--t3);margin-top:1px; }

/* ── Nav toggle ── */
.sb-toggle{
  display:flex;align-items:center;
  background:#f0f0f0;
  border:1px solid var(--bd);border-radius:20px;padding:3px;
  position:relative;box-shadow:var(--shadow-sm);
}
.sb-thumb{
  position:absolute;top:3px;bottom:3px;width:calc(33.33% - 2px);
  border-radius:17px;background:#fff;border:1px solid var(--bd);
  transition:transform .4s cubic-bezier(.4,0,.2,1);pointer-events:none;
  box-shadow:0 1px 4px rgba(0,0,0,.1);
}
.sb-thumb.t1{transform:translateX(0);}
.sb-thumb.t2{transform:translateX(100%);}
.sb-thumb.t3{transform:translateX(200%);}
.sb-tab{
  position:relative;z-index:1;padding:6px 20px;
  font-family:var(--mono);font-size:12px;font-weight:500;
  letter-spacing:.04em;text-transform:uppercase;
  color:rgba(86,86,86,1);background:transparent;border:none;cursor:pointer;transition:color .2s;
  white-space:nowrap;
}
.sb-tab.on{ color:var(--t1);font-weight:700; }
.sb-tab:hover:not(.on){ color:var(--t2); }

.sb-npill{
  display:flex;align-items:center;gap:5px;padding:5px 10px;
  border-radius:999px;border:1px solid var(--bd);background:#fff;
  font-family:var(--mono);font-size:11px;color:var(--t3);box-shadow:var(--shadow-sm);
  white-space:nowrap;flex-shrink:1;min-width:0;max-width:140px;
  overflow:hidden;text-overflow:ellipsis;
}
.sb-sdot{ width:6px;height:6px;border-radius:50%; }

/* ── Body ── */
.sb-body{ flex:1;display:flex;overflow:hidden;background:var(--bg2);gap:8px;padding:8px;position:relative; }

/* ── Agent col ── */
.sb-agent-col{
  flex:1;display:flex;flex-direction:column;
  overflow:hidden;background:#fafafa;
  border-radius:14px;border:1px solid var(--bd);
  box-shadow:var(--shadow-md);position:relative;
}

/* ── Thought stream ── */
.sb-stream{
  flex:1;overflow-y:auto;padding:0;
  scrollbar-width:thin;scrollbar-color:var(--bg3) transparent;
  display:flex;flex-direction:column;
}
.sb-stream::-webkit-scrollbar{ width:3px; }
.sb-stream::-webkit-scrollbar-thumb{ background:var(--bg3); }


@keyframes shimmer {
  0%   { background-position: 200% center; }
  100% { background-position: -200% center; }
}
@keyframes pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50%       { opacity: .4; transform: scale(.8); }
}
@keyframes fadeUp {
  from { opacity: 0; transform: translateY(4px); }
  to   { opacity: 1; transform: translateY(0); }
}
  /* ── Mobile sidebar ── */
.sb-mob-sidebar-backdrop{
  display:none;position:fixed;inset:0;z-index:350;
  background:rgba(0,0,0,.18);backdrop-filter:blur(3px);
  animation:fadeIn .2s ease both;
}
.sb-mob-sidebar{
  position:fixed;top:0;left:0;bottom:0;width:280px;z-index:351;
  background:#fff;border-right:1px solid var(--bd);
  box-shadow:4px 0 32px rgba(0,0,0,.10);
  display:flex;flex-direction:column;overflow:hidden;
  animation:slideInLeft .26s cubic-bezier(.22,1,.36,1) both;
}
.sb-mob-sidebar.closing{
  animation:slideOutLeft .2s cubic-bezier(.4,0,.2,1) both;
}
.sb-mob-sidebar-hdr{
  height:52px;display:flex;align-items:center;justify-content:space-between;
  padding:0 16px;border-bottom:1px solid var(--bd);flex-shrink:0;
  background:var(--bg1);
}
.sb-mob-ham{
  display:none;width:34px;height:34px;border-radius:50px;
  background:#000;border:1px solid var(--bd);
  align-items:center;justify-content:center;
  cursor:pointer;color:#fff;transition:all .15s;flex-shrink:0;
}
.sb-mob-ham:hover{background:var(--bg2);color:var(--t1);}
.sb-mob-ham{display:none;}
@media(max-width:768px){
  .sb-mob-ham{ display:flex; }
  .sb-mob-sidebar-backdrop{ display:block; }
  .sb-hide-mobile{ display:none !important; }
  .sb-show-mobile{ display:flex !important; }
}
/* ── Left sidebar ── */
.sb-left-panel{
  width:230px;flex-shrink:0;display:flex;flex-direction:column;
  overflow:hidden;background:#fff;border-radius:14px;
  border:1px solid var(--bd);box-shadow:var(--shadow-md);
  animation:blurAppear .3s ease both;
}
.sb-left-panel-hdr{
  flex-shrink:0;padding:0 14px;height:42px;
  display:flex;align-items:center;justify-content:space-between;
  border-bottom:1px solid var(--bd);background:var(--bg1);
  border-radius:14px 14px 0 0;
}
.sb-left-panel-title{
  font-family:var(--mono);font-size:11px;font-weight:600;
  letter-spacing:.1em;text-transform:uppercase;color:var(--t3);
}
.sb-left-panel-body{
  flex:1;overflow-y:auto;text-align:left;
  scrollbar-width:thin;scrollbar-color:var(--bg3) transparent;
}
.sb-left-panel-body::-webkit-scrollbar{ width:2px; }
.sb-left-panel-body::-webkit-scrollbar-thumb{ background:var(--bg3); }

/* Usage block */
.sb-usage-block{ padding:14px;border-bottom:1px solid var(--bd); }
.sb-usage-row{ margin-bottom:14px; }
.sb-usage-row:last-child{ margin-bottom:0; }
.sb-usage-label{ display:flex;justify-content:space-between;align-items:baseline;margin-bottom:6px; }
.sb-usage-name{ font-family:var(--mono);font-size:12px;color:var(--t2);font-weight:500; }
.sb-usage-val{ font-family:var(--mono);font-size:11px;color:var(--t3); }
.sb-usage-bar{ height:3px;background:var(--bg3);border-radius:99px;overflow:hidden; }
.sb-usage-fill{ height:100%;background:var(--t1);border-radius:99px;transition:width .6s cubic-bezier(.4,0,.2,1); }
.sb-usage-fill.warn{ background:var(--orange); }
.sb-usage-fill.crit{ background:var(--red); }

/* Recent drawer */
.sb-recent-drawer{
  position:fixed;top:0;left:0;bottom:0;width:300px;z-index:300;
  background:#fff;border-right:1px solid var(--bd);
  box-shadow:var(--shadow-lg);display:flex;flex-direction:column;
  animation:drawerIn .28s cubic-bezier(.22,1,.36,1) both;
}
.sb-recent-drawer.closing{ animation:drawerOut .22s cubic-bezier(.4,0,.2,1) both; }
@keyframes drawerIn{
  from{ opacity:0; transform:translateX(-100%) }
  to  { opacity:1; transform:translateX(0) }
}
@keyframes drawerOut{
  from{ opacity:1; transform:translateX(0) }
  to  { opacity:0; transform:translateX(-100%) }
}
.sb-drawer-backdrop{
  position:fixed;inset:0;z-index:299;
  background:rgba(0,0,0,.12);backdrop-filter:blur(2px);
  animation:fadeIn .2s ease both;
}
.sb-drawer-hdr{
  height:56px;display:flex;align-items:center;justify-content:space-between;
  padding:0 20px;border-bottom:1px solid var(--bd);flex-shrink:0;
}
.sb-drawer-title{
  font-family:var(--mono);font-size:13px;font-weight:600;
  letter-spacing:.06em;text-transform:uppercase;color:var(--t1);
}
.sb-drawer-close{
  width:28px;height:28px;border-radius:50%;
  background:transparent;border:1px solid var(--bd);
  display:flex;align-items:center;justify-content:center;
  cursor:pointer;color:var(--t3);transition:all .15s;
}
.sb-drawer-close:hover{ background:var(--bg2);color:var(--t1); }
.sb-drawer-body{
  flex:1;overflow-y:auto;padding:14px;
  scrollbar-width:thin;scrollbar-color:var(--bg3) transparent;
}

/* Nav history button */
.sb-nav-history-btn{
  width:32px;height:32px;border-radius:8px;
  background:transparent;border:1px solid var(--bd);
  display:flex;align-items:center;justify-content:center;
  cursor:pointer;color:var(--t3);transition:all .15s;flex-shrink:0;
}
.sb-nav-history-btn:hover{ background:var(--bg2);color:var(--t1);border-color:var(--bdh); }
.sb-nav-history-btn.on{ background:var(--t1);color:#fff;border-color:var(--t1); }

/* Recent sidebar inside agent col */
.sb-recent-sidebar{
  width:0;flex-shrink:0;overflow:hidden;
  transition:width .38s cubic-bezier(.22,1,.36,1);
  background:#fff;border-right:0px solid transparent;
  display:flex;flex-direction:column;border-radius:0;
}
.sb-recent-sidebar.open{ width:280px;border-right:1px solid var(--bd); }
.sb-recent-sidebar-inner{ width:240px;display:flex;flex-direction:column;height:100%;overflow:hidden; }
.sb-recent-sidebar-hdr{
  height:46px;display:flex;align-items:center;justify-content:space-between;
  padding:0 16px;border-bottom:1px solid var(--bd);flex-shrink:0;
  border-radius:14px 0 0 0;
}
.sb-recent-sidebar-close{
  width:26px;height:26px;border-radius:50%;
  background:var(--t1);border:none;
  display:flex;align-items:center;justify-content:center;
  cursor:pointer;color:#fff;transition:all .15s;flex-shrink:0;
}
.sb-recent-sidebar-close:hover{ background:#333; }
.sb-recent-sidebar-body{
  flex:1;overflow-y:auto;padding:10px;
  scrollbar-width:thin;scrollbar-color:var(--bg3) transparent;
}
.sb-recent-sidebar-body::-webkit-scrollbar{ width:2px; }
.sb-recent-sidebar-body::-webkit-scrollbar-thumb{ background:var(--bg3); }

.sb-nav-tagline{
  font-family:var(--mono);font-weight:700;color:#636363;
  background:#ececec;padding:5px 14px;border-radius:999px;
  font-size:13px;letter-spacing:.01em;white-space:nowrap;
}

/* Mobile recent sheet */
.sb-mob-recent-backdrop{
  display:none;position:fixed;inset:0;z-index:400;
  background:rgba(0,0,0,.18);backdrop-filter:blur(3px);
  animation:fadeIn .2s ease both;
}
.sb-mob-recent-sheet{
  display:none;position:fixed;bottom:0;left:0;right:0;z-index:401;
  background:#fff;border-radius:20px 20px 0 0;
  border:1px solid var(--bd);box-shadow:0 -8px 40px rgba(0,0,0,.12);
  max-height:72vh;display:flex;flex-direction:column;
  animation:sheetUp .3s cubic-bezier(.22,1,.36,1) both;
}
.sb-mob-recent-sheet.closing{ animation:sheetDown .22s cubic-bezier(.4,0,.2,1) both; }
.sb-mob-recent-handle{
  width:36px;height:4px;border-radius:2px;
  background:var(--bg3);margin:12px auto 0;flex-shrink:0;
}
.sb-mob-recent-hdr{
  display:flex;align-items:center;justify-content:space-between;
  padding:14px 20px 12px;flex-shrink:0;border-bottom:1px solid var(--bd);
}
.sb-mob-recent-title{
  font-family:var(--mono);font-size:13px;font-weight:600;
  letter-spacing:.06em;text-transform:uppercase;color:var(--t1);
}
.sb-mob-recent-close{
  width:28px;height:28px;border-radius:50%;
  background:var(--t1);border:none;
  display:flex;align-items:center;justify-content:center;
  cursor:pointer;color:#fff;
}
.sb-mob-recent-body{
  flex:1;overflow-y:auto;padding:14px;
  scrollbar-width:thin;scrollbar-color:var(--bg3) transparent;
}

/* Recent scans button */
.sb-recent-btn{
  position:absolute;top:14px;left:14px;
  display:flex;align-items:center;gap:7px;
  padding:7px 14px;background:var(--t1);
  border:none;border-radius:8px;
  font-family:var(--mono);font-size:12px;font-weight:500;
  color:#fff;cursor:pointer;transition:all .18s;
  letter-spacing:.03em;box-shadow:0 2px 8px rgba(0,0,0,.12);z-index:10;
}
.sb-recent-btn:hover{ background:#333;transform:translateY(-1px); }
.sb-mobile-nav{ display:none !important; }

@media(max-width:768px){
  .sb-mobile-nav.scan-active{ display:flex !important; }
}

.sb-mob-recent-btn{
  display:none;align-items:center;gap:7px;
  padding:10px 18px;background:var(--t1);border:none;border-radius:999px;
  font-family:var(--mono);font-size:13px;font-weight:600;
  color:#fff;cursor:pointer;box-shadow:0 2px 12px rgba(0,0,0,.18);
  transition:all .18s;margin-top:28px;
  animation:chipFadeIn .4s ease both;animation-delay:.4s;
}
.sb-mob-recent-btn:hover{ background:#333;transform:translateY(-1px); }

/* ═══════════════════════════════════════════════
   EMPTY STATE
═══════════════════════════════════════════════ */
.sb-empty{
  display:flex;flex-direction:column;align-items:center;
  justify-content:center;height:100%;gap:0;
  padding:40px 24px;text-align:center;
  background:
    linear-gradient(rgba(0,0,0,.03) 1px,transparent 1px),
    linear-gradient(90deg,rgba(0,0,0,.03) 1px,transparent 1px);
  background-size:32px 32px;
}
.sb-empty-title{
  font-weight:400;font-family:var(--serif);
  font-size:2.8rem;line-height:1.12;
  letter-spacing:-.025em;color:var(--t1);
  margin-bottom:32px;
  animation:blurAppear .6s cubic-bezier(.22,1,.36,1) both;
}
.sb-empty-word{ display:inline;animation:wordIn .5s ease both; }
.sb-empty-input-wrap{
  width:100%;max-width:640px;
  background:#fff;border:1px solid var(--bd);
  border-radius:20px;padding:18px 18px 14px;
  box-shadow:0 4px 8px rgba(0,0,0,.07),0 1px 4px rgba(0,0,0,.04);
  animation:inputSlideUp .5s cubic-bezier(.22,1,.36,1) both;
  animation-delay:.15s;
}
.sb-empty-url{
  width:100%;background:transparent;border:none;outline:none;
  font-family:var(--sans);font-size:15px;color:var(--t1);
  line-height:1.6;min-height:28px;
}
.sb-empty-url::placeholder{ color:var(--t3); }
.sb-empty-footer{
  display:flex;align-items:center;justify-content:space-between;
  margin-top:14px;padding-top:12px;border-top:1px solid var(--bd);
}
.sb-empty-left{ display:flex;align-items:center;gap:8px; }
.sb-empty-plus{
  width:30px;height:30px;border-radius:50px;
  background:transparent;border:1px solid var(--bd);
  display:flex;align-items:center;justify-content:center;
  cursor:pointer;font-size:16px;color:var(--t3);
  font-weight:300;line-height:1;transition:all .15s;
}
.sb-empty-plus:hover{ background:var(--bg2);color:var(--t2); }
.sb-empty-tos{ display:flex;align-items:center;gap:7px;cursor:pointer;user-select:none; }
.sb-empty-tos-cb{
  width:20px;height:20px;border-radius:26px;
  border:1.5px solid var(--bdh);background:#fff;
  display:flex;align-items:center;justify-content:center;
  flex-shrink:0;transition:all .18s;
}
.sb-empty-tos-cb.on{ background:#1193eaff;border:1.5px solid transparent;box-shadow:0 1px 4px rgba(138,204,255,.15); }
.sb-empty-tos-cb:hover:not(.on){ border-color:var(--t1);background:var(--bg1); }
.sb-empty-tos-lbl{ font-family:var(--mono);font-size:12px;color:black; }
.sb-empty-submit{
  width:36px;height:36px;border-radius:50%;
  background:var(--bg2);border:1px solid var(--bd);
  display:flex;align-items:center;justify-content:center;
  cursor:pointer;transition:all .18s;color:var(--t3);
  box-shadow:var(--shadow-sm);font-size:14px;
}
.sb-empty-submit:hover,
.sb-empty-submit.active{ background:var(--t1);color:#fff;border-color:var(--t1);box-shadow:0 2px 8px rgba(0,0,0,.15); }
.sb-empty-submit:disabled{ opacity:.5;cursor:not-allowed; }
.sb-empty-chips{
  display:flex;flex-wrap:nowrap;justify-content:center;
  gap:8px;margin-top:16px;
  animation:chipFadeIn .4s ease both;animation-delay:.28s;
  overflow:visible;max-width:100%;
  padding:4px 2px;
}
.sb-empty-chip{
  display:flex;align-items:center;gap:7px;
  padding:8px 16px;border-radius:999px;
  border:1px solid var(--bd);background:#fff;
  font-family:var(--sans);font-size:13px;font-weight:500;
  color:var(--t2);cursor:pointer;transition:all .15s;
  box-shadow:var(--shadow-sm);
}
.sb-empty-chip:hover{ background:var(--bg1);border-color:var(--bdh);color:var(--t1); }

/* History */
.sb-hist{ width:100%;max-width:640px;margin-top:24px;animation:chipFadeIn .4s ease both;animation-delay:.36s; }
.sb-hist-lbl{
  font-family:var(--mono);font-size:11px;color:var(--t3);
  letter-spacing:.1em;text-transform:uppercase;margin-bottom:10px;text-align:left;
}
.sb-hcard{
  display:flex;justify-content:space-between;align-items:center;
  padding:12px 14px;border-radius:12px;border:1px solid var(--bd);
  background:#fff;cursor:pointer;transition:all .18s;margin-bottom:4px;
  box-shadow:var(--shadow-sm);
}
.sb-hcard:hover{
  border-color:var(--bdh);background:#fff;
  box-shadow:var(--shadow-md);transform:translateY(-1px);
}
.sb-hurl{ font-family:var(--mono);font-size:13px;color:var(--t1);font-weight:500; }
.sb-hmeta{ font-size:12px;color:var(--t3);margin-top:2px; }

/* ── Divider ── */
.sb-divider{ display:flex;align-items:center;gap:10px;padding:10px 16px; }
.sb-div-line{ flex:1;height:1px;background:var(--bd); }
.sb-div-lbl{ font-family:var(--mono);font-size:11px;color:rgba(36, 36, 36, 0.94);letter-spacing:.1em;text-transform:uppercase; }
/* ── Thinking bubble ── */
.sb-think{
  padding:12px 18px 12px 16px;border-bottom:1px solid rgba(0,0,0,.04);
  animation:rowIn .18s ease both;text-align:left;
}
.sb-think-body{ font-family:var(--sans);font-size:13px;color:var(--t2);line-height:1.78; }
.sb-cursor{
  display:inline-block;width:5px;height:.75em;
  background:var(--t1);margin-left:2px;vertical-align:middle;
  animation:livePulse .75s infinite;
}
.sb-think-live .sb-think-body{
  background:linear-gradient(90deg,var(--t2) 20%,var(--t1) 48%,var(--t3) 52%,var(--t2) 80%);
  background-size:200% auto;
  -webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;
  animation:shimmer 2.2s linear infinite;
}

/* ── Tool pair card ── */
.sb-pair{
  margin:4px 14px;border-radius:9px;border:1px solid var(--bd);
  background:#fff;animation:rowIn .16s ease both;overflow:hidden;
  box-shadow:var(--shadow-sm);
}
.sb-pair-tool{
  display:flex;align-items:center;gap:9px;padding:8px 12px;
  cursor:pointer;user-select:none;transition:background .12s;
}
.sb-pair-tool:hover{ background:var(--bg1); }
.sb-pair-icon{ font-family:var(--mono);font-size:13px;color:var(--t3);flex-shrink:0;width:18px;text-align:center; }
.sb-pair-name{ font-family:var(--mono);font-size:12px;font-weight:500;color:var(--t2);flex:1;text-align:left; }
.sb-pair-arg{ font-family:var(--mono);font-size:11px;color:var(--t3);max-width:180px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;text-align:left; }
.sb-pair-chev{ font-size:11px;color:var(--t3);transition:transform .18s;flex-shrink:0; }
.sb-pair-chev.open{ transform:rotate(180deg); }
.sb-pair-result{
  padding:6px 12px 8px 39px;font-family:var(--mono);font-size:12px;
  line-height:1.55;text-align:left;border-top:1px solid rgba(0,0,0,.04);
}
.sb-pair-result.scanning{ color:var(--t3);animation:blurPulse 1.8s ease-in-out infinite; }
.sb-pair-detail{
  padding:6px 12px 10px 39px;font-family:var(--mono);font-size:11px;
  color:var(--t3);line-height:1.6;text-align:left;
  border-top:1px solid rgba(0,0,0,.04);background:var(--bg1);
}

/* ── Substep rows ── */
.sb-substep{ display:flex;align-items:flex-start;gap:10px;padding:4px 18px 4px 16px;animation:rowIn .14s ease both; }
.sb-substep-icon{ font-family:var(--mono);font-size:13px;flex-shrink:0;width:16px;text-align:center;padding-top:2px;line-height:1.5; }
.sb-substep-spin{ display:inline-block;animation:spin .9s linear infinite; }
.sb-substep-text{ font-family:var(--mono);font-size:12px;line-height:1.65;color:var(--t2); }
/* ── Approval card ── */
.sb-appr{
  margin:6px 14px;padding:14px;border-radius:10px;
  border:1px solid #141414ff;background:var(--bg);
  animation:rowIn .2s ease both;text-align:left;box-shadow:var(--shadow-sm);
}
.sb-appr-title{ font-family:var(--mono);font-size:12px;font-weight:600;color:var(--t1);margin-bottom:6px; }
.sb-appr-desc{ font-size:13px;color:var(--t2);line-height:1.75;margin-bottom:12px; }
.sb-appr-btns{ display:flex;gap:8px;flex-wrap:wrap; }
.sb-appr-yes{
  padding:8px 22px;border-radius:28px;background:var(--t1);border:none;
  font-family:var(--sans);font-size:13px;font-weight:600;
  color:#fff;cursor:pointer;transition:all .18s;box-shadow:0 2px 6px rgba(0,0,0,.12);
}
.sb-appr-yes:hover{ background:#222;transform:translateY(-1px); }
.sb-appr-yes:disabled{ opacity:.35;cursor:not-allowed; }
.sb-appr-all{
  padding:8px 16px;border-radius:28px;
  border:1px solid var(--bd);background:var(--bg2);
  color:var(--t2);font-family:var(--mono);font-size:12px;
  cursor:pointer;transition:all .18s;font-weight:500;
}
.sb-appr-all:hover{ background:var(--bg3);border-color:var(--bdh); }
.sb-appr-skip{
  padding:8px 14px;border-radius:28px;border:1px solid var(--bd);
  background:transparent;color:var(--t3);font-family:var(--mono);font-size:12px;
  cursor:pointer;transition:all .18s;
}
.sb-appr-skip:hover{ color:var(--t2);border-color:var(--bdh); }

/* ── Report row ── */
.sb-report-row{
  margin:6px 14px;padding:14px;border-radius:10px;
  border:1px solid var(--bd);background:#fff;
  animation:rowIn .2s ease both;text-align:left;box-shadow:var(--shadow-md);
}
.sb-report-shimmer{
  padding:14px 18px 6px;
  font-family:var(--sans);font-size:13px;line-height:1.78;text-align:left;
  background:linear-gradient(90deg,var(--t2) 20%,var(--t1) 48%,var(--t3) 52%,var(--t2) 80%);
  background-size:200% auto;
  -webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;
  animation:shimmer 2s linear infinite;
}

/* ── Action buttons ── */
.sb-act-pri{
  padding:10px 24px;border-radius:28px;background:var(--t1);border:none;
  font-family:var(--sans);font-size:13px;font-weight:600;
  color:#fff;cursor:pointer;transition:all .18s;box-shadow:0 2px 8px rgba(0,0,0,.12);
}
.sb-act-pri:hover:not(:disabled){ background:#222;transform:translateY(-1px); }
.sb-act-pri:disabled{ opacity:.35; }
.sb-act-ghost{
  padding:10px 16px;border-radius:28px;border:1px solid var(--bd);
  background:#fff;color:var(--t3);font-family:var(--mono);font-size:13px;
  cursor:pointer;transition:all .18s;box-shadow:var(--shadow-sm);
}
.sb-act-ghost:hover{ color:var(--t2);border-color:var(--bdh);background:var(--bg1); }
.sb-act-outline{
  padding:10px 18px;border-radius:28px;border:1px solid var(--bdh);
  background:#fff;color:var(--t1);font-family:var(--mono);font-size:13px;
  cursor:pointer;transition:all .18s;box-shadow:var(--shadow-sm);
}
.sb-act-outline:hover{ background:var(--bg2); }

/* ── Skeleton lines ── */
.sb-skel{ padding:10px 16px 14px;border-bottom:1px solid rgba(0,0,0,.04); }
.sb-skel-line{
  height:7px;border-radius:4px;margin-bottom:8px;
  background:linear-gradient(90deg,var(--bg2) 25%,var(--bg3) 50%,var(--bg2) 75%);
  background-size:200% 100%;animation:skelMove 1.8s ease infinite;
}
.sb-skel-line.short{width:50%;}
.sb-skel-line.med{width:72%;}
.sb-skel-line.full{width:100%;}

/* ── Stage blocks ── */
.sb-stage{ margin:2px 0;border-bottom:1px solid rgba(0,0,0,.04); }
.sb-stage-hdr{
  display:flex;align-items:center;gap:9px;padding:9px 16px;
  cursor:pointer;user-select:none;transition:background .12s;
}
.sb-stage-hdr:hover{ background:var(--bg1); }
.sb-stage-num{ font-family:var(--mono);font-size:11px;font-weight:600;color:#000;letter-spacing:.06em;flex-shrink:0;min-width:54px; }
.sb-stage-summary{ font-family:var(--mono);font-size:12px;color:var(--t3);flex:1;text-align:left; }
.sb-stage-pill{ font-family:var(--mono);font-size:11px;padding:2px 7px;border-radius:999px;border:1px solid;opacity:.8; }
.sb-stage-chev{ font-size:11px;color:var(--t3);opacity:.5;transition:transform .18s;flex-shrink:0; }
.sb-stage-chev.open{ transform:rotate(180deg); }
.sb-stage-body{ padding:2px 0 4px; }

/* ═══════════════════════════════════════════════
   FINDINGS PANEL
═══════════════════════════════════════════════ */
.sb-panel{
  width:288px;flex-shrink:0;display:flex;flex-direction:column;
  overflow:hidden;background:var(--bg1);
  border-radius:14px;border:1px solid var(--bd);box-shadow:var(--shadow-md);
}
.sb-panel-hdr{
  flex-shrink:0;padding:0 14px;height:42px;
  display:flex;align-items:center;justify-content:space-between;
  border-bottom:1px solid var(--bd);background:var(--bg2);border-radius:14px 14px 0 0;
}
.sb-panel-title{ font-family:var(--mono);font-size:11px;font-weight:600;letter-spacing:.1em;text-transform:uppercase;color:var(--t3); }
.sb-panel-count{ font-family:var(--mono);font-size:11px;color:var(--t3); }
.sb-panel-body{
  flex:1;overflow-y:auto;
  scrollbar-width:thin;scrollbar-color:var(--bg3) transparent;
}
.sb-panel-body::-webkit-scrollbar{ width:2px; }
.sb-panel-body::-webkit-scrollbar-thumb{ background:var(--bg3); }

/* Score block */
.sb-score-block{ padding:14px;border-bottom:1px solid var(--bd);display:flex;align-items:center;gap:12px; }
.sb-score-right{ flex:1;min-width:0;text-align:left; }
.sb-score-label{ font-family:var(--serif);font-size:17px;line-height:1;text-align:left; }
.sb-score-chips{ display:flex;gap:4px;flex-wrap:wrap;margin-top:7px; }

/* Severity chips */
.sb-sev-chip{
  font-family:var(--mono);font-size:11px;font-weight:700;
  padding:3px 8px;border-radius:999px;border:1px solid;letter-spacing:.03em;
}
.sb-sev-chip[data-sev="CRITICAL"]{ background:#fff0f0;color:#b91c1c;border-color:rgba(185,28,28,.2); }
.sb-sev-chip[data-sev="HIGH"]    { background:#fff4ee;color:#c2410c;border-color:rgba(194,65,12,.2); }
.sb-sev-chip[data-sev="MEDIUM"]  { background:#fefce8;color:#92400e;border-color:rgba(146, 65, 14, 0.61); }
.sb-sev-chip[data-sev="LOW"]     { background:#f0fdf4;color:#166534;border-color:rgba(22,101,52,.2); }
.sb-sev-chip[data-sev="INFO"]    { background:#eff6ff;color:#1e40af;border-color:rgba(30,64,175,.2); }

/* Finding card */
.sb-finding{
  padding:10px 14px;border-bottom:1px solid rgba(0,0,0,.05);
  animation:fadeUp .18s ease both;cursor:pointer;transition:background .12s;text-align:left;
}
.sb-finding:hover{ background:var(--bg1); }
.sb-f-top{ display:flex;align-items:center;gap:8px; }
.sb-f-sev{
  font-family:var(--mono);font-size:10px;font-weight:700;
  padding:2px 7px;border-radius:999px;flex-shrink:0;border:1px solid;letter-spacing:.03em;
}
.sb-f-sev.CRITICAL{ background:var(--redb);   color:var(--red-text);   border-color:rgba(220,38,38,.3); }
.sb-f-sev.HIGH    { background:var(--orangeb); color:var(--orange-text);border-color:rgba(234,88,12,.3); }
.sb-f-sev.MEDIUM  { background:var(--yellowb); color:var(--yellow-text);border-color:rgba(202,138,4,.3); }
.sb-f-sev.LOW     { background:var(--greenb);  color:var(--green-text); border-color:rgba(22,163,74,.3); }
.sb-f-sev.INFO    { background:var(--blueb);   color:var(--blue-text);  border-color:rgba(37,99,235,.3); }
.sb-f-title{ font-size:13px;font-weight:400;color:rgba(12, 12, 12, 0.35);line-height:1.35;flex:1; }
.sb-f-chev{ font-size:11px;color:var(--t3);opacity:.4;flex-shrink:0;transition:transform .16s; }
.sb-f-chev.open{ transform:rotate(180deg); }
.sb-f-detail{ padding:7px 0 4px;text-align:left; }
.sb-f-desc{ font-size:12px;color:var(--t2);line-height:1.65;margin-bottom:6px; }
.sb-f-fix{ font-family:var(--mono);font-size:11px;color:rgba(46, 46, 46, 0.54);line-height:1.55; }
.sb-f-fix::before{content:'fix:';opacity:.85;display:inline-block;font-family:var(--mono);font-size:11px;font-weight:700;color:#000;background:#e5e5e5;padding:2px 6px;border-radius:999px;margin-right:8px;line-height:1;}
.sb-f-cvss{ font-family:var(--mono);font-size:11px;color:var(--t3);margin-top:4px; }

.sb-no-findings{ display:flex;flex-direction:column;align-items:center;justify-content:center;height:200px;gap:8px; }
.sb-no-txt{ font-family:var(--mono);font-size:13px;color:var(--t3); }
.sb-scan-overlay{ position:relative;overflow:hidden; }
.sb-scan-line{
  position:absolute;left:0;right:0;height:1px;
  background:linear-gradient(90deg,transparent,rgba(0,0,0,.08),transparent);
  animation:scanMove 2.4s linear infinite;
}

.thinking-text{
  font-family:var(--mono);font-size:12px;
  background:linear-gradient(90deg,var(--t3) 20%,var(--t1) 48%,var(--t3) 80%);
  background-size:200% auto;
  -webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;
  animation:shimmer 2.2s linear infinite;
}

/* ── Live thought banner ── */
.sb-live-banner{
  position:sticky;top:0;z-index:50;border-bottom:1px solid var(--bd);
  background:rgba(255,255,255,.97);backdrop-filter:blur(20px);
}
.sb-live-dot{ width:7px;height:7px;border-radius:50%;background:var(--t1);flex-shrink:0;animation:livePulse 1.2s infinite; }
.sb-live-text{
  flex:1;font-family:var(--mono);font-size:12px;text-align:left;line-height:1.5;
  background:linear-gradient(90deg,var(--t3) 20%,var(--t1) 48%,var(--t3) 80%);
  background-size:200% auto;
  -webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;
  animation:shimmer 2s linear infinite;
}
.sb-live-expand{ font-family:var(--mono);font-size:11px;color:var(--t3);opacity:.5;flex-shrink:0;transition:transform .18s; }
.sb-live-expand.open{ transform:rotate(180deg); }

/* ── Deploy overlay ── */
@keyframes deployFadeIn {
  from{ opacity:0;filter:blur(8px);transform:scale(.98) }
  to  { opacity:1;filter:blur(0px);transform:scale(1)   }
}
@keyframes deployFadeOut {
  from{ opacity:1;filter:blur(0px);transform:scale(1)   }
  to  { opacity:0;filter:blur(8px);transform:scale(.98) }
}
.sb-deploy-overlay{
  position:absolute;inset:0;z-index:80;
  display:flex;flex-direction:column;align-items:center;justify-content:center;gap:16px;
  background:rgba(255,255,255,.97);backdrop-filter:blur(20px);
  animation:deployFadeIn .35s cubic-bezier(.22,1,.36,1) both;
}
.sb-deploy-overlay.hiding{ animation:deployFadeOut .4s cubic-bezier(.22,1,.36,1) both; }
.sb-deploy-ring{
  width:44px;height:44px;border-radius:50%;
  border:1.5px solid var(--bd);border-top-color:var(--t1);
  animation:spin 1s linear infinite;
}
.sb-deploy-label{ font-family:var(--mono);font-size:13px;color:var(--t2);letter-spacing:.05em; }
.sb-deploy-sub{ font-family:var(--mono);font-size:12px;color:var(--t3);animation:blurPulse 1.8s ease-in-out infinite; }

/* ═══════════════════════════════════════════════
   DEEP DIVE VIEW
═══════════════════════════════════════════════ */
.sb-deepdive{
  flex:1;display:grid;grid-template-columns:256px 1fr 1fr;
  gap:8px;padding:8px;overflow:hidden;background:var(--bg2);
}
.sb-ddpanel{
  background:#fff;border:1px solid var(--bd);
  border-radius:14px;display:flex;flex-direction:column;
  overflow:hidden;box-shadow:var(--shadow-md);
}
.sb-ddh{
  display:flex;align-items:center;justify-content:space-between;
  padding:9px 14px;border-bottom:1px solid var(--bd);
  flex-shrink:0;background:var(--bg1);
}
.sb-ddt{ font-family:var(--mono);font-size:11px;font-weight:600;letter-spacing:.1em;text-transform:uppercase;color:var(--t3); }
.sb-dds{
  flex:1;overflow-y:auto;min-height:0;
  scrollbar-width:thin;scrollbar-color:var(--bg3) transparent;
}
.sb-dds::-webkit-scrollbar{ width:3px; }
.sb-dds::-webkit-scrollbar-thumb{ background:var(--bg3); }
.sb-dde{ padding:16px 14px;font-family:var(--mono);font-size:12px;color:var(--t3); }

.sb-chk-ph{ font-family:var(--mono);font-size:11px;font-weight:600;letter-spacing:.1em;text-transform:uppercase;color:var(--t3);padding:8px 12px 3px;opacity:.7; }
.sb-chk-row{ display:flex;align-items:center;gap:7px;padding:5px 12px; }
.sb-chk-ico{ font-size:13px;flex-shrink:0;width:16px;text-align:center; }
.sb-chk-name{ font-family:var(--mono);font-size:12px;flex:1; }
.sb-chk-badge{ font-family:var(--mono);font-size:11px;font-weight:700;padding:2px 6px;border-radius:999px; }

.sb-fdd{
  margin:5px 8px;padding:10px 12px;border-radius:9px;
  border:1px solid var(--bd);background:#fff;
  animation:fadeUp .2s ease both;box-shadow:var(--shadow-sm);
}
.sb-fdd-h{ display:flex;align-items:center;gap:7px;margin-bottom:5px;flex-wrap:wrap; }
.sb-fdd-sev{
  font-size:10px;font-weight:700;letter-spacing:.05em;
  text-transform:uppercase;padding:2px 7px;border-radius:999px;border:1px solid;
}
.sb-fdd-sev.CRITICAL{ background:var(--redb);   color:var(--red-text);   border-color:rgba(220,38,38,.3); }
.sb-fdd-sev.HIGH    { background:var(--orangeb); color:var(--orange-text);border-color:rgba(234,88,12,.3); }
.sb-fdd-sev.MEDIUM  { background:var(--yellowb); color:var(--yellow-text);border-color:rgba(202,138,4,.3); }
.sb-fdd-sev.LOW     { background:var(--greenb);  color:var(--green-text); border-color:rgba(22,163,74,.3); }
.sb-fdd-sev.INFO    { background:var(--blueb);   color:var(--blue-text);  border-color:rgba(37,99,235,.3); }
.sb-fdd-title{ font-size:13px;font-weight:600;color:var(--t1); }
.sb-fdd-desc{ font-size:12px;color:var(--t2);line-height:1.65; }
.sb-fdd-fix{ font-size:12px;color:var(--green-text);line-height:1.6;margin-top:6px;padding-top:6px;border-top:1px solid var(--bd); }

/* AI messages */
.sb-amsg{ display:flex;gap:9px;animation:msgIn .22s ease both;position:relative; }
.sb-amsg+.sb-amsg{ margin-top:12px; }
.sb-amsg+.sb-amsg::before{ content:'';position:absolute;left:6px;top:-12px;width:1px;height:12px;background:var(--bd); }
.sb-adot{ width:14px;height:14px;border-radius:50%;flex-shrink:0;margin-top:2px;display:flex;align-items:center;justify-content:center;font-family:var(--mono);font-size:10px;font-weight:600; }
.sb-adot-1{ background:var(--blueb);border:1px solid rgba(37,99,235,.2);color:var(--blue-text); }
.sb-adot-2{ background:var(--greenb);border:1px solid rgba(22,163,74,.2);color:var(--green-text); }
.sb-aname{ font-family:var(--mono);font-size:11px;font-weight:500;letter-spacing:.05em;margin-bottom:3px; }
.sb-atxt{ font-size:13px;color:var(--t2);line-height:1.72; }
.sb-atyp{ display:flex;align-items:center;gap:3px;padding:2px 0; }
.sb-atd{ width:4px;height:4px;border-radius:50%; }
.sb-atd:nth-child(1){ animation:livePulse 1s 0s infinite; }
.sb-atd:nth-child(2){ animation:livePulse 1s .18s infinite; }
.sb-atd:nth-child(3){ animation:livePulse 1s .36s infinite; }

.sb-ddprog{ padding:8px 12px;border-top:1px solid var(--bd);flex-shrink:0; }
.sb-ddpn{ display:flex;justify-content:space-between;font-family:var(--mono);font-size:11px;color:var(--t3);margin-bottom:4px; }
.sb-ddpt{ height:2px;background:var(--bg3);border-radius:2px;overflow:hidden; }
.sb-ddpf{ height:100%;background:var(--t1);border-radius:2px;transition:width .5s; }

.sb-log-call{ display:flex;align-items:flex-start;gap:8px;padding:7px 14px;border-bottom:1px solid rgba(0,0,0,.04);transition:background .12s; }
.sb-log-call:hover{ background:var(--bg1); }
.sb-log-call-icon{ font-family:var(--mono);font-size:13px;color:var(--t3);flex-shrink:0;width:16px;text-align:center;padding-top:1px; }
.sb-log-call-body{ flex:1;min-width:0;display:flex;flex-direction:column;gap:3px; }
.sb-log-call-name{ font-family:var(--mono);font-size:12px;font-weight:500;color:var(--t2); }
.sb-log-call-arg{ font-family:var(--mono);font-size:11px;color:var(--t3);word-break:break-all;line-height:1.5; }
.sb-log-result{ display:flex;align-items:flex-start;gap:7px;padding:4px 14px 6px 38px;border-bottom:1px solid rgba(0,0,0,.03); }
.sb-log-result-dot{ width:5px;height:5px;border-radius:50%;flex-shrink:0;margin-top:5px; }
.sb-log-result-text{ font-family:var(--mono);font-size:11px;color:var(--t3);line-height:1.55;flex:1;min-width:0;word-break:break-word; }
.sb-log-result-text.warn{ color:var(--orange-text); }
.sb-log-result-text.hit{ color:var(--red-text); }
.sb-log-result-text.clean{ color:var(--green-text); }
.sb-log-think{ display:flex;align-items:flex-start;gap:8px;padding:6px 14px;border-bottom:1px solid rgba(0,0,0,.03);background:var(--bg1); }
.sb-log-think-lbl{ font-family:var(--mono);font-size:10px;color:var(--t3);flex-shrink:0;padding-top:2px;letter-spacing:.06em; }
.sb-log-think-txt{ font-family:var(--sans);font-size:12px;color:var(--t2);line-height:1.6;font-style:italic;flex:1;min-width:0; }
.sb-log-section-hdr{
  display:flex;align-items:center;justify-content:space-between;
  padding:5px 14px 4px;background:var(--bg1);border-bottom:1px solid var(--bd);
  position:sticky;top:0;z-index:2;backdrop-filter:blur(8px);
}
.sb-log-section-lbl{ font-family:var(--mono);font-size:10px;font-weight:600;letter-spacing:.1em;text-transform:uppercase;color:var(--t3);opacity:.6; }
.sb-log-sub{ padding:3px 14px 5px 38px;font-family:var(--mono);font-size:11px;color:var(--t3);line-height:1.55;border-bottom:1px solid rgba(0,0,0,.03);opacity:.65; }
.sb-log-approval{ padding:6px 14px;border-bottom:1px solid rgba(234,88,12,.15);background:#fff5f0;font-family:var(--mono);font-size:12px;color:var(--orange-text); }

.sb-trow{ display:flex;gap:7px;padding:3px 12px;align-items:flex-start; }
.sb-tts{ font-family:var(--mono);font-size:11px;color:var(--t3);flex-shrink:0;padding-top:1px; }
.sb-td2{ width:3px;height:3px;border-radius:50%;flex-shrink:0;margin-top:6px; }
.sb-tmsg{ font-family:var(--mono);font-size:12px;line-height:1.5;word-break:break-all; }

.sb-ring{ position:relative;flex-shrink:0; }
.sb-ring svg{ transform:rotate(-90deg); }
.sb-rinner{ position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center; }
.sb-rnum{ font-family:var(--serif);font-size:1.2rem;line-height:1; }
.sb-rdenom{ font-size:10px;font-weight:700;letter-spacing:.07em;text-transform:uppercase;color:var(--t3); }

/* Score ring */
.sb-score-ring{ position:relative;flex-shrink:0; }
.sb-score-ring svg{ transform:rotate(-90deg); }
.sb-score-inner{ position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center; }
.sb-score-num{ font-family:var(--serif);font-size:1.1rem;line-height:1; }
.sb-score-denom{ font-size:10px;font-weight:700;letter-spacing:.07em;text-transform:uppercase;color:var(--t3); }

/* ═══════════════════════════════════════════════
   REPORT VIEW
═══════════════════════════════════════════════ */
.sb-report-view{
  flex:1;overflow-y:auto;padding:28px;
  background:#f8f8f8;
  scrollbar-width:thin;scrollbar-color:var(--bg3) transparent;
}
.sb-rep-inner{ max-width:700px;margin:0 auto;text-align:left; }
.sb-rep-reasoning-row{
  font-size:13px;color:var(--t2);line-height:1.8;
  padding:8px 0;border-bottom:1px solid var(--bd);text-align:left;
}
.sb-rep-reasoning-label{ font-family:var(--mono);font-size:11px;color:var(--t3);margin-right:10px;opacity:.5; }
.sb-rep-hero{
  display:flex;align-items:flex-start;gap:20px;
  padding:24px;border:1px solid var(--bd);background:#fff;
  border-radius:14px;margin-bottom:18px;box-shadow:var(--shadow-sm);
}
.sb-rep-score-col{ display:flex;flex-direction:column;align-items:center;gap:6px;flex-shrink:0; }
.sb-rep-meta{ flex:1;min-width:0; }
.sb-rep-target{ font-family:var(--mono);font-size:14px;font-weight:600;color:var(--t1);margin-bottom:4px; }
.sb-rep-date{ font-family:var(--mono);font-size:12px;color:var(--t3); }
.sb-rep-grade{ font-family:var(--serif);font-size:1.4rem;line-height:1;margin-top:8px;color:var(--t1); }
.sb-rep-sum{ font-size:13px;color:var(--t2);line-height:1.82;margin-top:10px; }
.sb-rep-section{ margin-bottom:24px; }
.sb-rep-sh{
  font-family:var(--mono);font-size:11px;font-weight:600;
  letter-spacing:.12em;text-transform:uppercase;color:var(--t3);
  margin-bottom:12px;display:flex;align-items:center;gap:10px;
}
.sb-rep-sh::after{ content:'';flex:1;height:1px;background:var(--bd); }
.sb-rep-stats{ display:flex;gap:8px;margin-bottom:14px; }
.sb-rep-stat{
  flex:1;padding:14px;border:1px solid var(--bd);
  background:#fff;border-radius:10px;box-shadow:var(--shadow-sm);
}
.sb-rep-stat-n{ font-family:var(--serif);font-size:1.8rem;line-height:1;color:var(--t1); }
.sb-rep-stat-n.critical{ color:var(--red); }
.sb-rep-stat-n.high{ color:var(--orange); }
.sb-rep-stat-n.medium{ color:var(--yellow); }
.sb-rep-stat-l{ font-family:var(--mono);font-size:11px;color:var(--t3);margin-top:4px;text-transform:uppercase;letter-spacing:.06em; }
.sb-rep-finding{
  padding:14px;border:1px solid var(--bd);background:#fff;
  margin-bottom:6px;border-radius:10px;
  border-left:3px solid var(--bg3);box-shadow:var(--shadow-sm);
}
.sb-rep-finding.CRITICAL{ border-left-color:var(--red); }
.sb-rep-finding.HIGH    { border-left-color:var(--orange); }
.sb-rep-finding.MEDIUM  { border-left-color:var(--yellow); }
.sb-rep-finding.LOW     { border-left-color:var(--green); }
.sb-rep-finding.INFO    { border-left-color:var(--blue); }
.sb-rep-fix{
  font-family:var(--mono);font-size:12px;color:var(--t2);
  line-height:1.65;margin-top:10px;padding-top:10px;border-top:1px solid var(--bd);
}
.sb-rep-fix::before{
  content:'REMEDIATION';font-size:10px;color:var(--t3);
  opacity:.6;letter-spacing:.1em;display:block;margin-bottom:4px;
}
.sb-rep-recs{ margin-bottom:14px; }
.sb-rep-rec{
  display:flex;gap:14px;padding:10px 14px;
  border:1px solid var(--bd);background:#fff;
  margin-bottom:6px;border-radius:10px;box-shadow:var(--shadow-sm);
}
.sb-rep-rec-n{ font-family:var(--mono);font-size:13px;color:var(--t3);flex-shrink:0; }
.sb-rep-rec-txt{ font-size:13px;color:var(--t2);line-height:1.72; }
.sb-rep-attack{
  margin:18px 0;border:1px solid var(--bd);
  border-left:3px solid var(--red);border-radius:12px;
  background:#fff;overflow:hidden;
}
.sb-rep-attack-hdr{
  width:100%;display:flex;align-items:center;gap:8px;
  padding:12px 16px;background:transparent;border:none;cursor:pointer;
  font-family:var(--mono);font-size:13px;font-weight:500;
  color:var(--t2);text-align:left;transition:background .14s;
}
.sb-rep-attack-hdr:hover{ background:var(--bg1); }
.sb-rep-attack-hdr svg:first-child{ color:var(--red);flex-shrink:0;opacity:.7; }
.sb-rep-attack-hdr span:nth-child(2){ flex:1; }
.sb-rep-attack-count{
  font-family:var(--mono);font-size:11px;color:var(--t3);
  background:var(--bg2);padding:2px 8px;border-radius:999px;
}
.sb-rep-attack-chev{ color:var(--t3);transition:transform .22s;flex-shrink:0; }
.sb-rep-attack-chev.open{ transform:rotate(180deg); }
.sb-rep-attack-body{ padding:4px 16px 16px;border-top:1px solid var(--bd); }
.sb-rep-attack-step{
  display:flex;gap:12px;
  font-family:var(--mono);font-size:13px;color:var(--t2);
  line-height:1.75;padding:8px 0;border-bottom:1px solid var(--bd);
}
.sb-rep-attack-step:last-child{ border-bottom:none; }
.sb-rep-attack-num{ font-size:11px;font-weight:600;color:var(--t3);flex-shrink:0;padding-top:3px;min-width:16px; }
.sb-rep-actions{ display:flex;gap:8px;margin-top:20px; }
.sb-rep-empty{ display:flex;flex-direction:column;align-items:center;justify-content:center;min-height:320px;gap:12px; }
.sb-rep-empty-title{ font-family:var(--serif);font-size:2rem;color:var(--t2);font-style:italic; }
.sb-rep-empty-sub{ font-family:var(--mono);font-size:13px;color:var(--t3); }

/* ═══════════════════════════════════════════════
   MOBILE NAV
═══════════════════════════════════════════════ */
.sb-mobile-nav{
  display:none;
  position:fixed;bottom:0;left:0;right:0;z-index:200;
  padding:10px 16px max(16px,env(safe-area-inset-bottom));
  background:transparent;pointer-events:none;
}
.sb-mobile-nav.scan-active{ display:flex; }
.sb-mnav-inner{
  display:flex;flex-direction:row;align-items:center;
  width:100%;
  background:rgba(255,255,255,.88);
  backdrop-filter:blur(28px);-webkit-backdrop-filter:blur(28px);
  border:1px solid rgba(0,0,0,.07);
  border-radius:24px;padding:5px;
  box-shadow:0 8px 32px rgba(0,0,0,.10),0 2px 8px rgba(0,0,0,.05),inset 0 1px 0 rgba(255,255,255,.8);
  position:relative;pointer-events:all;gap:0;
}
.sb-mnav-thumb{
  position:absolute;top:5px;bottom:5px;
  border-radius:19px;background:#fff;
  border:1px solid rgba(0,0,0,.08);
  box-shadow:0 2px 8px rgba(0,0,0,.10),0 1px 3px rgba(0,0,0,.06);
  transition:transform .38s cubic-bezier(.4,0,.2,1),width .38s cubic-bezier(.4,0,.2,1);
  pointer-events:none;z-index:0;
}
.sb-mnav-btn{
  flex:1;min-width:0;
  display:flex;flex-direction:column;align-items:center;gap:4px;
  padding:9px 0 8px;
  border:none;background:transparent;cursor:pointer;
  position:relative;z-index:1;border-radius:19px;transition:opacity .18s;
}
.sb-mnav-btn:active{ opacity:.7; }
.sb-mnav-ico{ line-height:1;color:var(--t3);transition:color .22s,transform .22s; }
.sb-mnav-ico svg{ display:block; }
.sb-mnav-btn.on .sb-mnav-ico{ color:var(--t1);transform:scale(1.08); }
.sb-mnav-lbl{
  font-family:var(--mono);font-size:11px;
  letter-spacing:.04em;
  color:var(--t3);transition:color .22s;white-space:nowrap;
}
.sb-mnav-btn.on .sb-mnav-lbl{ color:var(--t1);font-weight:700; }
.hide-sm{ display:none; }
.hide-xs{ display:none; }

@media(min-width:480px){
  .hide-sm{ display:flex; }
}
@media(min-width:390px){
  .hide-xs{ display:flex; }
}
/* Subtitle pills */
.sb-empty-subtitle{ display:none; }
.sb-empty-subtitle-wrap{
  display:none;flex-wrap:wrap;justify-content:center;
  gap:5px;max-width:280px;
}
.sb-empty-subtitle-pill{
  font-family:var(--mono);font-size:13px;font-weight:600;
  color:#636363;background:#ececec;
  padding:5px 12px;border-radius:999px;
  animation:chipFadeIn .4s ease both;
}
  .sb-mob-tagline{
  display:none;
}
.sb-tos-short{ display:none; }
.sb-tos-full{ display:inline; }

/* ═══════════════════════════════════════════════
   RESPONSIVE — 768px
═══════════════════════════════════════════════ */
@media(max-width:768px){
  .sb-tos-full{ display:none; }
  .sb-tos-short{ display:inline; }
.sb-mob-tagline{
  display:block;
  font-family:var(--mono);
  font-size:13px;
  font-weight:600;
  color:#636363;
  background:#ececec;
  padding:8px 16px;
  text-align:center;
}
  .sb-toggle{ display:none; }
  .sb-nav{ padding:0 14px;gap:8px;height:50px; }
  .sb-ver{ display:none; }
  .sb-nav-tagline{ display:none; }
  .sb-empty-subtitle-wrap{ display:flex;margin-top:-14px;margin-bottom:22px;max-width:260px; }
  .sb-empty-subtitle{ display:block;margin-top:-14px;margin-bottom:22px;max-width:240px; }
  .sb-body{
    flex-direction:column;gap:0;padding:0;
    background:transparent;
    overflow-y:auto;overflow-x:hidden;
  }
  .sb-agent-col{
    width:100%;margin:0;border-radius:0;border:none;
    box-shadow:none;min-height:calc(100vh - 50px);flex:none;
  }
  .sb-panel{ display:none; }
  .sb-panel.mob-show{
    display:flex;width:100%;border-radius:0;border:none;
    box-shadow:none;max-height:none;min-height:calc(100vh - 50px);
  }
  .sb-recent-sidebar,
  .sb-recent-sidebar.open{ display:none !important; }
  .sb-recent-btn{ display:none; }
.sb-empty{
  min-height:calc(100vh - 70px);height:calc(100vh - 70px);
  padding:24px 16px 40px;justify-content:flex-start;align-items:center;
  padding-top:27vh;
}
  .sb-empty-title{ font-size:2rem;margin-bottom:24px; }
  .sb-empty-input-wrap{ max-width:100%;border-radius:22px; }
.sb-empty-chips{ gap:6px;justify-content:center;flex-wrap:nowrap;overflow:hidden; }
  .sb-empty-chip{ font-size:12px;padding:8px 14px; }
  .sb-mob-recent-btn{ display:flex; }
  .sb-mob-recent-backdrop.open{ display:block; }
  .sb-mob-recent-sheet{ display:flex; }
  .sb-hist{ display:none; }
.scan-active .sb-stream{ padding-bottom:110px; }
.scan-active .sb-panel-body{ padding-bottom:110px; }
.scan-active .sb-report-view{ padding-bottom:110px; }
.scan-active .sb-deepdive{ padding-bottom:110px; }
.sb-deepdive{
  grid-template-columns:1fr;overflow-y:auto;overflow-x:hidden;
  padding:10px 10px 120px;gap:10px;min-height:calc(100vh - 50px);
}
  .sb-ddpanel{ min-height:280px;border-radius:12px; }
.sb-report-view{ padding:18px 16px 120px; }
  .sb-rep-hero{ flex-direction:column;gap:14px;padding:18px; }
  .sb-rep-stats{ flex-wrap:wrap;gap:6px; }
  .sb-rep-stat{ min-width:calc(50% - 3px);flex:none; }
  .sb-rep-actions{ flex-wrap:wrap; }
  .sb-rep-inner{ max-width:100%; }
  .sb-pair{ margin:4px 8px; }
  .sb-think{ padding:10px 14px; }
  .sb-appr{ margin:6px 8px; }
  .sb-report-row{ margin:6px 8px; }
  .sb-left-panel{ display:none; }
}
@media(max-width:768px){
  /* Give every scrollable area enough bottom clearance for the mobile nav */
  .sb-stream{
    padding-bottom: 100px;
  }
  .sb-panel.mob-show .sb-panel-body{
    padding-bottom: 100px;
  }
  .sb-report-view{
    padding-bottom: 100px;
    padding-top: 50px;
  }
  .sb-deepdive{
    padding-bottom: 100px;
  }
}
@media(max-width:480px){
  .sb-empty-title{ font-size:1.8rem; }
.sb-empty{ padding-top:27vh; }
.sb-empty-chip{ font-size:11px;padding:6px 10px;flex-shrink:0; }
  .sb-empty-input-wrap{ padding:14px 14px 12px; }
  .sb-rep-stat{ min-width:calc(50% - 3px); }
  .sb-nav{ height:46px; }
  .sb-brand{ font-size:16px; }
.sb-empty-chips{ justify-content:center;gap:5px;flex-wrap:nowrap;overflow:hidden; }
  .sb-mnav-btn{ padding:7px 12px; }
  .sb-mnav-lbl{ font-size:10px;font-weight:700; }
  .sb-empty-subtitle-wrap{ max-width:260px;gap:4px; }
  .sb-empty-subtitle-pill{ font-size:12px;padding:4px 10px; }
  .sb-empty-subtitle{ font-size:12px;max-width:250px; }
}

@media(max-width:390px){
  .sb-empty-title{ font-size:1.5rem; }
  .sb-empty-chips{ gap:4px; }
.sb-empty-chip{ padding:5px 8px;font-size:10px;flex-shrink:0; }
  .sb-appr-btns{ flex-direction:column; }
  .sb-appr-btns button{ width:100%; }
  .sb-mnav-btn{ padding:6px 8px; }
}

@media(max-width:420px){
  .sb-empty-title{ font-size:1.45rem; }
  .sb-appr-btns button{ width:100%; }
}
`;


// ── Tiny components ───────────────────────────────────────────
const Spin = ({s=12, c='currentColor'}) => (
  <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2.5"
    strokeLinecap="round" style={{animation:'spin 1s linear infinite', flexShrink:0}}>
    <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
  </svg>
);

function ScoreRing({score, size=52}) {
  const r = size/2 - 5, circ = 2*Math.PI*r, fill = circ*(score/100);
  const col = scoreColor(score);
  return (
    <div className="sb-score-ring" style={{width:size, height:size}}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="rgba(255,255,255,.05)" strokeWidth="4"/>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={col} strokeWidth="4"
          strokeDasharray={`${fill} ${circ-fill}`} strokeLinecap="round"/>
      </svg>
      <div className="sb-score-inner">
        <div className="sb-score-num" style={{color:col}}>{score}</div>
        <div className="sb-score-denom">/100</div>
      </div>
    </div>
  );
}

function DDRing({score, size=46}) {
  const r = size/2 - 5, circ = 2*Math.PI*r, fill = circ*(score/100);
  const col = scoreColor(score);
  return (
    <div className="sb-ring" style={{width:size, height:size}}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="rgba(255,255,255,.05)" strokeWidth="3.5"/>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={col} strokeWidth="3.5"
          strokeDasharray={`${fill} ${circ-fill}`} strokeLinecap="round"/>
      </svg>
      <div className="sb-rinner">
        <div className="sb-rnum" style={{color:col}}>{score}</div>
        <div className="sb-rdenom">/100</div>
      </div>
    </div>
  );
}

function ToolPair({ icon, label, arg, result, resultColor, scanning, animDelay, toolName, liveThoughts=[], agents=[] }) {
  const [open, setOpen] = useState(true);
  const hasDetail = false; // tool pairs don't have expandable detail
  const isParallel = toolName === 'dispatch_subagents' || toolName === 'parallel_scan';

  const scanningText = scanning        // ← must come after isParallel
    ? isParallel && agents.length > 0
      ? (() => {
          const running = agents.find(a => a.status === 'running');
          if (running) {
            const t = liveThoughts.filter(x => x.agentId === running.id || x.toolName === running.id);
            const latest = t[t.length - 1];
            if (latest) return typeof latest === 'string' ? latest : latest.text;
          }
          return 'Agents starting up…';
        })()
      : (SCANNING_LABEL[toolName]?.(arg) || `${label}…`)
    : null;

  return (
    <div className="sb-pair" style={{animationDelay:`${animDelay}s`}}>
      <div className="sb-pair-tool" onClick={() => hasDetail && setOpen(o => !o)}>
        <span className="sb-pair-icon">{icon}</span>
        <span className="sb-pair-name">{label}</span>
        {arg && <span className="sb-pair-arg">{arg}</span>}
        {hasDetail && <div style={{width:22,height:22,borderRadius:'50%',background:'var(--bg3)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,transition:'transform .2s',transform:open?'rotate(180deg)':'rotate(0deg)'}}>
  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="var(--t2)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6"/></svg>
</div>}
      </div>
      {(result || scanning) && (
<div 
  className={`sb-pair-result${scanning ? ' scanning' : ''}`}
  style={{color: scanning ? undefined : resultColor}}
>
  {scanning ? scanningText : (() => {
    const isWarn = result.summary?.includes('⚠') || result.isWarning ||
      result.summary?.toLowerCase().includes('missing') ||
      result.summary?.toLowerCase().includes('issues:') ||
      result.summary?.toLowerCase().includes('crashed') ||
      result.summary?.toLowerCase().includes('error') ||
      result.summary?.toLowerCase().includes('failed');
          return (
      <span style={{
        fontFamily:'var(--mono)',
        fontSize:'11px',
        color: isWarn ? '#ef4444' : '#20a9ff',
        fontWeight:700,
        background: isWarn ? '#fff0f0' : '#eef5ff',
        padding:'3px 10px',
        borderRadius:999,
display:'inline',
lineHeight: 2.2,
WebkitBoxDecorationBreak: 'clone',
boxDecorationBreak: 'clone',
        maxWidth:'100%',
    fontStyle: 'italic',
      }}>
        {result.summary?.replace(/⚠\s*/g,'').trim()}
        {isWarn && (
          <span style={{marginLeft:6}}>⚠</span>
        )}
      </span>
    );
  })()}
</div>
      )}
    </div>
  );
}
function FindingCard({ f, delay }) {
  const [open, setOpen] = useState(false);
  const [flash, setFlash] = useState(f._severityChanged);
  
  useEffect(() => {
    if (f._severityChanged) {
      setFlash(true);
      const t = setTimeout(() => setFlash(false), 1200);
      return () => clearTimeout(t);
    }
  }, [f._severityChanged, f.severity]);
  const sv = SEV[f.severity] || SEV.LOW;
  const hasDetail = f.description || f.remediation || f.threatIntel || f.blastRadius?.summary;

  const blastParts = f.blastRadius?.summary
    ? f.blastRadius.summary
        .split(/[,·]/)
        .map(s => s.trim())
        .filter(Boolean)
        .filter(s => !s.toLowerCase().startsWith('data type'))
        .slice(0, 5)
    : [];

  return (
    <div
      className="sb-finding"
      style={{ animationDelay: `${delay}s` }}
      onClick={() => hasDetail && setOpen(o => !o)}
    >
      <div className="sb-f-top">
        <span style={{
          fontFamily: 'var(--mono)', fontSize: '10px', fontWeight: 700,
          padding: '3px 10px', borderRadius: 999, flexShrink: 0,
          color:
            f.severity === 'CRITICAL' ? '#ef4444' :
            f.severity === 'HIGH'     ? '#f97316' :
            f.severity === 'MEDIUM'   ? '#e6ac00ff' :
            f.severity === 'LOW'      ? '#22c55e' : '#3b82f6',
          background:
            f.severity === 'CRITICAL' ? '#ffdbdbff' :
            f.severity === 'HIGH'     ? '#ffe7dbff' :
            f.severity === 'MEDIUM'   ? '#fffbd2ff' :
            f.severity === 'LOW'      ? '#d7ffe3ff' : '#d3e6ffff',
              animation: flash ? 'severityFlash 1.2s ease both' : 'none',
        }}>
          {f.severity}
        </span>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <div className="sb-f-title" style={{
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {f.title}
            </div>
            {f.activelyExploited && (
              <span style={{
                fontFamily: 'var(--mono)', fontSize: '9px', fontWeight: 700,
                padding: '2px 6px', borderRadius: 999, flexShrink: 0,
                background: '#fff0f0', color: '#b91c1c',
                border: '1px solid rgba(185,28,28,.2)',
                whiteSpace: 'nowrap',
              }}>
                ⚡ KEV
              </span>
            )}
          </div>

          {!open && f.description && (
            <div style={{
              fontFamily: 'var(--mono)', fontSize: '11px', color: 'var(--t3)',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              marginTop: 2, lineHeight: 1.4,
            }}>
              {f.description}
            </div>
          )}

          {/* Exposes: pill row — always visible, no jargon */}
          {blastParts.length > 0 && (
            <div style={{
              display: 'flex', flexWrap: 'wrap', alignItems: 'center',
              gap: 4, marginTop: 5,
            }}>
              <span style={{
                fontFamily: 'var(--mono)', fontSize: '9px', fontWeight: 700,
                color: 'var(--t3)', letterSpacing: '.04em',
                marginRight: 2, whiteSpace: 'nowrap',
              }}>
                Exposes:
              </span>
              {blastParts.map((part, pi) => (
                <span key={pi} style={{
                  fontFamily: 'var(--mono)', fontSize: '9px', fontWeight: 700,
                  color: '#dc2626',
                  background: 'rgba(220,38,38,0.07)',
                  padding: '1px 6px', borderRadius: 999,
                  whiteSpace: 'nowrap',
                  border: '1px solid rgba(220,38,38,0.12)',
                }}>
                  {part}
                </span>
              ))}
            </div>
          )}
        </div>

        {hasDetail && (
          <div style={{
            width: 22, height: 22, borderRadius: '50%',
            background: 'var(--bg3)', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            flexShrink: 0, transition: 'transform .2s',
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
          }}>
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none"
              stroke="var(--t2)" strokeWidth="2.5"
              strokeLinecap="round" strokeLinejoin="round">
              <path d="M6 9l6 6 6-6"/>
            </svg>
          </div>
        )}
      </div>

      {open && hasDetail && (
        <div className="sb-f-detail">

          {f.threatIntel && (
            <div style={{
              padding: '7px 10px', borderRadius: 7, marginBottom: 8,
              background: '#fff0f0', border: '1px solid rgba(185,28,28,.2)',
              fontFamily: 'var(--mono)', fontSize: '11px', color: '#b91c1c',
              lineHeight: 1.6, display: 'flex', gap: 6, alignItems: 'flex-start',
            }}>
              <span style={{ flexShrink: 0 }}>⚡</span>
              <span>{f.threatIntel}</span>
            </div>
          )}

          {/* Expanded exposure block — plain English */}
          {f.blastRadius?.summary && (
            <div style={{
              padding: '9px 12px', borderRadius: 8, marginBottom: 8,
              background: 'rgba(220,38,38,0.04)',
              border: '1px solid rgba(220,38,38,0.12)',
            }}>
              <div style={{
                fontFamily: 'var(--mono)', fontSize: '9px', fontWeight: 700,
                color: 'var(--t3)', letterSpacing: '.07em',
                textTransform: 'uppercase', marginBottom: 7,
              }}>
                If exploited, an attacker could access
              </div>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 6 }}>
                {blastParts.map((part, pi) => (
                  <span key={pi} style={{
                    fontFamily: 'var(--mono)', fontSize: '10px', fontWeight: 700,
                    color: '#dc2626',
                    background: 'rgba(220,38,38,0.07)',
                    padding: '2px 8px', borderRadius: 999,
                    border: '1px solid rgba(220,38,38,0.12)',
                    whiteSpace: 'nowrap',
                  }}>
                    {part}
                  </span>
                ))}
              </div>

              {f.blastRadius.dataTypes?.length > 0 && (
                <div style={{
                  fontFamily: 'var(--mono)', fontSize: '10px',
                  color: 'var(--t3)', lineHeight: 1.6,
                }}>
                  Including: {f.blastRadius.dataTypes.join(', ')}.
                </div>
              )}

              {f.blastRadius.totalRows > 0 && (
                <div style={{
                  marginTop: 5, fontFamily: 'var(--mono)',
                  fontSize: '10px', fontWeight: 700, color: '#dc2626',
                }}>
                  {f.blastRadius.totalRows.toLocaleString()} records total.
                </div>
              )}
            </div>
          )}

          {f.description  && <div className="sb-f-desc">{f.description}</div>}
          {f.remediation  && <div className="sb-f-fix">{f.remediation}</div>}
          {f.cvss != null && <div className="sb-f-cvss">CVSS {f.cvss}</div>}
        </div>
      )}
    </div>
  );
}
const THINK_PREVIEW_WORDS = window.innerWidth <= 768 ? 10 : 20;
const truncateThought = (text, isExpanded) => {
  if (isExpanded) return text;
  const words = text.split(' ');
  if (words.length <= THINK_PREVIEW_WORDS) return text;
  return words.slice(0, THINK_PREVIEW_WORDS).join(' ') + '...';
};

function StageBlock({ stageNum, think, thinkIdx, pairs, warns, isActive, isLive, liveThought, forceOpen, isStartup }) {
  const [open, setOpen] = useState(isStartup ? true : isActive);
  const [showPill, setShowPill] = useState(false);

  useEffect(() => {
    if (isStartup) return;
    if (forceOpen !== undefined) setOpen(forceOpen);
  }, [forceOpen]);

  useEffect(() => {
    if (isActive) setOpen(true);
  }, [isActive]);

  useEffect(() => {
    if (isStartup && !isActive) {
      // Show the done pill but DO NOT force close
      // user controls open/close via toggle after this
      const t = setTimeout(() => setShowPill(true), 600);
      return () => clearTimeout(t);
    }
    if (!isStartup && !isActive && pairs.length > 0) {
      const t = setTimeout(() => setOpen(false), 1200);
      return () => clearTimeout(t);
    }
  }, [isActive, isStartup]);

  useEffect(() => {
    if (isStartup && forceOpen === true) setOpen(true);
  }, [forceOpen]);

  const hasPairs    = pairs.length > 0;
  const displayText = isLive && liveThought ? liveThought : think?.text || '';

  const actualWarns = pairs.filter(p => {
    const text = p.result?.summary || p.result?.output || '';
    return text.includes('⚠') || p.result?.isWarning;
  }).length;

  const getStageLabel = (pairs) => {
    if (isStartup) return { label: 'Setting up', color: '#7c3aed', bg: '#f3f0ff' };
    const tools = pairs.map(p => p.call?.tool || '').filter(Boolean);
    const has = (...names) => names.some(n => tools.some(t => t.includes(n)));
    if (has('fingerprint', 'deep_study', 'crawl', 'fetch_page', 'browser_fetch', 'recon'))
      return { label: 'Studying the site',  color: '#7c3aed', bg: '#f3f0ff' };
    if (has('check_headers', 'check_tls', 'check_source_maps', 'dns_enumerate'))
      return { label: 'Checking basics',    color: '#0ea5e9', bg: '#e0f2fe' };
    if (has('scan_js_bundle', 'enumerate_files'))
      return { label: 'Scanning files',     color: '#f59e0b', bg: '#fef3c7' };
    if (has('test_sqli', 'test_xss', 'test_lfi', 'test_xxe', 'test_ssrf', 'test_prototype'))
      return { label: 'Security testing',   color: '#ef4444', bg: '#fee2e2' };
    if (has('test_auth', 'test_jwt', 'test_csrf', 'test_idor', 'test_race', 'test_cors'))
      return { label: 'Checking access',    color: '#10b981', bg: '#d1fae5' };
    if (has('test_business', 'test_graphql', 'test_open_redirect', 'test_host', 'probe_api'))
      return { label: 'Testing the app',    color: '#8b5cf6', bg: '#ede9fe' };
    if (has('verify_finding', 'analyze_response', 'run_chain'))
      return { label: 'Verifying results',  color: '#6366f1', bg: '#eef2ff' };
    if (has('create_test_account', 'create_second', 'login_with'))
      return { label: 'Setting up',         color: '#64748b', bg: '#f1f5f9' };
    if (has('parallel_scan', 'dispatch_subagents'))
      return { label: 'Running checks',     color: '#6366f1', bg: '#eef2ff' };
    if (has('report', 'finish'))
      return { label: 'Preparing report',   color: '#0ea5e9', bg: '#e0f2fe' };
    return   { label: `Step ${stageNum}`,   color: 'var(--t2)', bg: 'var(--bg2)' };
  };

  const stageMeta = hasPairs ? getStageLabel(pairs) : null;

  return (
    <div className="sb-stage">

{/* Startup active — shows while running */}
{isStartup && isActive && (
  <div className="sb-stage-hdr">
    <span style={{
      fontFamily:'var(--mono)', fontSize:'11px', fontWeight:700,
      color:'#20a9ff', background:'#eef5ff',
      padding:'2px 9px', borderRadius:999, flexShrink:0,
    }}>
      Setting up
    </span>
    <span className="sb-stage-summary" style={{flex:1, minWidth:0}}>
      <span style={{display:'inline-flex', gap:5, alignItems:'center'}}>
        <Spin s={9} c="rgba(74,74,74,0.82)"/>
        <span>running…</span>
      </span>
    </span>
  </div>
)}

{/* Startup done — clickable to expand/collapse */}
{isStartup && !isActive && showPill && (
  <div className="sb-stage-hdr" onClick={() => setOpen(o => !o)}>
    <span style={{
      fontFamily:'var(--mono)', fontSize:'11px', fontWeight:700,
      color:'#20a9ff', background:'#eef5ff',
      padding:'2px 9px', borderRadius:999, flexShrink:0,
    }}>
      Ready
    </span>

    <span className="sb-stage-summary" style={{flex:1, minWidth:0}}>
      {pairs.length} check{pairs.length !== 1 ? 's' : ''} · loaded
    </span>

    <span style={{
      fontFamily:'var(--mono)', fontSize:'11px', fontWeight:700,
      color:'#20a9ff', background:'#eef5ff',
      padding:'3px 10px', borderRadius:999,
    }}>
      done
    </span>

    <div style={{
      width:22, height:22, borderRadius:'50%', background:'var(--bg3)',
      display:'flex', alignItems:'center', justifyContent:'center',
      flexShrink:0, transition:'transform .2s',
      transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
    }}>
      <svg width="10" height="10" viewBox="0 0 24 24" fill="none"
        stroke="var(--t2)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M6 9l6 6 6-6"/>
      </svg>
    </div>
  </div>
)}

      {/* Thinking row — hidden for startup */}
      {!isStartup && displayText && (
        <div style={{
          padding: '8px 16px 7px', borderBottom: '1px solid var(--bd)',
          background: 'var(--bg1)', textAlign: 'left',
        }}>
          <div style={{
            fontFamily: 'var(--mono)', fontSize: '12px',
            color: isLive ? 'var(--t2)' : '#7a7a7aff',
            lineHeight: 1.7, transition: 'color .3s',
            wordBreak: 'break-word', whiteSpace: 'pre-wrap',
            overflowWrap: 'break-word', textAlign: 'left',
          }}>
            {truncateThought(displayText, open)}
            {isLive && (
              <span style={{
                display: 'inline-block', width: 5, height: '0.75em',
                background: 'var(--t2)', marginLeft: 2,
                verticalAlign: 'middle', animation: 'livePulse .75s infinite',
              }}/>
            )}
          </div>
        </div>
      )}

      {/* Skeleton while waiting */}
      {!hasPairs && isActive && open && (
        <div className="sb-skel">
          <div className="sb-skel-line full"  style={{animationDelay:'0s'}}/>
          <div className="sb-skel-line med"   style={{animationDelay:'.18s'}}/>
          <div className="sb-skel-line short" style={{animationDelay:'.32s'}}/>
        </div>
      )}

      {/* Stage header — non-startup blocks */}
      {hasPairs && !isStartup && (
        <div className="sb-stage-hdr" onClick={() => setOpen(o => !o)}>
          <span style={{
            fontFamily: 'var(--mono)', fontSize:'11px', fontWeight:700,
            color: stageMeta.color, background: stageMeta.bg,
            padding: '2px 9px', borderRadius:999, flexShrink:0,
          }}>
            {stageMeta.label}
          </span>

          <span className="sb-stage-summary" style={{flex:1, minWidth:0}}>
            {isActive && !open
              ? <span style={{display:'inline-flex', gap:5, alignItems:'center'}}>
                  <Spin s={9} c="rgba(74,74,74,0.82)"/>
                  <span>Running {pairs.length} check{pairs.length!==1?'s':''}…</span>
                </span>
              : isActive
              ? `${pairs.length} check${pairs.length!==1?'s':''} · running`
              : actualWarns > 0
              ? `${pairs.length} checks · ${actualWarns} issue${actualWarns!==1?'s':''}`
              : `${pairs.length} checks · clean`
            }
          </span>

          {actualWarns > 0 && (
            <span style={{
              fontFamily:'var(--mono)', fontSize:'11px', fontWeight:700,
              color:'#ef4444', background:'#fff0f0',
              padding:'2px 9px', borderRadius:999, flexShrink:0,
            }}>
              {actualWarns} issue{actualWarns!==1?'s':''}
            </span>
          )}

          {!isActive && actualWarns === 0 && pairs.length > 0 && (
            <span style={{
              fontFamily:'var(--mono)', fontSize:'11px', fontWeight:700,
              color:'#20a9ff', background:'#eef5ff',
              padding:'3px 10px', borderRadius:999,
            }}>
              clean
            </span>
          )}

          <div style={{
            width:22, height:22, borderRadius:'50%', background:'var(--bg3)',
            display:'flex', alignItems:'center', justifyContent:'center',
            flexShrink:0, transition:'transform .2s',
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
          }}>
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none"
              stroke="var(--t2)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M6 9l6 6 6-6"/>
            </svg>
          </div>
        </div>
      )}

      {/* Tool pairs — renders for BOTH startup and non-startup when open */}
      {open && hasPairs && (
        <div className="sb-stage-body">
          {pairs.map((p, k) => {
            const stageLabel = p.call.tool === 'sast_scan' && p.call.input?.stage
              ? p.call.input.stage
              : p.call.tool === 'startup' && p.call.input?.stage
              ? p.call.input.stage
              : null;
            const icon  = TOOL_ICON[stageLabel || p.call.tool] || TOOL_ICON[p.call.tool] || '◇';
            const label = TOOL_LABEL[stageLabel || p.call.tool] || TOOL_LABEL[p.call.tool] || p.call.tool;

            const callData = p.call.args || p.call.input || {};
            const argRaw = (
              callData.path || callData.hostname || (callData.url ? (() => {
                try { const u = new URL(callData.url); return u.hostname || callData.url.slice(0,40); }
                catch { return callData.url?.slice(0,40) || ''; }
              })() : '') || (stageLabel ? '' : '')
            );

            const scanning    = !p.result && isActive && k === pairs.length - 1;
            const resultText  = p.result?.summary || p.result?.output || '';
            const warn        = resultText.includes('⚠') || p.result?.isWarning;
            const resultColor = warn ? 'rgba(248,48,48,.9)' : p.result ? 'rgba(69,255,180,.6)' : 'var(--t3)';
            const normResult  = p.result ? { ...p.result, summary: resultText } : null;

            return (
              <ToolPair key={k} icon={icon} label={label} arg={argRaw}
                result={normResult} resultColor={resultColor} scanning={scanning}
                animDelay={k * 0.04} toolName={p.call.tool}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
function LiveThoughtBanner({ thought, liveThoughts=[], open, onToggle }) {
  const scrollRef = useRef(null);
  const [scrolled, setScrolled] = useState(false);

  if (!thought) return null;
  return (
    <div style={{
      margin:'8px 14px 0',
      borderRadius: open ? '12px 12px 0 0' : 12,
      border:'1px solid var(--bd)',
      background:'#fff',
      overflow:'hidden',
      animation:'fadeUp .25s ease both',
      transition:'border-radius .2s',
    }}>
      {/* Main row */}
<div
  style={{
    padding:'10px 14px',
    cursor:'pointer', userSelect:'none',
    borderBottom: open ? '1px solid var(--bd)' : 'none',
    transition:'background .15s',
    textAlign:'left',
  }}
        onClick={onToggle}
        onMouseEnter={e => e.currentTarget.style.background='var(--bg1)'}
        onMouseLeave={e => e.currentTarget.style.background='transparent'}
      >
        {/* Row 1 — label */}
        <div style={{display:'flex', alignItems:'center', gap:7, marginBottom:5}}>
          <div className="sb-live-dot"/>
          <span style={{fontFamily:'var(--mono)', fontSize:'12px', fontWeight:700, color:'var(--t1)'}}>
            Agent is reasoning
          </span>
        </div>

        {/* Row 2 — thought pill + chevron */}
        <div style={{display:'flex', alignItems:'center', gap:8}}>
          <div style={{flex:1, minWidth:0}}>
            <div style={{
              fontFamily:'var(--mono)', fontSize:'13px',
              color:'#20a9ff', fontWeight:700,
              overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
              background:'#eef5ff',
              padding:'3px 10px', borderRadius:999,
              display:'inline-block', maxWidth:'100%',
            }}>
              {thought.text}
            </div>
          </div>

          <button
            onClick={e => { e.stopPropagation(); onToggle(); }}
            style={{
              width:26, height:26, borderRadius:'50%',
              background:'var(--t1)', border:'none',
              display:'flex', alignItems:'center', justifyContent:'center',
              cursor:'pointer', flexShrink:0, transition:'transform .2s',
              transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          >
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M6 9l6 6 6-6"/>
            </svg>
          </button>
        </div>
      </div>

      {/* Expanded thought history — scrollable */}
      {open && (
        <div
          ref={scrollRef}
          onScroll={e => setScrolled(e.currentTarget.scrollTop > 10)}
          style={{
            background:'var(--bg0)',
            maxHeight: 220,
            overflowY: 'auto',
            scrollbarWidth: 'thin',
            scrollbarColor: 'var(--bd) transparent',
          }}
        >
          {[...liveThoughts].reverse().map((t, ti) => (
            <div key={ti} style={{
              padding:'6px 14px',
              borderBottom: ti < liveThoughts.length - 1 ? '1px solid var(--bd)' : 'none',
              display:'flex', alignItems:'flex-start', gap:7,
              opacity: scrolled ? 1 : Math.max([1,.55,.32,.18,.1,.05][ti] ?? 0.05, 0.05),
              transition:'opacity .2s',
            }}>
              <div style={{width:3, height:3, borderRadius:'50%', flexShrink:0, background:'var(--t3)', marginTop:6}}/>
              <div style={{fontFamily:'var(--mono)', fontSize:'11px', color:'var(--t2)', lineHeight:1.65, textAlign:'left'}}>
                {typeof t==='string' ? t : t.text}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const AGENT_MSGS = {
  '1': (f) => {
    const t = (f.verdict || f.title || '').replace(/[.,!?]+$/, '').slice(0, 80);
    return f.severity === 'LOW'
      ? `Checked — ${t.toLowerCase()}.`
      : f.severity === 'MEDIUM'
      ? `Worth noting: ${t}. Not urgent but shouldn't stay open.`
      : f.severity === 'HIGH'
      ? `${t} — real exposure. Flagging before we go further.`
      : `${t} — actively exploitable. Everything stops until you review this.`;
  },
  '2': (sev) => sev === 'MEDIUM'
    ? 'Noted. Continuing checks.'
    : sev === 'HIGH'
    ? 'Agreed — pausing active tests. Your call on next steps.'
    : 'Full stop. Critical exposure needing immediate action.',
};

const dl = ms => new Promise(r => setTimeout(r, ms));

const NEXT_HINT = {
  thinking:            'Agent is working, one moment…',
  deep_study_site:     'Studying the site…',
  fingerprint_app:     'Reading the tech stack…',
  fetch_page:          'Fetching page content…',
  browser_fetch:       'Loading page…',
  crawl_site:          'Mapping all pages…',
  check_headers:       'Checking security headers…',
  check_tls:           'Checking SSL certificate…',
  scan_js_bundle:      'Scanning JavaScript files…',
  enumerate_files:     'Looking for exposed files…',
  dns_enumerate:       'Checking DNS records…',
  probe_api_endpoint:  'Probing API endpoints…',
  test_cors:           'Checking access policy…',
  test_sqli:           'Testing for SQL injection…',
  test_xss:            'Testing for script injection…',
  test_ssrf:           'Testing for server-side requests…',
  test_auth_bypass:    'Checking for auth bypass…',
  test_idor:           'Testing access controls…',
  test_lfi:            'Testing for file traversal…',
  test_csrf:           'Checking form protection…',
  test_jwt:            'Analysing login tokens…',
  test_host_header:    'Testing host header…',
  test_race_condition: 'Testing for race conditions…',
  test_graphql:        'Auditing GraphQL…',
  test_business_logic: 'Testing business logic…',
  test_open_redirect:  'Checking redirects…',
  test_xxe:            'Testing XML handling…',
  test_prototype_pollution: 'Testing prototype pollution…',
  test_header_injection:    'Testing header injection…',
  verify_finding:      'Verifying finding…',
  analyze_response:    'Analysing results…',
  check_source_maps:   'Checking source maps…',
  check_callback_hits: 'Checking for callbacks…',
  recon_extended:      'Running extended recon…',
  dispatch_subagents:  'Running security checks…',
  parallel_scan:       'Running checks in parallel…',
  create_test_account: 'Creating test account…',
  create_second_account:'Setting up second account…',
  login_with_credentials:'Logging in…',
  run_chain:           'Chaining attack steps…',
  finish_report:       'Writing up the report…',
};

function validateTarget(input) {
  const raw = input.trim();
  if (!raw) return { valid: false, error: null };
  
  // Reject obvious non-URLs
  if (raw.length < 4)                          return { valid: false, error: 'Enter a full URL or domain.' };
  if (!/[.]/.test(raw) && !raw.includes('localhost')) 
                                               return { valid: false, error: 'That doesn\'t look like a URL.' };
  if (/\s/.test(raw))                          return { valid: false, error: 'URLs can\'t contain spaces.' };
  if (raw.split('.').every(p => /^\d+$/.test(p)) && raw.split('.').length !== 4)
                                               return { valid: false, error: 'Enter a valid domain.' };

  // Try to construct a valid URL
  const withProtocol = raw.startsWith('http') ? raw : `https://${raw}`;
  try {
    const u = new URL(withProtocol);
    // Must have a real TLD or be localhost
    const host = u.hostname;
    if (!host.includes('.') && host !== 'localhost')
                                               return { valid: false, error: 'Enter a valid domain like example.com.' };
    if (host.split('.').pop().length < 2)      return { valid: false, error: 'Enter a valid domain.' };
    return { valid: true, error: null, normalized: withProtocol };
  } catch {
    return { valid: false, error: 'That doesn\'t look like a valid URL.' };
  }
}
function FaviconImg({ target, size = 14 }) {
  const hostname = (() => {
    try { return new URL(target).hostname; }
    catch { return target.replace(/https?:\/\//, '').split('/')[0]; }
  })();

  const sources = [
    `https://${hostname}/favicon.ico`,
    `https://${hostname}/favicon.png`,
    `https://${hostname}/apple-touch-icon.png`,
    `https://${hostname}/apple-touch-icon-precomposed.png`,
    `https://icons.duckduckgo.com/ip3/${hostname}.ico`,
    `https://www.google.com/s2/favicons?domain=${hostname}&sz=32`,
  ];

  const [srcIdx, setSrcIdx] = useState(0);
  const [failed, setFailed] = useState(false);

  if (failed) return null;

  return (
    <img
      src={sources[srcIdx]}
      width={size}
      height={size}
      style={{
        borderRadius: 3,
        flexShrink: 0,
        opacity: 1,
        objectFit: 'contain',
      }}
      onError={() => {
        if (srcIdx + 1 < sources.length) {
          setSrcIdx(i => i + 1);
        } else {
          setFailed(true);
        }
      }}
    />
  );
}
// ── ChecklistDropdowns component ──────────────────────────────
function StepperProgress({ thoughts, isRunning, scanId }) {
  const isGithub = scanId?.startsWith('github::');

  const calledTools = new Set(
    thoughts.filter(t => t.type === 'tool_call').map(t => t.tool)
  );

  const calledStages = new Set(
    thoughts
      .filter(t => t.type === 'tool_call' && t.tool === 'sast_scan' && t.input?.stage)
      .map(t => t.input.stage)
  );

  // ── Shimmer: shown while scanId is not yet known ──────────
  if (!scanId) {
    return (
      <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        <style>{`
          @keyframes _sbShimmer {
            0%   { background-position: -200% 0; }
            100% { background-position:  200% 0; }
          }
          .sb-shimmer-bone {
            border-radius: 4px;
            background: linear-gradient(
              90deg,
              var(--bg2) 25%,
              var(--bg3) 50%,
              var(--bg2) 75%
            );
            background-size: 200% 100%;
            animation: _sbShimmer 1.2s ease-in-out infinite;
          }
        `}</style>
        <div className="sb-shimmer-bone" style={{ width: 36, height: 8, marginBottom: 6 }} />
        {[88, 70, 58, 76, 58, 82, 64].map((w, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div className="sb-shimmer-bone" style={{ width: 10, height: 10, borderRadius: '50%', flexShrink: 0 }} />
            <div className="sb-shimmer-bone" style={{ width: w, height: 8 }} />
          </div>
        ))}
      </div>
    );
  }

  // ── Shared step renderer ──────────────────────────────────
  function renderSteps(steps, isDone, activeIdx) {
    return steps.map((step, i) => {
      const done   = isDone(step, i);
      const active = i === activeIdx && isRunning;
      const isLast = i === steps.length - 1;

      return (
        <div key={step.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <div style={{
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', width: 16, flexShrink: 0,
          }}>
            <div style={{
              width: 10, height: 10, borderRadius: '50%',
              flexShrink: 0, marginTop: 3,
              background: active ? '#555' : done ? 'var(--t1)' : 'var(--bg3)',
              border: `1.5px solid ${active ? '#555' : done ? 'var(--t1)' : 'var(--bd)'}`,
              boxShadow: active ? '0 0 0 3px rgba(0,0,0,.06)' : 'none',
              animation: active ? 'pulse 1.2s infinite' : 'none',
              transition: 'all .3s',
            }} />
            {!isLast && (
              <div style={{
                width: 1.5, flex: 1, minHeight: 18,
                background: done ? 'rgba(0,0,0,.15)' : 'var(--bg3)',
                margin: '3px 0', transition: 'background .4s',
              }} />
            )}
          </div>
          <div style={{ paddingBottom: isLast ? 0 : 4 }}>
            <span style={{
              fontFamily: 'var(--mono)', fontSize: '12px',
              fontWeight: active ? 700 : done ? 500 : 400,
              color: active ? '#555' : done ? 'var(--t1)' : 'var(--t3)',
              lineHeight: '16px', transition: 'all .25s',
            }}>
              {step.label}
            </span>
          </div>
        </div>
      );
    });
  }

  // ── GitHub SAST stepper ───────────────────────────────────
  if (isGithub) {
    const scanSteps = [
      { id: 'Connect',       label: 'Connect to repo'  },
      { id: 'Walk codebase', label: 'Walk file tree'   },
      { id: 'Scan 25%',      label: 'Scan 25%'         },
      { id: 'Scan 50%',      label: 'Scan 50%'         },
      { id: 'Scan 75%',      label: 'Scan 75%'         },
      { id: 'Analyze',       label: 'Analyse findings' },
      { id: 'Summarize',     label: 'Write report'     },
    ];

    const findingSteps = [
      { id: 'test_sqli',          label: 'SQL injection'  },
      { id: 'test_xss',           label: 'XSS'            },
      { id: 'test_lfi',           label: 'Path traversal' },
      { id: 'test_jwt',           label: 'JWT'            },
      { id: 'test_cors',          label: 'CORS'           },
      { id: 'test_auth_bypass',   label: 'Auth bypass'    },
      { id: 'scan_js_bundle',     label: 'Secrets'        },
      { id: 'check_headers',      label: 'Config issues'  },
      { id: 'probe_api_endpoint', label: 'Open redirect'  },
    ].filter(s => calledTools.has(s.id));

    const hasFindingSteps = findingSteps.length > 0;
    const firstPendingScan = scanSteps.findIndex(s => !calledStages.has(s.id));
    const activeIdx = isRunning
      ? (firstPendingScan === -1 ? scanSteps.length - 1 : firstPendingScan)
      : -1;

    return (
      <div style={{ padding: '16px', display: 'flex', flexDirection: 'column' }}>
        <div style={{
          fontFamily: 'var(--mono)', fontSize: '10px', fontWeight: 600,
          letterSpacing: '.12em', textTransform: 'uppercase',
          color: 'var(--t3)', opacity: .5, marginBottom: 8, textAlign: 'left',
        }}>Scan</div>

        {renderSteps(scanSteps, (step) => calledStages.has(step.id), activeIdx)}

        {hasFindingSteps && (
          <>
            <div style={{
              fontFamily: 'var(--mono)', fontSize: '10px', fontWeight: 600,
              letterSpacing: '.12em', textTransform: 'uppercase',
              color: 'var(--t3)', opacity: .5,
              marginTop: 14, marginBottom: 8, textAlign: 'left',
            }}>Findings</div>
            {findingSteps.map((step, i) => {
              const isLast = i === findingSteps.length - 1;
              return (
                <div key={step.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                  <div style={{
                    display: 'flex', flexDirection: 'column',
                    alignItems: 'center', width: 16, flexShrink: 0,
                  }}>
                    <div style={{
                      width: 10, height: 10, borderRadius: '50%',
                      flexShrink: 0, marginTop: 3,
                      background: 'var(--t1)', border: '1.5px solid var(--t1)',
                      transition: 'all .3s',
                    }} />
                    {!isLast && (
                      <div style={{
                        width: 1.5, flex: 1, minHeight: 18,
                        background: 'rgba(0,0,0,.15)',
                        margin: '3px 0', transition: 'background .4s',
                      }} />
                    )}
                  </div>
                  <div style={{ paddingBottom: isLast ? 0 : 4 }}>
                    <span style={{
                      fontFamily: 'var(--mono)', fontSize: '12px', fontWeight: 500,
                      color: 'var(--t1)', lineHeight: '16px',
                    }}>
                      {step.label}
                    </span>
                  </div>
                </div>
              );
            })}
          </>
        )}
      </div>
    );
  }

  // ── Web URL stepper ───────────────────────────────────────
  // Progress is based on total tool call count so it always
  // moves smoothly regardless of which tools the agent picks.
  const toolCallCount = thoughts.filter(t => t.type === 'tool_call').length;
  const finishCalled  = calledTools.has('finish_report');

  const urlSteps = [
    { id: 'reading',  label: 'Studying your site' },
    { id: 'scan25',   label: 'Scan 25%'           },
    { id: 'scan50',   label: 'Scan 50%'           },
    { id: 'scan75',   label: 'Scan 75%'           },
    { id: 'attack',   label: 'Active testing'     },
    { id: 'analyze',  label: 'Analyse findings'   },
    { id: 'report',   label: 'Write report'       },
  ];

  // Map tool call count → how many steps are "done"
  // Thresholds tuned to a typical ~13 tool call scan
function urlDoneCount() {
  if (finishCalled)                                        return 7; // report done

  // Phase 4 — verifying or chaining findings
  if (calledTools.has('verify_finding') ||
      calledTools.has('run_chain'))                        return 6;

  // Phase 3 — active testing (second dispatch OR finished with just recon)
  const dispatchCount = thoughts.filter(t =>
    t.type === 'tool_call' && t.tool === 'dispatch_subagents'
  ).length;
  if (dispatchCount >= 2)                                  return 5;

  // Phase 3 started — active dispatch OR static site that skips active
  if (dispatchCount >= 1 && (
    calledTools.has('test_sqli')       ||
    calledTools.has('test_xss')        ||
    calledTools.has('test_auth_bypass')||
    calledTools.has('probe_api_endpoint') ||
    calledTools.has('enumerate_files')    // static site — recon only
  ))                                                       return 5;

  // Phase 2 — recon dispatch fired
  if (dispatchCount >= 1)                                  return 4;

  // Scan 75% — stack fingerprinted and bundles scanned
  if (calledTools.has('scan_js_bundle') ||
      calledTools.has('fingerprint_app'))                  return 3;

  // Scan 25% — site studied
  if (calledTools.has('deep_study_site'))                  return 2;

  // Started — any tool call at all
  if (toolCallCount >= 1)                                  return 1;

  return 0;
}

  const doneCount    = urlDoneCount();
  const activeUrlIdx = isRunning
    ? (finishCalled ? urlSteps.length - 1 : Math.min(doneCount, urlSteps.length - 1))
    : -1;

  return (
    <div style={{ padding: '16px', display: 'flex', flexDirection: 'column' }}>
      <div style={{
        fontFamily: 'var(--mono)', fontSize: '10px', fontWeight: 600,
        letterSpacing: '.12em', textTransform: 'uppercase',
        color: 'var(--t3)', opacity: .5, marginBottom: 8, textAlign: 'left',
      }}>Scan</div>

      {renderSteps(
        urlSteps,
        (step, i) => i < doneCount,
        activeUrlIdx,
      )}
    </div>
  );
}
const DEPLOY_STEPS = [
  'Starting up…',
  'Connecting to target…',
  'Reading the site…',
  'Mapping attack surface…',
  'Almost there…',
];
function DeployTicker() {
  const [idx, setIdx] = useState(0);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setIdx(i => Math.min(i + 1, DEPLOY_STEPS.length - 1));
        setVisible(true);
      }, 300);
    }, 3500);
    return () => clearInterval(interval);
  }, []);

  return (
    <div style={{
      fontFamily:'var(--mono)', fontSize:'12px', color:'var(--t3)',
      opacity: visible ? 1 : 0,
      transition:'opacity .3s ease',
      marginTop:4,
    }}>
      {DEPLOY_STEPS[idx]}
    </div>
  );
}
function RepFindingRow({ f }) {
  const [isOpen, setIsOpen] = useState(false);
  const sv = SEV[f.severity] || SEV.LOW;
  return (
    <div className={`sb-rep-finding ${f.severity}`}>
      <div
        onClick={() => setIsOpen(o => !o)}
        style={{display:'flex', alignItems:'flex-start', gap:9, marginBottom: isOpen ? 8 : 0, cursor:'pointer'}}
      >
        <span style={{
          fontFamily:'var(--mono)', fontSize:'.42rem', fontWeight:700,
          padding:'2px 6px', borderRadius:999, flexShrink:0,
          color:sv.c, background:sv.bg, border:`1px solid ${sv.bd}`
        }}>
          {f.severity}
        </span>
        <div style={{fontWeight:600, fontSize:'.68rem', lineHeight:1.35, flex:1, textAlign:'left'}}>{f.title}</div>
        <svg
          style={{flexShrink:0, color:'var(--t3)', transition:'transform .18s', transform: isOpen ? 'rotate(180deg)' : 'none', marginTop:2}}
          width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
        >
          <path d="M6 9l6 6 6-6"/>
        </svg>
      </div>
      {isOpen && (
        <div style={{animation:'fadeUp .15s ease both'}}>
          {f.description && <div style={{fontSize:'.62rem', color:'var(--t2)', lineHeight:1.75, marginBottom:8, textAlign:'left'}}>{f.description}</div>}
          {f.remediation && <div className="sb-rep-fix" style={{textAlign:'left'}}>{f.remediation}</div>}
          {f.cvss && <div style={{fontFamily:'var(--mono)', fontSize:'.44rem', color:'var(--t3)', marginTop:6}}>CVSS {f.cvss}</div>}
        </div>
      )}
    </div>
  );
}
function WarmupSkeleton({ target }) {
  const [phase, setPhase] = useState(0);
  const [visible, setVisible] = useState(true);
  const host = (target || '').replace(/https?:\/\//, '').replace(/\/$/, '');

  useEffect(() => {
    // each phase lasts 1800ms, last one stays forever
    const timings = [1800, 1800, 1800, 1800, 1800, 1800, 9999];
    const id = setTimeout(() => {
      setVisible(false);
      setTimeout(() => {
        setPhase(p => Math.min(p + 1, 4));
        setVisible(true);
      }, 320);
    }, timings[phase]);
    return () => clearTimeout(id);
  }, [phase]);

  const ShimmerText = ({ text }) => (
    <div style={{
      fontFamily: 'var(--mono)', fontSize: '13px', marginBottom: 14,
      background: 'linear-gradient(90deg,var(--t3) 20%,var(--t1) 50%,var(--t3) 80%)',
      backgroundSize: '200% auto',
      WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
      animation: 'shimmer 1.8s linear infinite',
    }}>
      {text}
    </div>
  );

  const PillCards = ({ items }) => (
    <div style={{display:'flex', flexDirection:'column', gap:6}}>
      {items.map(({ label, done, active }, i) => (
        <div key={i} style={{
          display:'flex', alignItems:'center', gap:8,
          padding:'7px 11px', borderRadius:8,
          background: active ? '#eef5ff' : done ? 'var(--bg1)' : 'transparent',
          border:`1px solid ${active ? 'rgba(32,169,255,.25)' : 'var(--bd)'}`,
          animation:`fadeUp .2s ease ${i * .07}s both`,
        }}>
          <div style={{
            width:6, height:6, borderRadius:'50%', flexShrink:0,
            background: active ? '#20a9ff' : done ? '#22c55e' : 'var(--bg3)',
            boxShadow: active ? '0 0 0 3px rgba(32,169,255,.18)' : 'none',
            animation: active ? 'pulse 1.2s infinite' : 'none',
          }}/>
          <span style={{
            fontFamily:'var(--mono)', fontSize:'12px',
            color: active ? '#20a9ff' : done ? 'var(--t2)' : 'var(--t3)',
            fontWeight: active ? 600 : 400, flex:1,
          }}>
            {label}
          </span>
          {done && (
            <svg style={{flexShrink:0}} width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
          )}
          {active && (
            <div style={{display:'flex', gap:3}}>
              {[0,.15,.3].map((d,j) => (
                <div key={j} style={{
                  width:3, height:3, borderRadius:'50%', background:'#20a9ff',
                  animation:`livePulse 1s ${d}s infinite`,
                }}/>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );

  return (
    <div style={{
      padding:'20px 18px',
      opacity: visible ? 1 : 0,
      transform: visible ? 'translateY(0)' : 'translateY(6px)',
      transition:'opacity .32s ease, transform .32s ease',
      textAlign:'left',
    }}>

      {phase === 0 && (
        <>
          <ShimmerText text={`Reading ${host}…`} />
          <div className="sb-skel">
            <div className="sb-skel-line full"/>
            <div className="sb-skel-line med" style={{animationDelay:'.12s'}}/>
            <div className="sb-skel-line short" style={{animationDelay:'.24s'}}/>
          </div>
          <div className="sb-skel" style={{marginTop:4}}>
            <div className="sb-skel-line med"/>
            <div className="sb-skel-line full" style={{animationDelay:'.18s'}}/>
          </div>
        </>
      )}

      {phase === 1 && (
        <>
          <ShimmerText text="Opening your site in a browser…" />
          <div className="sb-skel">
            <div className="sb-skel-line full"/>
            <div className="sb-skel-line short" style={{animationDelay:'.1s'}}/>
          </div>
          <div className="sb-skel" style={{marginTop:4}}>
            <div className="sb-skel-line med"/>
            <div className="sb-skel-line full" style={{animationDelay:'.14s'}}/>
            <div className="sb-skel-line short" style={{animationDelay:'.22s'}}/>
          </div>
          <div className="sb-skel" style={{marginTop:4}}>
            <div className="sb-skel-line full"/>
            <div className="sb-skel-line med" style={{animationDelay:'.08s'}}/>
          </div>
        </>
      )}

      {phase === 2 && (
        <>
          <ShimmerText text="Checking what your site is built on…" />
          <div className="sb-skel">
            <div className="sb-skel-line short"/>
            <div className="sb-skel-line full" style={{animationDelay:'.1s'}}/>
            <div className="sb-skel-line med" style={{animationDelay:'.2s'}}/>
          </div>
          <div className="sb-skel" style={{marginTop:4}}>
            <div className="sb-skel-line full"/>
            <div className="sb-skel-line short" style={{animationDelay:'.12s'}}/>
          </div>
          <div className="sb-skel" style={{marginTop:4}}>
            <div className="sb-skel-line med"/>
            <div className="sb-skel-line full" style={{animationDelay:'.16s'}}/>
            <div className="sb-skel-line short" style={{animationDelay:'.28s'}}/>
          </div>
        </>
      )}

      {phase === 3 && (
        <>
          <ShimmerText text="Finding all the pages and links…" />
          <div className="sb-skel">
            <div className="sb-skel-line med"/>
            <div className="sb-skel-line full" style={{animationDelay:'.08s'}}/>
            <div className="sb-skel-line short" style={{animationDelay:'.16s'}}/>
          </div>
          <div className="sb-skel" style={{marginTop:4}}>
            <div className="sb-skel-line full"/>
            <div className="sb-skel-line med" style={{animationDelay:'.1s'}}/>
          </div>
          <div className="sb-skel" style={{marginTop:4}}>
            <div className="sb-skel-line short"/>
            <div className="sb-skel-line full" style={{animationDelay:'.14s'}}/>
            <div className="sb-skel-line med" style={{animationDelay:'.22s'}}/>
          </div>
        </>
      )}

      {phase === 4 && (
        <>
          <ShimmerText text="Almost there, one moment…" />
          <div className="sb-skel">
            <div className="sb-skel-line full"/>
            <div className="sb-skel-line med" style={{animationDelay:'.1s'}}/>
          </div>
          <div className="sb-skel" style={{marginTop:4}}>
            <div className="sb-skel-line short"/>
            <div className="sb-skel-line full" style={{animationDelay:'.12s'}}/>
            <div className="sb-skel-line med" style={{animationDelay:'.2s'}}/>
          </div>
        </>
      )}

    </div>
  );
}
function CopyMasterPromptBtn({ prompt }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(prompt).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <button
      onClick={copy}
      title="Copy a prompt you can paste into Cursor, Claude, or Copilot to fix all findings"
      style={{
        height: 34, padding: '0 12px', borderRadius: 26, flexShrink: 0,
        background: copied ? '#f0f6fdff' : '#ffffffff',
        border: `1px solid ${copied ? '#f0f9ffff' : 'var(--bd)'}`,
        fontFamily: 'var(--mono)', fontSize: '.64rem', fontWeight: 800,
        color: copied ? '#209ceaff' : '#00000092',
        cursor: 'pointer', transition: 'all .18s',
        display: 'flex', alignItems: 'center', gap: 5,
        whiteSpace: 'nowrap',
      }}
    >
      {copied
        ? <><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>Copied!</>
        : <><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>AI Fix Prompt</>
      }
    </button>
  );
}
function SecurityChat({ scan, findings, score, summary, thoughts, isRunning, scanId }) {
  const target = (scan?.target || '').replace(/https?:\/\//, '');

  const renderWelcome = () => {
    const crit = findings.filter(f => f.severity === 'CRITICAL').length;
    const high = findings.filter(f => f.severity === 'HIGH').length;
    const med  = findings.filter(f => f.severity === 'MEDIUM').length;
    const low  = findings.filter(f => f.severity === 'LOW').length;

    const severityPill = (label, count, color, bg) => count > 0 ? (
      <span style={{
        color, background: bg,
        padding: '1px 7px', borderRadius: 999,
        fontFamily: 'var(--mono)', fontSize: '11px', fontWeight: 700,
        display: 'inline-block', verticalAlign: 'middle', margin: '0 1px',
      }}>{count} {label}</span>
    ) : null;

    if (findings.length === 0) {
      return (
        <span style={{ fontFamily: 'var(--sans)', fontSize: '13px', lineHeight: 1.78, color: 'var(--t2)' }}>
          Scan complete for <span style={{ fontWeight: 600, color: 'var(--t1)' }}>{target}</span>. No issues found — everything looks clean. Ask me anything about the results or methodology.
        </span>
      );
    }
    return (
      <span style={{ fontFamily: 'var(--sans)', fontSize: '13px', lineHeight: 1.78, color: 'var(--t2)' }}>
        Scan complete for <span style={{ fontWeight: 600, color: 'var(--t1)' }}>{target}</span> —{' '}
        {severityPill('Critical', crit, '#dc2626', 'rgba(255,225,225,1)')}
        {severityPill('High', high, '#ea580c', '#fff2e1')}
        {severityPill('Medium', med, '#d97706', '#fff7d7')}
        {severityPill('Low', low, '#16a34a', '#e0ffe9')}
        {' '}issue{findings.length !== 1 ? 's' : ''} found. Ask me about any vulnerability, how to fix it, or what the real-world impact is.
      </span>
    );
  };

  const [messages, setMessages] = useState([
    { id: 'welcome', role: 'assistant', text: '', ts: new Date() },
  ]);
  const [input, setInput]     = useState('');
  const [loading, setLoading] = useState(false);
  const bottomRef    = useRef(null);
  const textareaRef  = useRef(null);
  const scrollRef    = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  const QUICK_PROMPTS = findings.length > 0
    ? ['What should I fix first?', 'How do I patch the top vulnerability?', 'What was scanned?']
    : ['What was tested?', 'How do I keep this secure?', 'Any blind spots to watch?'];

  function buildContext() {
    return [
      `Target: ${scan?.target || 'unknown'}`,
      score != null ? `Security score: ${score}/100 (${scoreLabel(score)})` : '',
      summary ? `Executive summary: ${summary}` : '',
      findings.length > 0
        ? `Security findings (${findings.length} total):\n${findings.slice(0, 14).map((f, i) =>
            `${i + 1}. [${f.severity}] ${f.title}${f.description ? ' — ' + f.description.slice(0, 160) : ''}${f.remediation ? '\n   Remediation: ' + f.remediation.slice(0, 140) : ''}${f.cvss ? ' (CVSS ' + f.cvss + ')' : ''}`
          ).join('\n')}`
        : 'No security issues found.',
    ].filter(Boolean).join('\n\n');
  }

  const SYSTEM_PROMPT = () => `You are a senior penetration tester. Never use em dashes. Be direct, no filler words.
Scan context:\n${buildContext()}`;

  async function send(text) {
    const q = (text || input).trim();
    if (!q || loading) return;
    setInput('');
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
    setLoading(true);
    const userMsg = { id: Date.now(), role: 'user', text: q, ts: new Date() };
    setMessages(p => [...p, userMsg]);
    const history = messages.filter(m => m.id !== 'welcome')
      .map(m => ({ role: m.role === 'assistant' ? 'assistant' : 'user', content: m.text }));
    try {
      const resp = await SecureAPI.callAzureOpenAI(
        [{ role: 'system', content: SYSTEM_PROMPT() }, ...history, { role: 'user', content: q }],
        { temperature: 0.3, max_tokens: 1000 }
      );
      const reply = resp.choices?.[0]?.message?.content || resp.content?.[0]?.text || 'No response.';
      setMessages(p => [...p, { id: Date.now() + 1, role: 'assistant', text: reply, ts: new Date() }]);
    } catch (e) {
      setMessages(p => [...p, { id: Date.now() + 1, role: 'assistant', text: `Error: ${e.message}`, ts: new Date() }]);
    }
    setLoading(false);
  }

  const hasUserMsg = messages.some(m => m.role === 'user');

  function inlineFormat(text) {
    const parts = [];
    const rx = /(\*\*(.+?)\*\*|`([^`]+)`|\[([^\]]+)\]\(([^)]+)\))/g;
    let last = 0, m;
    while ((m = rx.exec(text)) !== null) {
      if (m.index > last) parts.push(text.slice(last, m.index));
      if (m[0].startsWith('**')) {
        parts.push(<strong key={m.index} style={{ fontWeight: 700, color: 'var(--t1)' }}>{m[2]}</strong>);
      } else if (m[0].startsWith('`')) {
        parts.push(
          <code key={m.index} style={{
            fontFamily: 'var(--mono)', fontSize: '11.5px',
            background: '#f3f4f6', color: '#374151',
            padding: '1px 5px', borderRadius: 4,
            border: '1px solid #e5e7eb',
            wordBreak: 'break-all',
          }}>{m[3]}</code>
        );
      } else {
        parts.push(
          <span key={m.index} style={{ color: 'var(--t1)', fontWeight: 600 }}>{m[4]}</span>
        );
      }
      last = m.index + m[0].length;
    }
    if (last < text.length) parts.push(text.slice(last));
    return parts.length ? parts : text;
  }

  function renderText(raw) {
    const lines = raw.split('\n');
    const out   = [];
    let i = 0;
    while (i < lines.length) {
      const line = lines[i];

      // Code block
      if (line.trim().startsWith('```')) {
        const lang      = line.trim().slice(3).trim() || 'code';
        const codeLines = [];
        i++;
        while (i < lines.length && !lines[i].trim().startsWith('```')) {
          codeLines.push(lines[i]); i++;
        }
        i++;
        out.push(
          <div key={`code-${i}`} style={{
            margin: '8px 0', borderRadius: 8,
            background: '#111', border: '1px solid #2a2a2a', overflow: 'hidden',
          }}>
            <div style={{
              padding: '5px 12px', background: '#1a1a1a',
              borderBottom: '1px solid #2a2a2a',
              fontFamily: 'var(--mono)', fontSize: '10px',
              color: '#555', letterSpacing: '.07em', textTransform: 'uppercase',
            }}>{lang}</div>
            <pre style={{
              margin: 0, padding: '12px 14px',
              fontFamily: 'var(--mono)', fontSize: '12px',
              lineHeight: 1.65, color: '#e2e8f0',
              overflowX: 'auto', whiteSpace: 'pre',
              wordBreak: 'normal', wordWrap: 'normal',
            }}>
              {codeLines.join('\n')}
            </pre>
          </div>
        );
        continue;
      }

      // H3
      if (line.startsWith('### ')) {
        out.push(<div key={`h3-${i}`} style={{ fontFamily: 'var(--mono)', fontSize: '11px', fontWeight: 700, color: 'var(--t1)', letterSpacing: '.07em', textTransform: 'uppercase', marginTop: 14, marginBottom: 4 }}>{line.slice(4)}</div>);
        i++; continue;
      }
      // H2
      if (line.startsWith('## ')) {
        out.push(<div key={`h2-${i}`} style={{ fontFamily: 'var(--sans)', fontSize: '14px', fontWeight: 700, color: 'var(--t1)', marginTop: 16, marginBottom: 5, paddingBottom: 4, borderBottom: '1px solid var(--bd)' }}>{line.slice(3)}</div>);
        i++; continue;
      }
      // H1
      if (line.startsWith('# ')) {
        out.push(<div key={`h1-${i}`} style={{ fontFamily: 'var(--sans)', fontSize: '15px', fontWeight: 700, color: 'var(--t1)', marginTop: 14, marginBottom: 5 }}>{line.slice(2)}</div>);
        i++; continue;
      }

      // Bullet
      if (line.match(/^(\s*[-*•]\s)/)) {
        const indent = line.match(/^(\s*)/)[1].length;
        const text   = line.replace(/^\s*[-*•]\s/, '');
        out.push(
          <div key={`li-${i}`} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, paddingLeft: indent * 8, marginBottom: 3 }}>
            <div style={{ width: 4, height: 4, borderRadius: '50%', flexShrink: 0, marginTop: 8, background: 'var(--t2)' }}/>
            <span style={{ fontFamily: 'var(--sans)', fontSize: '13px', lineHeight: 1.72, color: 'var(--t2)', flex: 1, wordBreak: 'break-word' }}>{inlineFormat(text)}</span>
          </div>
        );
        i++; continue;
      }

      // Numbered list
      const numMatch = line.match(/^(\s*)(\d+)\.\s(.+)/);
      if (numMatch) {
        const [, spaces, num, text] = numMatch;
        out.push(
          <div key={`nl-${i}`} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, paddingLeft: spaces.length * 8, marginBottom: 4 }}>
            <span style={{ fontFamily: 'var(--mono)', fontSize: '12px', fontWeight: 600, color: 'var(--t1)', flexShrink: 0, minWidth: 16, lineHeight: 1.72 }}>{num}.</span>
            <span style={{ fontFamily: 'var(--sans)', fontSize: '13px', lineHeight: 1.72, color: 'var(--t2)', flex: 1, wordBreak: 'break-word' }}>{inlineFormat(text)}</span>
          </div>
        );
        i++; continue;
      }

      // HR
      if (line.trim() === '---' || line.trim() === '***') {
        out.push(<div key={`hr-${i}`} style={{ margin: '10px 0', height: 1, background: 'var(--bd)' }}/>);
        i++; continue;
      }

      // Empty
      if (line.trim() === '') {
        out.push(<div key={`sp-${i}`} style={{ height: 4 }}/>);
        i++; continue;
      }

      // Paragraph
      out.push(
        <div key={`p-${i}`} style={{ fontFamily: 'var(--sans)', fontSize: '13px', lineHeight: 1.78, color: 'var(--t2)', marginBottom: 2, wordBreak: 'break-word', overflowWrap: 'break-word' }}>
          {inlineFormat(line)}
        </div>
      );
      i++;
    }
    return out;
  }

  return (
    <div style={{
      flex: 1, display: 'flex', flexDirection: 'column',
      background: '#fafafa', borderRadius: 14,
      border: '1px solid var(--bd)', boxShadow: 'var(--shadow-md)',
      overflow: 'hidden', minHeight: 0, width: '100%', position: 'relative',
    }}>

      {/* Header */}
      <div style={{
        flexShrink: 0, height: 50,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 16px', borderBottom: '1px solid var(--bd)', background: '#fff',
      }}>
        <span style={{
          fontFamily: 'var(--mono)', fontSize: '11px', fontWeight: 700,
          color: '#20a9ff', background: '#eef5ff',
          padding: '3px 10px', borderRadius: 999,
        }}>
          Security Agent
        </span>
        {score != null && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <div style={{
              fontFamily: 'var(--mono)', fontSize: '11px', fontWeight: 700,
              color: scoreColor(score), padding: '3px 10px', borderRadius: 999,
              position: 'relative',
            }}>
              <div style={{
                position: 'absolute', inset: 0, borderRadius: 999,
                backgroundColor: scoreColor(score), opacity: 0.12,
              }}/>
              <span style={{ position: 'relative' }}>{score}/100</span>
            </div>
            <span style={{ fontFamily: 'var(--mono)', fontSize: '11px', fontWeight: 700, color: scoreColor(score) }}>
              {scoreLabel(score)}
            </span>
          </div>
        )}
      </div>

      {/* Messages */}
      <div ref={scrollRef} style={{
        flex: 1, overflowY: 'auto',
padding: '20px 16px 280px',
        scrollbarWidth: 'thin', scrollbarColor: 'var(--bg3) transparent',
        display: 'flex', flexDirection: 'column', gap: 14,
        width: '100%', boxSizing: 'border-box',
      }}>
        {messages.map(m => {
          const isAI = m.role === 'assistant';
          return (
            <div key={m.id} style={{
              display: 'flex', gap: 9, alignItems: 'flex-start',
              flexDirection: isAI ? 'row' : 'row-reverse',
              animation: 'msgIn .2s ease both',
              maxWidth: '100%',
            }}>
              <div style={{
                width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                background: isAI ? 'var(--t1)' : 'var(--bg2)',
                border: '1px solid var(--bd)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontFamily: 'var(--mono)', fontSize: '10px', fontWeight: 700,
                color: isAI ? '#fff' : 'var(--t2)',
              }}>
                {isAI ? 'AI' : 'You'}
              </div>
              <div style={{
                maxWidth: 'calc(100% - 80px)',
                minWidth: 0,
                background: isAI ? '#fff' : 'var(--t1)',
                border: isAI ? '1px solid #ebebeb' : 'none',
                borderRadius: isAI ? '3px 14px 14px 14px' : '14px 3px 14px 14px',
                padding: isAI ? '12px 14px 8px' : '10px 14px',
                boxShadow: isAI ? '0 1px 4px rgba(0,0,0,.05)' : '0 2px 8px rgba(0,0,0,.14)',
                overflow: 'hidden',
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
              }}>
                {isAI ? (
                  <div style={{ textAlign: 'left', minWidth: 0, overflow: 'hidden' }}>
                    {m.id === 'welcome' ? renderWelcome() : renderText(m.text)}
                  </div>
                ) : (
                  <div style={{
                    fontFamily: 'var(--sans)', fontSize: '13px', lineHeight: 1.72,
                    color: '#fff', textAlign: 'left',
                    whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflowWrap: 'break-word',
                  }}>
                    {m.text}
                  </div>
                )}
                <div style={{
                  fontFamily: 'var(--mono)', fontSize: '10px',
                  color: isAI ? 'var(--t3)' : 'rgba(255,255,255,.32)',
                  marginTop: 6, textAlign: isAI ? 'left' : 'right',
                }}>
                  {m.ts.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                </div>
              </div>
            </div>
          );
        })}

        {loading && (
          <div style={{ display: 'flex', gap: 9, alignItems: 'flex-start', animation: 'msgIn .2s ease both' }}>
            <div style={{
              width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
              background: 'var(--t1)', border: '1px solid var(--bd)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontFamily: 'var(--mono)', fontSize: '10px', fontWeight: 700, color: '#fff',
            }}>AI</div>
            <div style={{
              background: '#fff', border: '1px solid #ebebeb',
              borderRadius: '3px 14px 14px 14px', padding: '12px 15px',
              boxShadow: '0 1px 4px rgba(0,0,0,.05)',
              display: 'flex', alignItems: 'center', gap: 5,
            }}>
              {[0, 0.2, 0.4].map((d, j) => (
                <div key={j} style={{
                  width: 5, height: 5, borderRadius: '50%', background: 'var(--t3)',
                  animation: `livePulse 1s ${d}s infinite`,
                }}/>
              ))}
            </div>
          </div>
        )}
        <div ref={bottomRef}/>
      </div>
{/* Input area */}
<div style={{
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  padding: '10px 10px',
  paddingBottom: window.innerWidth <= 768
    ? 'max(80px, calc(env(safe-area-inset-bottom) + 74px))'
    : '14px',
  background: 'rgba(250,250,250,.97)',
  backdropFilter: 'blur(16px)',
  borderTop: '1px solid var(--bd)',
}}>

  {/* Quick prompt chips */}
  {!hasUserMsg && !loading && (
    <div style={{
      display: 'flex', gap: 6, flexWrap: 'nowrap',
      overflowX: 'auto', marginBottom: 8,
      scrollbarWidth: 'none', msOverflowStyle: 'none',
    }}>
      {QUICK_PROMPTS.map((p, i) => (
        <button key={i} onClick={() => send(p)} style={{
          padding: '5px 11px', borderRadius: 999, flexShrink: 0,
          border: '1px solid var(--bd)', background: '#fff',
          fontFamily: 'var(--mono)', fontSize: '11px', fontWeight: 500,
          color: 'var(--t2)', cursor: 'pointer', transition: 'all .15s',
          whiteSpace: 'nowrap',
        }}
          onMouseEnter={e => { e.currentTarget.style.borderColor='rgba(32,169,255,.35)'; e.currentTarget.style.background='#eef5ff'; e.currentTarget.style.color='#20a9ff'; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor='var(--bd)'; e.currentTarget.style.background='#fff'; e.currentTarget.style.color='var(--t2)'; }}
        >{p}</button>
      ))}
    </div>
  )}

  {/* Input box */}
  <div style={{
    background: '#fff', border: '1px solid var(--bd)',
    borderRadius: 14, padding: '10px 10px 8px',
    boxShadow: '0 1px 4px rgba(0,0,0,.06)',
    display: 'flex', flexDirection: 'column',
  }}>
    <textarea
      ref={textareaRef}
      value={input}
      onChange={e => {
        setInput(e.target.value);
        e.target.style.height = 'auto';
        e.target.style.height = Math.min(e.target.scrollHeight, 80) + 'px';
      }}
      onKeyDown={e => {
        if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey && window.innerWidth > 640) {
          e.preventDefault(); send();
        }
      }}
      placeholder="Ask about vulnerabilities, fixes, or risks…"
      rows={1}
      style={{
        width: '100%', background: 'transparent',
        border: 'none', outline: 'none', resize: 'none',
        fontFamily: 'var(--sans)', fontSize: '14px',
        color: 'var(--t1)', lineHeight: 1.5,
        minHeight: 22, maxHeight: 80,
        overflowY: 'auto', padding: 0,
      }}
    />

    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
      marginTop: 8, paddingTop: 7, borderTop: '1px solid var(--bd)',
    }}>
<button
  onClick={() => send()}
  disabled={!input.trim() || loading}
  style={{
    width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
    background: input.trim() || loading ? 'var(--t1)' : 'var(--bg2)',
    border: '1px solid var(--bd)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    cursor: input.trim() && !loading ? 'pointer' : 'default',
    transition: 'all .18s',
    boxShadow: input.trim() || loading ? '0 2px 6px rgba(0,0,0,.15)' : 'none',
  }}
>
  {loading
    ? <Spin s={11} c="#fff"/>
    : <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
        stroke={input.trim() ? '#fff' : 'var(--t3)'}
        strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 19V5m0 0-6 6m6-6 6 6"/>
      </svg>
  }
</button>
    </div>
  </div>
</div>
    </div>
  );
}
// ── Main component ────────────────────────────────────────────
export default function SharkBase() {
  const [tab,          setTab]          = useState('agent');
  const [target,       setTarget]       = useState('');
  const [mobIssuesOpen, setMobIssuesOpen] = useState(false);
  const [mobProgOpen, setMobProgOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [tos,          setTos]          = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [showSteps, setShowSteps] = useState(false);
  const  [attackOpen, setAttackOpen] = useState(false);
  const [findingsExpanded, setFindingsExpanded] = useState(false);
  const [agentsExpanded, setAgentsExpanded] = useState(false);
  const [scanMode, setScanMode] = useState(null); // null | 'unauth' | 'auth'
  const [showScanModeQ, setShowScanModeQ] = useState(false);
  const [urlError, setUrlError] = useState('');
  const scanType = useRef('agent');
  const [scanFn, setScanFn] = useState('getAgentScan');
const [branchError, setBranchError] = useState('');
const [showGithub,      setShowGithub]      = useState(false);
const [githubScanning,  setGithubScanning]  = useState(false);
const [githubScanId,    setGithubScanId]    = useState(null);
const [githubStep,   setGithubStep]   = useState('idle'); // 'idle' | 'ask-mode' | 'ask-branch'
const [githubRepo,   setGithubRepo]   = useState('');
const [baseBranch,   setBaseBranch]   = useState('main');
const [inputFading,  setInputFading]  = useState(false);
const [githubScan,      setGithubScan]      = useState(null);
const [githubFindings,  setGithubFindings]  = useState([]);
const [githubPollRef,   setGithubPollRef]   = useState(null);

const [elapsedSec, setElapsedSec] = useState(0);
  const [showClearMenu, setShowClearMenu] = useState(false);
  const [recentSidebarOpen, setRecentSidebarOpen] = useState(false);
    const [open, setOpen] = useState(false);
    const [allOpen, setAllOpen] = useState(false);
const [starting,     setStarting]     = useState(false);
  const [deploying,    setDeploying]    = useState(false);
    const [approving,    setApproving]    = useState(false);
  const [pdfBusy,      setPdfBusy]      = useState(false);
  const [agentLiveThought, setAgentLiveThought] = useState(null);
const [liveThoughtOpen,  setLiveThoughtOpen]  = useState(false);
const [showAllReasoning, setShowAllReasoning] = useState(false);
const [liveThoughts,     setLiveThoughts]     = useState([]);
const [browserFrame, setBrowserFrame] = useState(null);
const [subAgents, setSubAgents] = useState([]);
  const [scan,         setScan]         = useState(null);
  const [scanId,       setScanId]       = useState(null);
  const [thoughts,     setThoughts]     = useState([]);
  const [findings,     setFindings]     = useState([]);
  const [score,        setScore]        = useState(null);
  const [summary,      setSummary]      = useState('');
  const [recs,         setRecs]         = useState([]);
  const [recent,       setRecent]       = useState([]);
  const [scanDate,     setScanDate]     = useState('');
  const scanStartTs = useRef(null);
  const [progress,     setProgress]     = useState(0);
  const [liveThought,  setLiveThought]  = useState('');
  const [liveIdx,      setLiveIdx]      = useState(-1);
  const [showAllRecent,setShowAllRecent]= useState(false);
  const [sessionCookie,setSessionCookie]= useState('');
  const [showCookie,   setShowCookie]   = useState(false);
  const [appContext,   setAppContext]   = useState({ description:'', dataStored:'', worstCase:'', userTypes:'' });
  const [showContext,  setShowContext]  = useState(false);
  const [msgs,         setMsgs]         = useState([]);
  const [techLogs,     setTechLogs]     = useState([]);

  const pollRef   = useRef(null);
  const streamRef = useRef(null);
  const prevLen   = useRef(0);
  const approvedTs = useRef(null); 
  const [approved, setApproved] = useState(false);
  const liveTimer = useRef(null);
  const cQ        = useRef([]);
  const cA        = useRef(false);
  const reacted   = useRef(new Set());
  const ddEnd     = useRef(null);
  const logEnd    = useRef(null);

  const isRunning = scan?.status === 'running';
  const isGate    = scan?.status === 'awaiting_approval';
  const isDone    = scan?.status === 'done';
  const isStopped = scan?.status === 'stopped';
  const isActive  = isRunning || isGate;
  const usedTools = new Set(thoughts.filter(t => t.type === 'tool_call').map(t => t.tool));
const [drawerOpen, setDrawerOpen] = useState(false);
const [drawerClosing, setDrawerClosing] = useState(false);
const [mobSidebarOpen, setMobSidebarOpen] = useState(false);
const [mobSidebarClosing, setMobSidebarClosing] = useState(false);

const stages = useMemo(() => {
  const thoughts = scan?.thoughts || [];
  const stageMap = {};
  for (const t of thoughts) {
    const stage = t.stage || 'Recon';
    if (!stageMap[stage]) stageMap[stage] = { name: stage, tools: [], status: 'done' };
    stageMap[stage].tools.push(t);
  }
  return Object.values(stageMap);
}, [scan?.thoughts]);
const af = findings.filter(f =>
  f.severity !== 'INFO' &&
  !f.falsePositive &&
  !f.exiting 
);
async function startMobileScan(file) {
  if (!tos) return;
  setUploading(true);  // ← immediately shows spinner on button

  try {
    const { getStorage, ref, uploadBytes, getDownloadURL } = await import('firebase/storage');
    const storage    = getStorage();
    const storageRef = ref(storage, `mobile-scans/${Date.now()}_${file.name}`);

    await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(storageRef);

    setUploading(false);
    setStarting(true);

    setScan({ status: 'running', target: file.name });
    setThoughts([]); setFindings([]);
    setScore(null); setSummary(''); setRecs([]);
    scanStartTs.current  = Date.now();
    finalElapsed.current = null;
    scanType.current     = 'mobile';

    const r = await SecureAPI.callFunction('startMobileScan', {
      fileUrl:  downloadURL,
      fileName: file.name,
    });

    if (r?.scanId) setScanId(r.scanId);

  } catch (e) {
    console.error('Mobile scan error:', e);
    setScan(null);
    setUrlError('Upload failed — check your connection and try again.');
  }

  setUploading(false);
  setStarting(false);
  setUploadedFile(null);
}
// How many false positives have been removed this session
// Used to show the subtle notice at the bottom of the panel
const removedCount = findings.filter(f => f.falsePositive).length;
function openDrawer()  { setDrawerOpen(true); setDrawerClosing(false); }
const finalElapsed = useRef(null);
useEffect(() => {
  if (!isActive || !scanStartTs.current) return;
  const tick = () => setElapsedSec(Math.floor((Date.now() - scanStartTs.current) / 1000));
  tick();
  const id = setInterval(tick, 1000);
  return () => clearInterval(id);
}, [isActive]);

// Freeze elapsed when done
useEffect(() => {
  if ((isDone || isStopped) && scanStartTs.current && finalElapsed.current === null) {
    finalElapsed.current = Math.floor((Date.now() - scanStartTs.current) / 1000);
  }
}, [isDone, isStopped]);
function closeDrawer() {
  setDrawerClosing(true);
  setTimeout(() => { setDrawerOpen(false); setDrawerClosing(false); }, 220);
}
   const [mobRecentOpen, setMobRecentOpen] = useState(false);
 const [mobRecentClosing, setMobRecentClosing] = useState(false);

 function openMobRecent()  { setMobRecentOpen(true); setMobRecentClosing(false); }
 function closeMobRecent() {
setMobRecentClosing(true); setTimeout(() => { setMobRecentOpen(false); 
  setMobRecentClosing(false); }, 220);
 }
const tsNow = () => new Date().toLocaleTimeString('en-US', {hour12:true, hour:'numeric', minute:'2-digit'});
  const addLog = useCallback((msg, t='info') => setTechLogs(p => [...p, {msg, t, ts:tsNow()}]), []);

  // Cookie event listener
  useEffect(() => {
    const handler = (e) => {
      const { cookie, targetUrl, targetHostname } = e.detail;
      setSessionCookie(cookie);
      setShowCookie(true);
      if (!scan && targetUrl) setTarget(targetHostname || targetUrl);
    };
    window.addEventListener('sharkbase:cookie', handler);
    return () => window.removeEventListener('sharkbase:cookie', handler);
  }, [scan]);

  // Load history
  useEffect(() => {
    SecureAPI.callFunction('listAgentScans', {})
      .then(r => { if (r?.scans) setRecent(r.scans); })
      .catch(() => {});
  }, []);

// Auto-scroll agent stream
  useEffect(() => {
    if (streamRef.current) streamRef.current.scrollTop = streamRef.current.scrollHeight;
  }, [thoughts, liveThought]);

// Dismiss deploy overlay once first thought OR live thought arrives
useEffect(() => {
  if (deploying && (thoughts.length > 0 || agentLiveThought?.text)) {
    setDeploying(false);
  }
}, [thoughts.length, agentLiveThought, deploying]);

useEffect(() => {
  if (!deploying) return;
  const id = setTimeout(() => setDeploying(false), 200);
  return () => clearTimeout(id);
}, [deploying]);
  // Auto-scroll log
  useEffect(() => {
    if (logEnd.current) logEnd.current.scrollIntoView({behavior:'smooth'});
  }, [thoughts]);

// Char-by-char stream for thinking rows
  useEffect(() => {
    if (thoughts.length === 0) return;
    const last = thoughts[thoughts.length - 1];
    if (last.type !== 'thinking' || last._streamed) return;
    if (thoughts.length <= prevLen.current) return;
    prevLen.current = thoughts.length;

    setThoughts(prev => prev.map((t, i) =>
      i === prev.length - 1 ? {...t, _streamed: true} : t
    ));

    const fullText = last.text || '';
    setLiveThought('');
    setLiveIdx(thoughts.length - 1);
    let i = 0;

    if (liveTimer.current) {
      clearInterval(liveTimer.current);
      liveTimer.current = null;
    }

    liveTimer.current = setInterval(() => {
      if (i >= fullText.length) {
        clearInterval(liveTimer.current);
        liveTimer.current = null;
        setThoughts(prev => prev.map((t, idx) =>
          idx === prev.length - 1 ? {...t, text: fullText, _done: true} : t
        ));
        setLiveThought('');
        setLiveIdx(-1);
        return;
      }
      setLiveThought(fullText.slice(0, ++i));
    }, 9);

    return () => {
      if (liveTimer.current) {
        clearInterval(liveTimer.current);
        liveTimer.current = null;
      }
      setThoughts(prev => prev.map((t, idx) =>
        idx === prev.length - 1 && !t._done ? {...t, text: fullText, _done: true} : t
      ));
      setLiveThought('');
      setLiveIdx(-1);
    };
  }, [thoughts.length]);
  // Agent commentary queue
  const drainQ = useCallback(async () => {
    if (cA.current) return;
    cA.current = true;
    while (cQ.current.length > 0) { const t = cQ.current.shift(); await t(); }
    cA.current = false;
  }, []);
  const qMsg = useCallback(fn => { cQ.current.push(fn); drainQ(); }, [drainQ]);
  const addMsg = useCallback((ag, text, chip=null) => new Promise(async res => {
    if (!text?.trim()) { res(); return; }
    const id = Date.now() + Math.random();
    setMsgs(p => [...p, {id, ag, typing:true}]);
    await dl(380 + Math.random()*140);
    setMsgs(p => p.map(m => m.id === id ? {id, ag, text:text.trim(), chip, typing:false} : m));
    await dl(text.split(' ').length * 14 + 70);
    res();
  }), []);

  // React to findings
  useEffect(() => {
    for (const f of findings) {
      if (f.severity === 'INFO') continue;
      const key = `${f.type || f.title}-${(f.verdict || f.title || '').slice(0, 30)}`;
      if (reacted.current.has(key)) continue;
      reacted.current.add(key);
      if (f.severity === 'LOW') {
        qMsg(async () => { await addMsg('1', AGENT_MSGS['1'](f)); });
      } else {
        const riskColor = f.severity === 'CRITICAL'
          ? {color:'rgba(252,99,99,.9)', bg:'rgba(252,99,99,.07)', border:'rgba(252,99,99,.22)', label:'Critical Risk'}
          : f.severity === 'HIGH'
          ? {color:'rgba(251,150,60,.9)', bg:'rgba(251,150,60,.07)', border:'rgba(251,150,60,.2)', label:'High Risk'}
          : {color:'rgba(250,210,40,.8)', bg:'rgba(250,210,40,.06)', border:'rgba(250,210,40,.16)', label:'Medium Risk'};
        qMsg(async () => {
          await addMsg('1', AGENT_MSGS['1'](f), riskColor);
          await addMsg('2', AGENT_MSGS['2'](f.severity), riskColor);
        });
      }
    }
  }, [findings]);



  useEffect(() => { ddEnd.current?.scrollIntoView({behavior:'smooth'}); }, [msgs]);

  // Start
async function start() {
  const raw = target.trim();
  if (!raw || !tos) return;

  // ── Validate URL ───────────────────────────────────────────
  const validation = validateTarget(raw);
  if (!validation.valid) {
    setUrlError(validation.error || 'Enter a valid URL.');
    return;
  }
  setUrlError('');

  let tgt = validation.normalized;

  // ── GitHub URL → question flow ─────────────────────────────
  if (tgt.includes('github.com/')) {
    const repo = tgt.replace(/\.git$/, '');
    setGithubRepo(repo);
    setInputFading(true);
    setTimeout(() => {
      setInputFading(false);
      setGithubStep('ask-mode');
    }, 260);
    return;
  }

  // ── Normal web scan ────────────────────────────────────────
  setStarting(true);
  setSubAgents([]);
  scanType.current  = 'agent';
  setScanFn('getAgentScan');
  setScanDate(new Date().toLocaleString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  }));

  // Reset all state
  setScan(null);
  setThoughts([]);
  setFindings([]);
  setScore(null);
  setSummary('');
  setRecs([]);
  setMsgs([]);
  setTechLogs([]);
  setLiveThought('');
  setLiveIdx(-1);
  setProgress(0);
  setAgentLiveThought(null);
  setLiveThoughts([]);
  setBrowserFrame(null);
  setLiveThoughtOpen(false);
  prevLen.current    = 0;
  reacted.current    = new Set();
  cQ.current         = [];
  cA.current         = false;
  scanStartTs.current = Date.now();
  finalElapsed.current = null;

  clearInterval(pollRef.current);
  clearInterval(liveTimer.current);

  try {
    setScan({ status: 'running', target: tgt });
    setDeploying(false);

    const r = await SecureAPI.callFunction('startAgent', {
      target:        tgt,
      sessionCookie: sessionCookie.trim(),
      appContext:    Object.values(appContext).some(v => v.trim()) ? appContext : null,
    });

    if (r?.scanId) {
      setScanId(r.scanId);
      addLog(`Target: ${tgt}`, 'system');
    }
  } catch (e) {
    console.error('Start error:', e);
    setUrlError('Could not connect — check your internet and try again.');
    setScan(null);
  }

  setStarting(false);
}
async function launchGithubScan(mode, branch) {
  const repo = githubRepo;

  // Validate branch exists before doing anything
  if (mode === 'recent' && branch.trim()) {
    const match = repo.match(/github\.com\/([^/]+)\/([^/?#]+)/);
    if (match) {
      const [, owner, repoName] = match;
      try {
        const r = await fetch(
          `https://api.github.com/repos/${owner}/${repoName.replace(/\.git$/, '')}/branches/${branch.trim()}`,
          { headers: { 'Accept': 'application/vnd.github.v3+json', 'User-Agent': 'SharkBase' } }
        );
        if (!r.ok) {
          setBranchError(`Branch "${branch.trim()}" not found in this repo.`);
          return;
        }
      } catch {
        setBranchError('Could not verify branch — check your connection.');
        return;
      }
    }
  }

  setBranchError('');
  setGithubStep('idle');
  setTarget(repo);
  setStarting(true);
  setSubAgents([]);
  scanType.current = 'github';
  setScanFn('getGithubScan');
  setScanDate(new Date().toLocaleString('en-US', { year:'numeric', month:'long', day:'numeric', hour:'2-digit', minute:'2-digit' }));
  setScan({ status:'running', target:repo });
  setGithubScan({ status:'running', target:repo, thoughts:[], liveThought:null });
  setGithubFindings([]);
  setGithubScanning(true);
  setThoughts([]); setFindings([]);
  setScore(null); setSummary(''); setRecs([]);
  setMsgs([]); setTechLogs([]);
  setLiveThought(''); setLiveIdx(-1); setProgress(0);
  scanStartTs.current = Date.now();
  prevLen.current = 0; reacted.current = new Set();
  cQ.current = []; cA.current = false;
  clearInterval(pollRef.current);
  clearInterval(liveTimer.current);
  try {
    const r = await SecureAPI.callFunction('startGithubScan', {
      repoUrl: repo,
      ...(mode === 'recent' && branch.trim() ? { baseBranch: branch.trim() } : {}),
    });
    if (r?.scanId) setScanId('github::' + r.scanId);
  } catch(e) {
    console.error('GitHub scan error:', e);
    setScan(null); setGithubScan(null); setGithubScanning(false);
  }
  setStarting(false);
}
async function startGithubScan() {
  let repo = githubRepo.trim();
  if (!repo || !tos) return;
  repo = repo.replace(/\.git$/, '');
  if (!repo.startsWith('http')) repo = 'https://github.com/' + repo;

  setTarget(repo);
  setStarting(true);
  setSubAgents([]);
  scanType.current = 'github';
  setScanFn('getGithubScan');
  setScanDate(new Date().toLocaleString('en-US', {year:'numeric',month:'long',day:'numeric',hour:'2-digit',minute:'2-digit'}));
  setScan({ status:'running', target:repo });
  setGithubScan({ status:'running', target:repo, thoughts:[], liveThought:null });
  setGithubFindings([]);
  setGithubScanning(true); // ← stays true until poll flips it
  setThoughts([]); setFindings([]);
  setScore(null); setSummary(''); setRecs([]);
  setMsgs([]); setTechLogs([]);
  setLiveThought(''); setLiveIdx(-1); setProgress(0);
  scanStartTs.current = Date.now();
  prevLen.current = 0; reacted.current = new Set();
  cQ.current = []; cA.current = false;
  clearInterval(pollRef.current);
  clearInterval(liveTimer.current);

  try {
const r = await SecureAPI.callFunction('startGithubScan', {
  repoUrl: repo,
  ...(baseBranch.trim() ? { baseBranch: baseBranch.trim() } : {}),
});    if (r?.scanId) {
      setScanId('github::' + r.scanId);
    }
  } catch(e) {
    console.error('GitHub scan error:', e);
    setScan(null);
    setGithubScan(null);
    setGithubScanning(false);
  }
  setStarting(false);
  // NOTE: do NOT call setGithubScanning(false) here — the poll useEffect handles it
}


// ============================================
// FIX 2 — poll useEffect
// ============================================
useEffect(() => {
  if (!scanId) return;
  let consecutiveErrors = 0;
  const MAX_ERRORS = 3;

  const isGithub  = scanId.startsWith('github::');
  const realScanId = isGithub ? scanId.slice(8) : scanId;
  const fnName     = isGithub ? 'getGithubScan' : 'getAgentScan';

  async function poll() {
    try {
      const r = await SecureAPI.callFunction(fnName, { scanId: realScanId }, 'GET');
      consecutiveErrors = 0;
      if (!r?.scan) return;

      setScan(r.scan);

      // ── GitHub scan ──────────────────────────────────────────
      if (isGithub) {
        setGithubScan(r.scan);
        setGithubScanning(r.scan.status === 'running');
if (r.scan.subAgents?.length) {
  setSubAgents(r.scan.subAgents);
}
        // Filter false positives before setting findings
        const liveFindings = (r.findings || []).filter(f => !f.falsePositive);
        setGithubFindings(liveFindings);
        setFindings(liveFindings);

        if (r.scan.score != null) setScore(r.scan.score);
        if (r.scan.summary)       setSummary(r.scan.summary);
if (r.scan.recommendations?.length) setRecs(Array.isArray(r.scan.recommendations) ? r.scan.recommendations : []);

        const incoming = r.thoughts || [];
        setThoughts(prev => incoming.length > prev.length ? incoming : prev);

        if (r.liveThought?.text) {
          setAgentLiveThought(r.liveThought);
        } else if (['done','stopped','error'].includes(r.scan.status)) {
          setTimeout(() => setAgentLiveThought(null), 3000);
        }

        if (r.liveThoughts?.length) setLiveThoughts(r.liveThoughts);

        if (['done','stopped','error'].includes(r.scan.status)) {
          clearInterval(pollRef.current);
          setProgress(100);
          SecureAPI.callFunction('listAgentScans', {})
            .then(lr => { if (lr?.scans) setRecent(lr.scans); }).catch(() => {});
        }
        return;
      }

      // ── Agent (URL) scan ─────────────────────────────────────
      // Filter false positives before setting — anything marked
      // falsePositive: true gets removed from the display immediately
const confirmedFindings = (r.findings || []).filter(f => !f.falsePositive);
setFindings(prev => {
  if (!prev.length) return confirmedFindings;
  
  // Merge — update existing findings in place, add new ones
  const updated = prev.map(existing => {
    const fresh = confirmedFindings.find(f => f.title === existing.title);
    if (!fresh) return existing; // keep showing until explicitly removed
    // Severity changed — animate the update
    if (fresh.severity !== existing.severity) {
      return { ...fresh, _severityChanged: true };
    }
    return { ...existing, ...fresh };
  });
  
  // Add any brand new findings not in prev
  const prevTitles = new Set(prev.map(f => f.title));
  const newOnes = confirmedFindings.filter(f => !prevTitles.has(f.title));
  
  return [...updated, ...newOnes];
});
      // Track which findings were just removed so we can animate them out
      setFindings(prev => {
        const prevTitles     = new Set(prev.map(f => f.title));
        const currentTitles  = new Set(confirmedFindings.map(f => f.title));
        // Any finding that was showing before but is now gone = false positive removal
        const removed = prev.filter(f => !currentTitles.has(f.title));
        if (removed.length > 0) {
          // Mark them as animating out briefly before full removal
          const withExiting = prev.map(f =>
            !currentTitles.has(f.title)
              ? { ...f, exiting: true }
              : f
          );
          // Remove fully after animation completes
          setTimeout(() => setFindings(confirmedFindings), 450);
          return withExiting;
        }
        return confirmedFindings;
      });

      if (r.scan?.subAgents?.length) {
        setSubAgents(r.scan.subAgents);
      } else if (r.liveThoughts?.length) {
        const agentIds = ['recon', 'active', 'auth'];
        const seen = agentIds.filter(id =>
          r.liveThoughts.some(t => t.toolName === id)
        );
        if (seen.length > 0) {
          setSubAgents(prev => {
            const existingIds = new Set(prev.map(a => a.id));
            const additions   = seen
              .filter(id => !existingIds.has(id))
              .map(id => ({ id, status: 'running', findings: 0, tasks: '' }));
            if (!additions.length) return prev;
            return [...prev, ...additions];
          });
        }
      }

      if (r.liveThought?.text) {
        setAgentLiveThought(r.liveThought);
        if (['done','stopped','error'].includes(r.scan.status)) {
          setTimeout(() => setAgentLiveThought(null), 5000);
        }
      } else if (['done','stopped','error'].includes(r.scan.status)) {
        setTimeout(() => setAgentLiveThought(null), 3000);
      }

      if (r.liveThoughts?.length) setLiveThoughts(r.liveThoughts);

      if (r.browserFrame?.data) {
        setBrowserFrame(r.browserFrame);
      } else if (r.browserFrame?.loading) {
        setBrowserFrame({
          data:    null,
          label:   r.browserFrame.label || r.scan?.target || '',
          loading: true,
          ts:      r.browserFrame.ts,
        });
      } else if (r.browserFrame === null) {
        setBrowserFrame(null);
      }

      if (r.scan.score != null) setScore(r.scan.score);
      if (r.scan.summary)       setSummary(r.scan.summary);
if (r.scan.recommendations?.length) setRecs(Array.isArray(r.scan.recommendations) ? r.scan.recommendations : []);

      const incoming = r.thoughts || [];
      setThoughts(prev => incoming.length <= prev.length ? prev : incoming);

      const toolCalls = incoming.filter(t => t.type === 'tool_call').length;
      setProgress(Math.min(95, Math.round((toolCalls / 13) * 100)));

      if (['done','stopped','error'].includes(r.scan.status)) {
        clearInterval(pollRef.current);
        setProgress(100);
        SecureAPI.callFunction('listAgentScans', {})
          .then(lr => { if (lr?.scans) setRecent(lr.scans); }).catch(() => {});
      }

    } catch(e) {
      consecutiveErrors++;
      const msg     = e?.message || '';
      const isFatal = e?.status === 404 ||
        msg.includes('404') ||
        msg.toLowerCase().includes('not found');
      if (isFatal || consecutiveErrors >= MAX_ERRORS) {
        clearInterval(pollRef.current);
        setScan(prev => {
          if (!prev || ['done','stopped','error'].includes(prev.status)) return prev;
          return { ...prev, status: 'error' };
        });
        if (isGithub) {
          setGithubScan(prev => prev ? { ...prev, status: 'error' } : prev);
          setGithubScanning(false);
        }
      }
    }
  }

  const initialDelay = isGithub ? 2000 : 0;
  const startPolling = setTimeout(() => {
    poll();
    pollRef.current = setInterval(poll, 600);
  }, initialDelay);

  return () => {
    clearTimeout(startPolling);
    clearInterval(pollRef.current);
  };
}, [scanId]);
  // Stop
  async function stop() {
    if (!scanId) return;
    clearInterval(pollRef.current);
    setScan(p => ({...p, status:'stopped'}));
    SecureAPI.callFunction('stopAgent', { scanId }).catch(() => {});
  }
const AGENT_ICONS = {
  recon: (
<svg width="12" height="12" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M4.67129 3.14634C4.47603 3.34161 4.47603 3.65819 4.67129 3.85345L7.14616 6.32833C7.34142 6.52359 7.65801 6.52359 7.85327 6.32833L10.3281 3.85345C10.5234 3.65819 10.5234 3.34161 10.3281 3.14634L7.85327 0.671471C7.65801 0.476209 7.34142 0.476209 7.14616 0.671471L4.67129 3.14634ZM7.49971 5.26766L5.73195 3.4999L7.49971 1.73213L9.26748 3.4999L7.49971 5.26766ZM8.67129 7.14634C8.47603 7.34161 8.47603 7.65819 8.67129 7.85345L11.1462 10.3283C11.3414 10.5236 11.658 10.5236 11.8533 10.3283L14.3281 7.85345C14.5234 7.65819 14.5234 7.34161 14.3281 7.14634L11.8533 4.67147C11.658 4.47621 11.3414 4.47621 11.1462 4.67147L8.67129 7.14634ZM11.4997 9.26766L9.73195 7.4999L11.4997 5.73213L13.2675 7.4999L11.4997 9.26766ZM4.67129 11.8535C4.47603 11.6582 4.47603 11.3416 4.67129 11.1463L7.14616 8.67147C7.34142 8.47621 7.65801 8.47621 7.85327 8.67147L10.3281 11.1463C10.5234 11.3416 10.5234 11.6582 10.3281 11.8535L7.85327 14.3283C7.65801 14.5236 7.34142 14.5236 7.14616 14.3283L4.67129 11.8535ZM5.73195 11.4999L7.49971 13.2677L9.26748 11.4999L7.49971 9.73213L5.73195 11.4999ZM0.671288 7.14649C0.476026 7.34175 0.476026 7.65834 0.671288 7.8536L3.14616 10.3285C3.34142 10.5237 3.65801 10.5237 3.85327 10.3285L6.32814 7.8536C6.5234 7.65834 6.5234 7.34175 6.32814 7.14649L3.85327 4.67162C3.65801 4.47636 3.34142 4.47636 3.14616 4.67162L0.671288 7.14649ZM3.49972 9.26781L1.73195 7.50005L3.49972 5.73228L5.26748 7.50005L3.49972 9.26781Z" fill="currentColor" fill-rule="evenodd" clip-rule="evenodd"></path></svg>


  ),
  active: (
<svg width="12" height="12" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M4.5 2C3.11929 2 2 3.11929 2 4.5C2 5.88072 3.11929 7 4.5 7C5.88072 7 7 5.88072 7 4.5C7 3.11929 5.88072 2 4.5 2ZM3 4.5C3 3.67157 3.67157 3 4.5 3C5.32843 3 6 3.67157 6 4.5C6 5.32843 5.32843 6 4.5 6C3.67157 6 3 5.32843 3 4.5ZM10.5 2C9.11929 2 8 3.11929 8 4.5C8 5.88072 9.11929 7 10.5 7C11.8807 7 13 5.88072 13 4.5C13 3.11929 11.8807 2 10.5 2ZM9 4.5C9 3.67157 9.67157 3 10.5 3C11.3284 3 12 3.67157 12 4.5C12 5.32843 11.3284 6 10.5 6C9.67157 6 9 5.32843 9 4.5ZM2 10.5C2 9.11929 3.11929 8 4.5 8C5.88072 8 7 9.11929 7 10.5C7 11.8807 5.88072 13 4.5 13C3.11929 13 2 11.8807 2 10.5ZM4.5 9C3.67157 9 3 9.67157 3 10.5C3 11.3284 3.67157 12 4.5 12C5.32843 12 6 11.3284 6 10.5C6 9.67157 5.32843 9 4.5 9ZM10.5 8C9.11929 8 8 9.11929 8 10.5C8 11.8807 9.11929 13 10.5 13C11.8807 13 13 11.8807 13 10.5C13 9.11929 11.8807 8 10.5 8ZM9 10.5C9 9.67157 9.67157 9 10.5 9C11.3284 9 12 9.67157 12 10.5C12 11.3284 11.3284 12 10.5 12C9.67157 12 9 11.3284 9 10.5Z" fill="currentColor" fill-rule="evenodd" clip-rule="evenodd"></path></svg>
  ),
  auth: (
<svg width="12" height="12" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3.87935 1H3.9H11.1H11.1207C11.5231 0.999994 11.8553 0.999989 12.1259 1.0221C12.407 1.04506 12.6653 1.09434 12.908 1.21799C13.2843 1.40973 13.5903 1.7157 13.782 2.09202C13.9057 2.33469 13.9549 2.59304 13.9779 2.87409C14 3.14468 14 3.47686 14 3.87934V3.9V11.1V11.1207C14 11.5231 14 11.8553 13.9779 12.1259C13.9549 12.407 13.9057 12.6653 13.782 12.908C13.5903 13.2843 13.2843 13.5903 12.908 13.782C12.6653 13.9057 12.407 13.9549 12.1259 13.9779C11.8553 14 11.5231 14 11.1207 14H11.1H3.9H3.87934C3.47686 14 3.14468 14 2.87409 13.9779C2.59304 13.9549 2.33469 13.9057 2.09202 13.782C1.7157 13.5903 1.40973 13.2843 1.21799 12.908C1.09434 12.6653 1.04506 12.407 1.0221 12.1259C0.999989 11.8553 0.999994 11.5231 1 11.1207V11.1207V11.1V3.9V3.87935V3.87934C0.999994 3.47686 0.999989 3.14468 1.0221 2.87409C1.04506 2.59304 1.09434 2.33469 1.21799 2.09202C1.40973 1.7157 1.7157 1.40973 2.09202 1.21799C2.33469 1.09434 2.59304 1.04506 2.87409 1.0221C3.14469 0.999989 3.47687 0.999994 3.87935 1ZM2.95552 2.01878C2.73631 2.03669 2.62421 2.06915 2.54601 2.10899C2.35785 2.20487 2.20487 2.35785 2.10899 2.54601C2.06915 2.62421 2.03669 2.73631 2.01878 2.95552C2.00039 3.18056 2 3.47171 2 3.9V7H7V2H3.9C3.47171 2 3.18056 2.00039 2.95552 2.01878ZM7 8H2V11.1C2 11.5283 2.00039 11.8194 2.01878 12.0445C2.03669 12.2637 2.06915 12.3758 2.10899 12.454C2.20487 12.6422 2.35785 12.7951 2.54601 12.891C2.62421 12.9309 2.73631 12.9633 2.95552 12.9812C3.18056 12.9996 3.47171 13 3.9 13H7V8ZM8 8H13V11.1C13 11.5283 12.9996 11.8194 12.9812 12.0445C12.9633 12.2637 12.9309 12.3758 12.891 12.454C12.7951 12.6422 12.6422 12.7951 12.454 12.891C12.3758 12.9309 12.2637 12.9633 12.0445 12.9812C11.8194 12.9996 11.5283 13 11.1 13H8V8ZM13 7H8V2H11.1C11.5283 2 11.8194 2.00039 12.0445 2.01878C12.2637 2.03669 12.3758 2.06915 12.454 2.10899C12.6422 2.20487 12.7951 2.35785 12.891 2.54601C12.9309 2.62421 12.9633 2.73631 12.9812 2.95552C12.9996 3.18056 13 3.47171 13 3.9V7Z" fill="currentColor" fill-rule="evenodd" clip-rule="evenodd"></path></svg>
  ),
};

// ============================================
// SUB AGENT PANEL
// ============================================
function SubAgentPanel({ agents=[], liveThoughts=[], agentThoughts={}, isRunning=false, expanded=false, onToggleExpanded }) {
  const [visible, setVisible] = useState(true);
useEffect(() => {
  const allDone = agents.length > 0 && agents.every(a => a.status === 'done');
  if (allDone && !isRunning) {
    const t = setTimeout(() => setVisible(false), 8000);
    return () => clearTimeout(t);
  }
}, [agents, isRunning]);
  if (!agents.length || !visible) return null;

  const runningCount = agents.filter(a => a.status === 'running').length;

  return (
    <div style={{
      margin:'-1px 14px 8px',
      borderRadius:'0 0 12px 12px',
      border:'1px solid var(--bd)', borderTop:'none',
      background:'#fff', overflow:'hidden',
    }}>
      <div style={{
        padding:'5px 14px', borderBottom:'1px solid var(--bd)',
        display:'flex', alignItems:'center', gap:6, background:'var(--bg1)',
      }}>
        <div style={{width:1.5, height:12, background:'var(--bd)', borderRadius:2}}/>
        <span style={{
          fontFamily:'var(--mono)', fontSize:'11px', fontWeight:600,
          color:'var(--t3)', letterSpacing:'.06em', textTransform:'uppercase',
        }}>Calling Sub-agents</span>
      </div>

{(expanded ? agents : agents.slice(0, 2)).map((agent, idx) => (
  <AgentRow key={agent.id} agent={agent} isLast={idx === agents.length - 1} isRunning={isRunning}/>
))}
{agents.length > 2 && (
  <div
onClick={() => onToggleExpanded()}
    style={{
      padding:'6px 14px', cursor:'pointer',
      fontFamily:'var(--mono)', fontSize:'11px',
      color:'var(--t3)', background:'var(--bg1)',
      borderTop:'1px solid var(--bd)',
      transition:'color .15s',
    }}
    onMouseEnter={e => e.currentTarget.style.color='var(--t1)'}
    onMouseLeave={e => e.currentTarget.style.color='var(--t3)'}
  >
    {expanded ? '↑ Show less' : `+ ${agents.length - 2} more agents`}
  </div>
)}
    </div>
  );
}

function AgentRow({ agent, isLast, isRunning }) {
  const [gone,      setGone]      = useState(false);
  const [opacity,   setOpacity]   = useState(1);
  const wasDone = useRef(false);

const AGENT_META = {
  general: { label:'General', color:'#6b7280', bg:'#f3f4f6' },
  recon:    { label:'Recon',    color:'#7c3aed', bg:'#f3f0ff' },
  active:   { label:'Active',   color:'#0ea5e9', bg:'#e0f2fe' },
  auth:     { label:'Auth',     color:'#7c3aed', bg:'#f3f0ff' },
  routes:   { label:'Routes',   color:'#0ea5e9', bg:'#e0f2fe' },
  config:   { label:'Config',   color:'#d97706', bg:'#fef3c7' },
  db:       { label:'Database', color:'#10b981', bg:'#d1fae5' },
  frontend: { label:'Frontend', color:'#ef4444', bg:'#fee2e2' },
};
const meta = AGENT_META[agent?.id] || { label: agent?.id || 'Agent', color:'var(--t2)', bg:'var(--bg2)' };
const fileCount = agent.fileCount ? `${agent.fileCount} files` : '';
  const isDone = agent.status === 'done';

useEffect(() => {
  if (isDone && !wasDone.current) {
    wasDone.current = true;
    setOpacity(1); // force visible first so fade looks correct
    const t1 = setTimeout(() => setOpacity(0), 1500);
    const t2 = setTimeout(() => setGone(true), 2000);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }
  if (!isDone && wasDone.current) {
    wasDone.current = false;
    setGone(false);
    setOpacity(1);
  }
}, [isDone]);
  if (gone) return null;

  const findings = agent.findings || 0;
  const pill = isDone
    ? findings > 0
      ? { label:`${findings} issue${findings !== 1 ? 's' : ''}`, color:'#ef4444', bg:'#fee2e2' }
      : { label:'done', color:'#20a9ff', bg:'#eef5ff' }
    : { label:'running', color:'var(--t3)', bg:'var(--bg2)' };

  return (
    <div style={{
      borderBottom: isLast ? 'none' : '1px solid var(--bd)',
      padding:'7px 14px',
      display:'flex', alignItems:'center', gap:8,
      opacity,
      transition:'opacity .5s ease',
    }}>
      {/* Agent label */}
      <span style={{
        fontFamily:'var(--mono)', fontSize:'11px', fontWeight:700,
        color:meta.color, background:meta.bg,
        padding:'2px 9px', borderRadius:999, flexShrink:0,
      }}>
        {meta.label}
      </span>

      <div style={{flex:1}}/>

{/* File count */}
      {fileCount && (
        <span style={{
          fontFamily:'var(--mono)', fontSize:'10px', color:'var(--t3)', flexShrink:0,
        }}>
          {fileCount}
        </span>
      )}

      {/* Status pill — updates live */}
      <span style={{
        fontFamily:'var(--mono)', fontSize:'11px', fontWeight:500,
        color:pill.color, background:pill.bg,
        padding:'2px 9px', borderRadius:999, flexShrink:0,
        transition:'all .3s ease',
      }}>
        {pill.label}
      </span>

      {/* Spinner → checkmark when done */}
      <div style={{
        width:20, height:20, borderRadius:'50%',
        background:'var(--bg2)', border:'1px solid var(--bd)',
        display:'flex', alignItems:'center', justifyContent:'center',
        flexShrink:0,
      }}>
        {isDone ? (
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none"
            stroke="#20a9ff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
            style={{animation:'blurAppear .2s ease both'}}
          >
            <path d="M20 6L9 17l-5-5"/>
          </svg>
        ) : (
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none"
            stroke="var(--t2)" strokeWidth="2.5" strokeLinecap="round"
            style={{animation:'spin .8s linear infinite'}}
          >
            <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
          </svg>
        )}
      </div>
    </div>
  );
}
  // Approve
async function approve(yes, all = false) {
  setApproving(true);
  setApproved(true); // hides card immediately
  setScan(p => ({ ...p, status: 'running', pendingApproval: null }));
  try {
    await SecureAPI.callFunction('approveStep', { scanId, approve: yes, approveAll: all });
  } catch (e) {
    console.error('Approve error:', e);
    setApproved(false); // show card again if it failed
    setScan(p => ({ ...p, status: 'awaiting_approval' }));
  } finally {
    setApproving(false);
    setTimeout(() => setApproved(false), 3000); // cleanup after Firestore catches up
  }
}
  // Reset
function reset() {
  setSubAgents([]);
  clearInterval(pollRef.current);
  clearInterval(liveTimer.current);
  setScanId(null); setScan(null); setThoughts([]); setFindings([]);
  setScore(null); setSummary(''); setRecs([]);
  setMsgs([]); setTechLogs([]);
  setLiveThought(''); setLiveIdx(-1); setProgress(0);
  setTarget(''); setSessionCookie(''); setShowCookie(false);
  setAgentLiveThought(null); setLiveThoughts([]); setShowAllReasoning(false);
  setBrowserFrame(null);
  setLiveThoughtOpen(false);
  prevLen.current = 0; reacted.current = new Set();
}

  // Load history scan
async function loadHistoryScan(s) {
  clearInterval(pollRef.current);
  clearInterval(liveTimer.current);

  // If we only have metadata, fetch the full scan first
  let fullScan = s;
  if (!s.thoughts?.length && s.scanId) {
    try {
      const isGithub = s.scanType === 'github';
      const fn       = isGithub ? 'getGithubScan' : 'getAgentScan';
      const realId   = s.scanId.replace('github::', '');
      const r        = await SecureAPI.callFunction(fn, { scanId: realId }, 'GET');
      if (r?.scan) fullScan = { ...s, ...r.scan };
      if (r?.findings?.length) fullScan.findings   = r.findings;
      if (r?.thoughts?.length) fullScan.thoughts    = r.thoughts;
    } catch (e) {
      console.warn('Could not fetch full scan history:', e);
    }
  }

  setScan({ ...fullScan, status: fullScan.status || 'done' });
  setFindings(Array.isArray(fullScan.findings) ? fullScan.findings : []);
  setThoughts(fullScan.thoughts || []);
  setScore(fullScan.score ?? null);
  setSummary(fullScan.summary || '');
  setRecs(Array.isArray(fullScan.recommendations) ? fullScan.recommendations : []);
  setMsgs([]); setTechLogs([]);
  setLiveThought(''); setLiveIdx(-1);
  setBrowserFrame(null);
  setProgress(100);
  prevLen.current    = 0;
  reacted.current    = new Set();

  const restoredId = fullScan.scanType === 'github'
    ? `github::${fullScan.scanId}`
    : fullScan.scanId || null;
  setScanId(restoredId);

  if (fullScan.scannedAt) {
    setScanDate(new Date(fullScan.scannedAt).toLocaleString('en-US', {
      year:'numeric', month:'long', day:'numeric',
      hour:'2-digit', minute:'2-digit',
    }));
  }
}
  // PDF Export
async function exportPdf() {
  setPdfBusy(true);
  try {
    const J = await new Promise(res => {
      if (window.jspdf) return res(window.jspdf.jsPDF);
      const s = document.createElement('script');
      s.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
      s.onload = () => res(window.jspdf.jsPDF);
      document.head.appendChild(s);
    });

    const doc = new J({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const W = 210, M = 22, CW = W - M * 2;
    let y = 28;

    const newPage = () => { doc.addPage(); y = 28; };
    const ck = (need = 16) => { if (y + need > 270) newPage(); };
    const gap = (n = 5) => { y += n; };

    // ── Typography helpers ────────────────────────────────────
    const title = (text) => {
      ck(14);
      doc.setFont('times', 'bold');
      doc.setFontSize(22);
      doc.setTextColor(10, 10, 10);
      doc.text(text, M, y);
      y += 10;
    };

    const subtitle = (text) => {
      doc.setFont('times', 'normal');
      doc.setFontSize(11);
      doc.setTextColor(100, 100, 100);
      doc.text(text, M, y);
      y += 7;
    };

    const sectionHead = (text) => {
      ck(14);
      gap(6);
      doc.setFont('times', 'bold');
      doc.setFontSize(13);
      doc.setTextColor(10, 10, 10);
      doc.text(text, M, y);
      y += 2;
      doc.setDrawColor(200, 200, 200);
      doc.setLineWidth(0.3);
      doc.line(M, y, W - M, y);
      y += 5;
    };

    const body = (text, color = [50, 50, 50], indent = 0) => {
      ck(10);
      doc.setFont('times', 'normal');
      doc.setFontSize(11);
      doc.setTextColor(...color);
      const lines = doc.splitTextToSize(text, CW - indent);
      lines.forEach(line => {
        ck(6);
        doc.text(line, M + indent, y);
        y += 5.5;
      });
    };

    const bodyBold = (label, rest, indent = 0) => {
      ck(8);
      doc.setFont('times', 'bold');
      doc.setFontSize(11);
      doc.setTextColor(10, 10, 10);
      const labelW = doc.getTextWidth(label + ' ');
      doc.text(label, M + indent, y);
      doc.setFont('times', 'normal');
      doc.setTextColor(50, 50, 50);
      const restLines = doc.splitTextToSize(rest, CW - indent - labelW);
      doc.text(restLines[0] || '', M + indent + labelW, y);
      y += 5.5;
      if (restLines.length > 1) {
        restLines.slice(1).forEach(l => {
          ck(6);
          doc.text(l, M + indent, y);
          y += 5.5;
        });
      }
    };

    const rule = () => {
      gap(3);
      doc.setDrawColor(220, 220, 220);
      doc.setLineWidth(0.2);
      doc.line(M, y, W - M, y);
      gap(4);
    };

    // ── Grade helper ──────────────────────────────────────────
    const grade = score >= 80 ? 'A' : score >= 65 ? 'B' : score >= 50 ? 'C' : score >= 35 ? 'D' : 'F';
    const gradeLabel = score >= 70 ? 'Low Risk' : score >= 50 ? 'Moderate Risk' : score >= 30 ? 'High Risk' : 'Critical Risk';
    const hostname = (scan?.target || '').replace(/https?:\/\//, '').replace(/\/$/, '');

    // ── HEADER ────────────────────────────────────────────────
    title('Security Assessment Report');
    subtitle(`${hostname}   ·   ${scanDate}   ·   Overall Grade: ${grade} (${gradeLabel})`);
    rule();

    // ── EXECUTIVE SUMMARY ─────────────────────────────────────
    sectionHead('Executive Summary');

    const crit = af.filter(f => f.severity === 'CRITICAL').length;
    const high = af.filter(f => f.severity === 'HIGH').length;
    const med  = af.filter(f => f.severity === 'MEDIUM').length;
    const low  = af.filter(f => f.severity === 'LOW').length;

    if (summary) {
      body(summary);
      gap(3);
    }

    if (af.length === 0) {
      body('No security vulnerabilities were identified during this assessment. The application appears to follow security best practices. Continued monitoring and regular assessments are recommended as the codebase evolves.');
    } else {
      const sevText = [
        crit > 0 ? `${crit} critical` : '',
        high > 0 ? `${high} high` : '',
        med  > 0 ? `${med} medium` : '',
        low  > 0 ? `${low} low` : '',
      ].filter(Boolean).join(', ');

      body(`This assessment identified ${af.length} security issue${af.length !== 1 ? 's' : ''} across the application: ${sevText}. ${
        crit > 0
          ? 'Critical vulnerabilities were found that require immediate attention before this application handles any user data or goes live.'
          : high > 0
          ? 'High severity issues were identified that present meaningful risk and should be addressed promptly.'
          : 'The issues found are moderate in severity and should be remediated in the near term.'
      }`);
    }

    // ── WHAT COULD HAPPEN ─────────────────────────────────────
    if (af.length > 0) {
      sectionHead('What an Attacker Could Do');

      // Build a natural language attack narrative from the findings
      const critFindings  = af.filter(f => f.severity === 'CRITICAL');
      const highFindings  = af.filter(f => f.severity === 'HIGH');
      const topFindings   = [...critFindings, ...highFindings].slice(0, 4);

      if (topFindings.length > 0) {
        body('Based on the vulnerabilities found, a malicious actor with basic technical knowledge could potentially:');
        gap(2);
        topFindings.forEach(f => {
          const impact = f.blastRadius?.summary
            ? f.blastRadius.summary.split(/[,·]/)[0].trim()
            : f.description
            ? f.description.slice(0, 120)
            : 'compromise application data or functionality';
          ck(8);
          doc.setFont('times', 'normal');
          doc.setFontSize(11);
          doc.setTextColor(50, 50, 50);
          doc.text('—', M + 3, y);
          const lines = doc.splitTextToSize(
            `${f.title.toLowerCase().replace(/\.$/, '')}${impact ? ', potentially exposing ' + impact : ''}`,
            CW - 8
          );
          lines.forEach((l, li) => {
            ck(6);
            doc.text(l, M + 9, li === 0 ? y : y);
            y += 5.5;
          });
        });

        gap(2);

        if (af.some(f => f.blastRadius?.totalRows > 0)) {
          const totalRows = af.reduce((s, f) => s + (f.blastRadius?.totalRows || 0), 0);
          body(`In total, up to ${totalRows.toLocaleString()} records could be accessible to an attacker if these vulnerabilities were exploited together.`);
        } else if (crit > 0 || high > 0) {
          body('If these vulnerabilities were chained together, an attacker could potentially gain unauthorized access to user data, bypass authentication, or disrupt the application entirely.');
        }
      } else {
        body('The issues found are lower severity and would require significant effort for an attacker to exploit meaningfully. However, they should still be addressed to maintain a strong security posture.');
      }
    }

    // ── FINDINGS ──────────────────────────────────────────────
    if (af.length > 0) {
      sectionHead(`Security Issues  (${af.length} total)`);

      af.forEach((f, idx) => {
        ck(20);

        const sevWord = f.severity === 'CRITICAL' ? 'Critical'
          : f.severity === 'HIGH'   ? 'High'
          : f.severity === 'MEDIUM' ? 'Medium'
          : f.severity === 'LOW'    ? 'Low' : 'Info';

        // Finding title line
        doc.setFont('times', 'bold');
        doc.setFontSize(11);
        doc.setTextColor(10, 10, 10);
        const numLabel = `${idx + 1}.  `;
        doc.text(numLabel, M, y);
        const titleLines = doc.splitTextToSize(f.title, CW - 8);
        doc.text(titleLines[0], M + doc.getTextWidth(numLabel), y);
        y += 5.5;
        if (titleLines.length > 1) {
          titleLines.slice(1).forEach(l => {
            doc.text(l, M + 8, y); y += 5.5;
          });
        }

        // Severity + KEV inline
        doc.setFont('times', 'italic');
        doc.setFontSize(10);
        doc.setTextColor(120, 120, 120);
        const kevNote = f.activelyExploited ? '  —  Actively exploited in the wild (CISA KEV)' : '';
        doc.text(`Severity: ${sevWord}${f.cvss ? '  ·  CVSS ' + f.cvss : ''}${kevNote}`, M + 8, y);
        y += 5.5;

        // Description
        if (f.description) {
          body(f.description, [60, 60, 60], 8);
        }

        // Remediation
        if (f.remediation) {
          gap(1);
          bodyBold('Fix:', f.remediation, 8);
        }

        gap(3);
      });
    }

    // ── RECOMMENDATIONS ───────────────────────────────────────
    if (recs.length > 0) {
      sectionHead('Recommendations');
      body('Based on the findings above, the following actions are recommended in order of priority:');
      gap(2);
      recs.forEach((r, i) => {
        ck(8);
        bodyBold(`${i + 1}.`, r, 0);
      });
    }

    // ── CLOSING NOTE ──────────────────────────────────────────
    sectionHead('Notes');
    body('This report was generated automatically by SharkBase, an AI-powered security assessment tool. All findings should be reviewed by a qualified developer or security professional before taking action. Some findings may require additional context to remediate correctly. This report does not constitute a guarantee of security and should be treated as one layer of a broader security programme.');

    // ── FOOTER on every page ─────────────────────────────────
    const pages = doc.internal.getNumberOfPages();
    for (let p = 1; p <= pages; p++) {
      doc.setPage(p);
      doc.setFont('times', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(160, 160, 160);
      doc.text('SharkBase Security Report  —  Confidential', M, 287);
      doc.text(`Page ${p} of ${pages}`, W - M, 287, { align: 'right' });
      doc.setDrawColor(220, 220, 220);
      doc.setLineWidth(0.2);
      doc.line(M, 283, W - M, 283);
    }

    doc.save(`security-report-${hostname.replace(/[/\s.:]/g, '-').slice(0, 40)}.pdf`);

  } catch (e) {
    console.error('PDF error:', e);
  }
  setPdfBusy(false);
}

  // Render a thought row (agent tab)

function renderThought(t, i, thoughts, isActive, findings) {
  if (!t) return null;

  if (t.type === 'thinking') {
    // Skip milestone narrative — it's the thinking entry that follows
    // tool_call → tool_result in a GitHub milestone triple.
    // It already shows in the LiveThoughtBanner, no need to repeat it here.
    const prev     = thoughts[i - 1];
    const prevPrev = thoughts[i - 2];
    if (prev?.type === 'tool_result' && prevPrev?.type === 'tool_call') return null;

    // Skip folder-flicker thoughts — they follow another thinking entry
    // and are already shown transiently in the LiveThoughtBanner.
    if (prev?.type === 'thinking') return null;

    const isLiveIdx = i === thoughts.length - 1 && isActive;
    return (
      <div key={t.ts||i} style={{
        padding:'7px 16px',
        display:'flex', alignItems:'flex-start', gap:8,
        borderBottom:'1px solid var(--bd)',
        background:'var(--bg1)',
      }}>
        <div style={{width:3, height:3, borderRadius:'50%', flexShrink:0, background:'var(--t3)', marginTop:7}}/>
        <div style={{
          fontFamily:'var(--mono)', fontSize:'12px', color:'var(--t3)',
          lineHeight:1.7, textAlign:'left', fontStyle:'italic',
          wordBreak:'break-word', whiteSpace:'pre-wrap', overflowWrap:'break-word',
        }}>
          {t.text}
          {isLiveIdx && (
            <span style={{
              display:'inline-block', width:5, height:'0.75em',
              background:'var(--t3)', marginLeft:2,
              verticalAlign:'middle', animation:'livePulse .75s infinite',
            }}/>
          )}
        </div>
      </div>
    );
  }

  if (t.type === 'tool_call') {
    const stageLabel = t.tool === 'sast_scan' && t.input?.stage ? t.input.stage : null;
    const icon       = TOOL_ICON[stageLabel || t.tool] || TOOL_ICON[t.tool] || '◇';
    const label      = TOOL_LABEL[stageLabel || t.tool] || TOOL_LABEL[t.tool] || t.tool;

    const callData = t.args || t.input || {};
    const argRaw   = callData.path || callData.hostname
      || (callData.url ? (() => {
          try { return new URL(callData.url).pathname.slice(0,32) || callData.url.slice(0,40); }
          catch { return callData.url?.slice(0,40) || ''; }
        })() : '')
      || (stageLabel ? '' : '');

    const nextT      = thoughts[i + 1];
    const result     = nextT?.type === 'tool_result' ? nextT : null;
    const resultText = result?.summary || result?.output || '';
    const warn       = resultText.includes('⚠') || result?.isWarning;
    const scanning   = !result && isActive;
    const resultColor = warn ? 'rgba(252,120,120,.75)' : result ? 'rgba(110,231,183,.6)' : 'var(--t3)';
    const normResult  = result ? { ...result, summary: resultText } : null;

    return (
      <ToolPair key={t.ts||i} icon={icon} label={label} arg={argRaw}
        result={normResult} resultColor={resultColor} scanning={scanning}
        animDelay={Math.min(i * .02, .4)} toolName={t.tool}
      />
    );
  }

  if (t.type === 'tool_result') {
    const prev = thoughts[i - 1];
    if (prev?.type === 'tool_call') return null;

    const resultText = t.summary || t.output || '';
    const warn = resultText.includes('⚠') || t.isWarning;
    return (
      <div key={t.ts||i} style={{
        padding:'4px 18px', fontFamily:'var(--mono)', fontSize:'.52rem',
        color: warn ? 'rgba(252,120,120,.7)' : 'var(--t3)', textAlign:'left',
        borderBottom:'1px solid rgba(255,255,255,.025)',
      }}>
        {resultText}
      </div>
    );
  }
if (t.type === 'approval_request' && !approvedTs.current) {
    const pend = t.pendingApproval || scan?.pendingApproval;
  return (
    <div key={t.ts||i} className="sb-appr">
      <div className="sb-appr-title">⚠ Approval Required</div>
      <div className="sb-appr-desc">
        {pend?.description || t.description || 'The agent wants to run an active test. Approve to continue.'}
      </div>
      {pend?.tool && (
        <div style={{fontFamily:'var(--mono)',fontSize:'11px',color:'var(--t3)',marginBottom:10}}>
          Tool: <strong>{pend.tool}</strong>
          {pend?.arg ? ` · ${String(pend.arg).slice(0,60)}` : ''}
        </div>
      )}
      <div className="sb-appr-btns">
        <button className="sb-appr-yes" onClick={() => approve(true)} disabled={approving}>
          {approving ? <Spin s={10} c="#fff"/> : 'Approve'}
        </button>
        <button className="sb-appr-all" onClick={() => approve(true, true)} disabled={approving}>
          Approve all
        </button>
        <button className="sb-appr-skip" onClick={() => approve(false)} disabled={approving}>
          Skip
        </button>
      </div>
    </div>
  );
}
  return null;
}


  const dotCol = isRunning ? 'var(--t1)'  : isGate ? 'var(--yellow)' : isDone ? 'rgba(6, 6, 6, 0.54)' : isStopped ? 'rgba(252, 99, 99, 0.99)' : 'rgba(255,255,255,.12)';
  const statusLbl = {running:'Scanning', awaiting_approval:'Your Decision', done:'Complete', stopped:'Stopped', error:'Error'}[scan?.status] || 'Ready';
  const thumbClass = tab === 'agent' ? 't1' : tab === 'deepdive' ? 't2' : 't3';

  // ── Render ────────────────────────────────────────────────────
  return (
    <>
      <style>{CSS}</style>
<div className="sb-shell">
  <div className="sb-grid"/>
  <div className="sb-orb sb-orb-1"/>
  <div className="sb-orb sb-orb-2"/>

        {/* NAV */}
<nav className="sb-nav">

  {/* Left — hamburger on mobile, logo+brand on desktop */}
<div style={{display:'flex', alignItems:'center', gap:6, paddingLeft:4, width:120, flexShrink:0}}>
  {scan && (
    <button
      className="sb-mob-ham"
      onClick={() => { setMobSidebarOpen(true); setMobSidebarClosing(false); }}
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <path d="M3 12h18M3 6h18M3 18h18"/>
      </svg>
    </button>
  )}
  {/* Desktop always, mobile only when no scan */}
  <div className={scan ? 'sb-logo sb-hide-mobile' : 'sb-logo'}>
    <img src="https://www.svgrepo.com/show/323217/shark-fin.svg" alt=""/>
  </div>
  <div className={scan ? 'sb-brand sb-hide-mobile' : 'sb-brand'}>SharkBase</div>
</div>
  {/* Center — truly centered because left and right are same width */}
<div style={{flex:1, display:'flex', justifyContent:'center', alignItems:'center', minWidth:0, overflow:'hidden'}}>
      {scan ? (
      <div style={{animation:'blurAppear .3s ease both'}}>
        <div className="sb-toggle">
          <div className={`sb-thumb ${thumbClass}`}/>
<button className={`sb-tab${tab==='agent'?' on':''}`} onClick={() => { setTab('agent'); setAllOpen(false); }}>Agent</button>
          <button className={`sb-tab${tab==='deepdive'?' on':''}`} onClick={() => setTab('deepdive')}>Deep Dive</button>
          <button className={`sb-tab${tab==='report'?' on':''}`} onClick={() => setTab('report')}>Report</button>
        </div>
      </div>
) : (
  <span className="sb-nav-tagline">
    SharkBase finds issues in your vibe-coded app before hackers do
  </span>
)}
  </div>

  <div style={{display:'flex', alignItems:'center', gap:8, paddingRight:8, flexShrink:0, justifyContent:'flex-end'}}>

{/* Mobile only — logo+brand center-right when scanning, hidden when not */}
{scan && (
  <div className="sb-show-mobile" style={{display:'none', alignItems:'center', gap:5}}>
    <div className="sb-logo" style={{width:22, height:22}}>
      <img src="https://www.svgrepo.com/show/323217/shark-fin.svg" alt=""/>
    </div>
    <div className="sb-brand" style={{fontSize:'16px'}}>SharkBase</div>
  </div>
)}

    {/* Desktop only — sign in/up or status */}
    <div className="sb-hide-mobile" style={{display:'flex', alignItems:'center', gap:8}}>
      {scan ? (
        <div className="sb-npill">
          <div className="sb-sdot" style={{
            background:dotCol,
            boxShadow: isRunning ? `0 0 6px ${dotCol}` : 'none',
            animation: isRunning ? 'pulse 1.4s infinite' : 'none',
          }}/>
          {statusLbl}
        </div>
      ) : (
        <div style={{display:'flex', alignItems:'center', gap:6}}>
          <button style={{
            height:30, padding:'0 14px', borderRadius:999,
            background:'transparent', border:'1px solid var(--bd)',
            fontFamily:'var(--mono)', fontSize:'12px', fontWeight:500,
            color:'var(--t2)', cursor:'pointer', transition:'all .15s',
            whiteSpace:'nowrap', flexShrink:0, letterSpacing:'.01em',
          }}
            onMouseEnter={e => { e.currentTarget.style.background='var(--bg1)'; e.currentTarget.style.color='var(--t1)'; e.currentTarget.style.borderColor='var(--bdh)'; }}
            onMouseLeave={e => { e.currentTarget.style.background='transparent'; e.currentTarget.style.color='var(--t2)'; e.currentTarget.style.borderColor='var(--bd)'; }}
          >
            Sign in
          </button>
          <button style={{
            height:30, padding:'0 14px', borderRadius:999,
            background:'var(--t1)', border:'none',
            fontFamily:'var(--mono)', fontSize:'12px', fontWeight:700,
            color:'#fff', cursor:'pointer', transition:'all .15s',
            whiteSpace:'nowrap', flexShrink:0,
            boxShadow:'0 1px 4px rgba(0,0,0,.14)',
            letterSpacing:'.01em',
          }}
            onMouseEnter={e => e.currentTarget.style.background='#222'}
            onMouseLeave={e => e.currentTarget.style.background='var(--t1)'}
          >
            Sign up
          </button>
        </div>
      )}
    </div>
{/* Mobile only — sign in/up when no scan */}
{!scan && (
  <div className="sb-show-mobile" style={{display:'none', alignItems:'center', gap:6}}>
    <button style={{
      height:28, padding:'0 11px', borderRadius:999,
      background:'transparent', border:'1px solid var(--bd)',
      fontFamily:'var(--mono)', fontSize:'12px', fontWeight:500,
      color:'var(--t2)', cursor:'pointer', whiteSpace:'nowrap', flexShrink:0,
    }}>
      Sign in
    </button>
    <button style={{
      height:28, padding:'0 11px', borderRadius:999,
      background:'var(--t1)', border:'none',
      fontFamily:'var(--mono)', fontSize:'12px', fontWeight:700,
      color:'#fff', cursor:'pointer', whiteSpace:'nowrap', flexShrink:0,
      boxShadow:'0 1px 4px rgba(0,0,0,.14)',
    }}>
      Sign up
    </button>
  </div>
)}
  </div>
</nav>
{/* Recent scans drawer */}
{drawerOpen && (
  <>
    <div className="sb-drawer-backdrop" onClick={closeDrawer}/>
    <div className={`sb-recent-drawer${drawerClosing?' closing':''}`}>
      <div className="sb-drawer-hdr">
        <span className="sb-drawer-title">Recent Scans</span>
        <button className="sb-drawer-close" onClick={closeDrawer}>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <path d="M18 6 6 18M6 6l12 12"/>
          </svg>
        </button>
      </div>
      <div className="sb-drawer-body">
        {recent.length === 0 ? (
          <div style={{fontFamily:'var(--mono)', fontSize:'.52rem', color:'var(--t3)', padding:'20px 0', textAlign:'center'}}>
            No scans yet
          </div>
        ) : recent.map(s => {
          const statusColor = {done:'var(--green)', stopped:'var(--red)', error:'var(--red)'}[s.status] || 'var(--t3)';
          return (
            <div key={s.scanId} className="sb-hcard" onClick={() => { loadHistoryScan(s); closeDrawer(); }}>
              <div>
                <div className="sb-hurl">{(s.target||'').replace(/https?:\/\//,'')}</div>
                <div className="sb-hmeta">
                  {s.status}{s.score != null ? ` · Score ${s.score}` : ''}
                  {s.scannedAt ? ` · ${new Date(s.scannedAt).toLocaleDateString()}` : ''}
                </div>
              </div>
              <span style={{fontFamily:'var(--mono)', fontSize:'.44rem', fontWeight:600, color:statusColor, padding:'2px 8px', borderRadius:999}}>
                {s.status}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  </>
)}
{/* ── Mobile sidebar ── */}
{mobSidebarOpen && (
  <>
    <div
      className="sb-mob-sidebar-backdrop"
      onClick={() => {
        setMobSidebarClosing(true);
        setTimeout(() => { setMobSidebarOpen(false); setMobSidebarClosing(false); }, 200);
      }}
    />
    <div className={`sb-mob-sidebar${mobSidebarClosing ? ' closing' : ''}`}>

      {/* Header */}
      <div className="sb-mob-sidebar-hdr">
        <div style={{display:'flex', alignItems:'center', gap:7}}>
          <div className="sb-logo" style={{width:20, height:20}}>
            <img src="https://www.svgrepo.com/show/323217/shark-fin.svg" alt=""/>
          </div>
          <span style={{fontFamily:'var(--serif)', fontSize:'16px', fontWeight:700, color:'var(--t1)'}}>SharkBase</span>
        </div>
        <button
          onClick={() => {
            setMobSidebarClosing(true);
            setTimeout(() => { setMobSidebarOpen(false); setMobSidebarClosing(false); }, 200);
          }}
          style={{
            width:28, height:28, borderRadius:'50%',
            background:'#000', border:'1px solid var(--bd)',
            display:'flex', alignItems:'center', justifyContent:'center',
            cursor:'pointer', color:'#fff',
          }}
        >
   <svg width="15" height="15" viewBox="0 0 15 15" fill="none"
    xmlns="http://www.w3.org/2000/svg"><path d="M8.81809 4.18179C8.99383 4.35753 8.99383 4.64245 8.81809 4.81819L6.13629 7.49999L8.81809 10.1818C8.99383 10.3575 8.99383 10.6424 8.81809 10.8182C8.64236 10.9939 8.35743 10.9939 8.1817 10.8182L5.1817 7.81819C5.09731 7.73379 5.0499 7.61933 5.0499 7.49999C5.0499 7.38064 5.09731 7.26618 5.1817 7.18179L8.1817 4.18179C8.35743 4.00605 8.64236 4.00605 8.81809 4.18179Z" fill="currentColor" fill-rule="evenodd" clip-rule="evenodd"></path></svg>
        </button>
      </div>

{/* Scrollable body */}
<div style={{flex:1, overflowY:'auto', padding:'16px 14px', display:'flex', flexDirection:'column', gap:12}}>

  {/* Scan stats */}
  {scan && (
    <div style={{
      borderRadius:10, border:'1px solid var(--bd)',
      background:'#fff', overflow:'hidden',
    }}>
      <div style={{
        padding:'8px 12px', borderBottom:'1px solid var(--bd)',
        fontFamily:'var(--mono)', fontSize:'10px', fontWeight:600,
        letterSpacing:'.1em', textTransform:'uppercase', color:'#000',
        textAlign:'left',
      }}>
       Usage
      </div>
      {(() => {
        const rawSec    = finalElapsed.current ?? elapsedSec;
        const mins      = Math.floor(rawSec / 60);
        const secs      = rawSec % 60;
        const timeLabel = mins > 0
          ? `${mins}m ${String(secs).padStart(2,'0')}s`
          : `${secs}s`;
        const toolCount = thoughts.filter(t => t.type === 'tool_call').length;
        return (
          <div style={{padding:'10px 12px', display:'flex', flexDirection:'column', gap:10}}>
            {[
              { label:'Scan Time',    value: timeLabel, live: isRunning, done: isDone || isStopped },
              { label:'Tool Calls', value: toolCount  },
              { label:'Findings',   value: af.length  },
            ].map(({ label, value, live, done }) => (
              <div key={label} style={{
                display:'flex', alignItems:'center', justifyContent:'space-between',
                textAlign:'left',
              }}>
                <span style={{fontFamily:'var(--mono)', fontSize:'12px', color:'var(--t3)'}}>
                  {label}
                </span>
                <span style={{
                  fontFamily:'var(--mono)', fontSize:'12px', fontWeight:700,
                  color: live ? '#20a9ff' : 'var(--t1)',
                  display:'flex', alignItems:'center', gap:5,
                }}>
                  {live && (
                    <span style={{
                      width:5, height:5, borderRadius:'50%',
                      background:'#20a9ff', display:'inline-block',
                      animation:'pulse 1.2s infinite',
                    }}/>
                  )}
                  {value}
                  {done && (
                    <span style={{
                      fontFamily:'var(--mono)', fontSize:'10px',
                      color:'var(--t3)', fontWeight:400,
                    }}>
                      · done
                    </span>
                  )}
                </span>
              </div>
            ))}
          </div>
        );
      })()}
    </div>
  )}

  {/* Scan progress — collapsible container */}
  {scan && (() => {
    return (
      <div style={{
        borderRadius:20, border:'1px solid var(--bd)',
        background:'#fff', overflow:'hidden',
      }}>
        {/* Header — tap to expand */}
        <div
          onClick={() => setMobProgOpen(o => !o)}
          style={{
            padding:'8px 12px',
            display:'flex', alignItems:'center', justifyContent:'space-between',
            cursor:'pointer', transition:'background .12s',
            borderBottom: mobProgOpen ? '1px solid var(--bd)' : 'none',
          }}
        >
          <span style={{
            fontFamily:'var(--mono)', fontSize:'10px', fontWeight:600,
            letterSpacing:'.1em', textTransform:'uppercase', color:'#000',
            textAlign:'left',
          }}>
            Scan Progress
          </span>
          <div style={{
            display:'flex', alignItems:'center', gap:6,
          }}>
            <div style={{
              width:18, height:18, borderRadius:'50%',
              background:'var(--bg2)', border:'1px solid var(--bd)',
              display:'flex', alignItems:'center', justifyContent:'center',
              transition:'transform .2s',
              transform: mobProgOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            }}>
              <svg width="8" height="8" viewBox="0 0 24 24" fill="none"
                stroke="var(--t2)" strokeWidth="2.5"
                strokeLinecap="round" strokeLinejoin="round">
                <path d="M6 9l6 6 6-6"/>
              </svg>
            </div>
          </div>
        </div>

{mobProgOpen && (
          <div style={{animation:'fadeUp .18s ease both'}}>
            <StepperProgress
              thoughts={thoughts}
              isRunning={isRunning}
              scanId={scanId}
            />
          </div>
        )}
      </div>
    );
  })()}

{/* Issues Found */}
{scan && af.length > 0 && (
  <div style={{
    borderRadius:10, border:'1px solid var(--bd)',
    background:'#fff', overflow:'hidden',
  }}>
    <div
      onClick={() => setMobIssuesOpen(o => !o)}
      style={{
        padding:'8px 12px',
        display:'flex', alignItems:'center', justifyContent:'space-between',
        cursor:'pointer', transition:'background .12s',
        borderBottom: mobIssuesOpen ? '1px solid var(--bd)' : 'none',
      }}
      onMouseEnter={e => e.currentTarget.style.background='var(--bg1)'}
      onMouseLeave={e => e.currentTarget.style.background='transparent'}
    >
      <div style={{display:'flex', alignItems:'center', gap:8}}>
        <span style={{
          fontFamily:'var(--mono)', fontSize:'10px', fontWeight:600,
          letterSpacing:'.1em', textTransform:'uppercase', color:'#000',
        }}>
          Live Findings
        </span>
        <span style={{
          fontFamily:'var(--mono)', fontSize:'10px', fontWeight:700,
          color:'#ef4444', background:'#fff0f0',
          padding:'1px 7px', borderRadius:999,
          border:'1px solid rgba(220,38,38,.2)',
        }}>
          {af.length}
        </span>
      </div>
      <div style={{
        width:18, height:18, borderRadius:'50%',
        background:'var(--bg2)', border:'1px solid var(--bd)',
        display:'flex', alignItems:'center', justifyContent:'center',
        transition:'transform .2s',
        transform: mobIssuesOpen ? 'rotate(180deg)' : 'rotate(0deg)',
      }}>
        <svg width="8" height="8" viewBox="0 0 24 24" fill="none"
          stroke="var(--t2)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M6 9l6 6 6-6"/>
        </svg>
      </div>
    </div>
    {mobIssuesOpen && (
      <div style={{animation:'fadeUp .18s ease both'}}>
        {af.map((f, i) => {
          const color =
            f.severity === 'CRITICAL' ? '#ef4444' :
            f.severity === 'HIGH'     ? '#f97316' :
            f.severity === 'MEDIUM'   ? '#e6ac00' :
            f.severity === 'LOW'      ? '#505050' : '#3b82f6';
          const bg =
            f.severity === 'CRITICAL' ? '#ffdbdb' :
            f.severity === 'HIGH'     ? '#ffe7db' :
            f.severity === 'MEDIUM'   ? '#fffbd2' :
            f.severity === 'LOW'      ? '#efefef' : '#d3e6ff';
          return (
            <div key={i} style={{
              padding:'9px 12px',
              borderTop: i === 0 ? 'none' : '1px solid rgba(0,0,0,.05)',
              display:'flex', alignItems:'flex-start', gap:8,
              animation:`fadeUp .15s ease ${i * 0.04}s both`,
            }}>
              <span style={{
                fontFamily:'var(--mono)', fontSize:'10px', fontWeight:700,
                color, background:bg,
                padding:'2px 7px', borderRadius:999, flexShrink:0, marginTop:1,
              }}>
                {f.severity}
              </span>
              <span style={{
                fontFamily:'var(--mono)', fontSize:'11px', color:'var(--t2)',
                lineHeight:1.5, textAlign:'left', wordBreak:'break-word',
              }}>
                {f.title}
              </span>
            </div>
          );
        })}
      </div>
    )}
  </div>
)}
</div>
  {/* Connected */}
  <div style={{
    borderRadius:10, border:'1px solid var(--bd)',
    background:'#fff', overflow:'hidden',
  }}>
    <div style={{
      padding:'8px 12px', borderBottom:'1px solid var(--bd)',
      fontFamily:'var(--mono)', fontSize:'10px', fontWeight:600,
      letterSpacing:'.1em', textTransform:'uppercase', color:'var(--t3)',
      textAlign:'left',
    }}>
      Connected
    </div>
    <div style={{padding:'10px 12px', display:'flex', flexDirection:'column', gap:8}}>
      {['myapp.lovable.app', 'github.com/user/repo'].map(label => (
        <div key={label} style={{
          display:'flex', alignItems:'center', justifyContent:'space-between', gap:8,
        }}>
          <span style={{
            fontFamily:'var(--mono)', fontSize:'11px', color:'var(--t2)',
            overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
            flex:1, textAlign:'left',
          }}>
            {label}
          </span>
          <button style={{
            flexShrink:0, padding:'3px 10px', borderRadius:999,
            background:'var(--t1)', border:'none',
            fontFamily:'var(--mono)', fontSize:'10px', fontWeight:700,
            color:'#fff', cursor:'pointer',
          }}>
            Connected
          </button>
        </div>
      ))}
    </div>
  </div>
      {/* Account footer — pinned bottom */}
      <div style={{
        flexShrink:0, borderTop:'1px solid var(--bd)',
        padding:'12px 14px', background:'#fff',textAlign:'left'
      }}>
        <div style={{
          display:'flex', alignItems:'center', gap:8,
          padding:'8px 10px', borderRadius:10,
          background:'var(--bg1)', border:'1px solid var(--bd)',
        }}>
          {/* Avatar */}
          <div style={{
            width:30, height:30, borderRadius:'50%', flexShrink:0,
            background:'#dc2626',
            display:'flex', alignItems:'center', justifyContent:'center',
            fontFamily:'var(--mono)', fontSize:'11px', fontWeight:700, color:'#fff',
          }}>
            JA
          </div>
          {/* Name + plan */}
          <div style={{flex:1, minWidth:0}}>
<div style={{
  fontFamily:'var(--sans)', fontSize:'13px', fontWeight:600,
  color:'var(--t1)', overflow:'hidden', textOverflow:'ellipsis',
  whiteSpace:'nowrap', textAlign:'left',
}}>
  james
</div>
            <span style={{
              fontFamily:'var(--mono)', fontSize:'10px', fontWeight:700,
    color: '#113ceaff', background: '#eef0ffff',
              padding:'1px 7px', borderRadius:999, display:'inline-block', marginTop:2,textAlign:'left'
            }}>
              Pay as you go
            </span>
          </div>
          {/* Upgrade */}
          <button style={{
            flexShrink:0, padding:'5px 11px', borderRadius:999,
            background:'var(--t1)', border:'none',
            fontFamily:'var(--mono)', fontSize:'10px', fontWeight:700,
            color:'#fff', cursor:'pointer', whiteSpace:'nowrap',
          }}>
            Upgrade
          </button>
        </div>
      </div>

    </div>
  </>
)}
        {/* BODY */}
        <div className="sb-body">

  {/* ── Left usage panel — only when scan exists ── */}
  {scan && tab === 'agent' && (
    <div className="sb-left-panel">
      <div className="sb-left-panel-hdr">
        <span className="sb-left-panel-title">Usage</span>
      </div>
<div className="sb-left-panel-body">

<div className="sb-left-panel-body">
  <div className="sb-usage-block">
    {/* Elapsed time — no cap, freezes when done */}
    {(() => {
      const rawSec = finalElapsed.current ?? elapsedSec;
      const mins   = Math.floor(rawSec / 60);
      const secs   = rawSec % 60;
      const label  = mins > 0
        ? `${mins}m ${String(secs).padStart(2,'0')}s`
        : `${secs}s`;
      return (
        <div className="sb-usage-row">
          <div className="sb-usage-label">
            <span className="sb-usage-name">Scan Time</span>
            <span className="sb-usage-val" style={{
              color: isRunning ? '#20a9ff' : 'var(--t3)',
              fontWeight: isRunning ? 700 : 400,
              display:'flex', alignItems:'center', gap:4,
            }}>
              {isRunning && <span style={{width:5,height:5,borderRadius:'50%',background:'#20a9ff',display:'inline-block',animation:'pulse 1.2s infinite'}}/>}
              {label}
              {(isDone || isStopped) && <span style={{color:'var(--t3)', fontWeight:400}}>· done</span>}
            </span>
          </div>
          <div className="sb-usage-bar">
            <div style={{
              height:'100%', borderRadius:2, width:'100%',
              background: isRunning ? '#20a9ff' : isDone ? '#20a9ff' : '#20a9ff',
              transition:'background .4s ease',
              animation: isRunning ? 'progBreath 2s ease-in-out infinite' : 'none',
            }}/>
          </div>
        </div>
      );
    })()}

    {/* Tool calls + Findings */}
    {[
      { name:'Tool Calls', used:thoughts.filter(t=>t.type==='tool_call').length, max:20 },
      { name:'Findings',   used:af.length, max:100 },
    ].map(({ name, used, max }) => {
      const pct = Math.min(100, Math.round((used / max) * 100));
      const fillColor = pct >= 90 ? 'var(--red)' : pct >= 70 ? 'var(--orange)' : 'var(--t1)';
      return (
        <div key={name} className="sb-usage-row">
          <div className="sb-usage-label">
            <span className="sb-usage-name">{name}</span>
            <span className="sb-usage-val">{used}</span>
          </div>
          <div className="sb-usage-bar">
            <div style={{
              height:'100%', borderRadius:2,
              width:`${pct}%`,
              background: fillColor,
              transition:'width .4s ease',
            }}/>
          </div>
        </div>
      );
    })}
  </div>
</div>

  {/* Recent — only when not scanning */}
  {!isActive && !isDone && !isStopped && recent.length > 0 && (
    <div style={{padding:'12px 14px', borderTop:'1px solid var(--bd)'}}>
      <div style={{
        fontFamily:'var(--mono)', fontSize:'.42rem', color:'var(--t3)',
        letterSpacing:'.1em', textTransform:'uppercase', marginBottom:10,
      }}>
        Recent
      </div>
      {recent.slice(0, 5).map(s => {
        const statusColor = {done:'var(--green)', stopped:'var(--red)', error:'var(--red)'}[s.status] || 'var(--t3)';
        return (
          <div key={s.scanId} className="sb-hcard" style={{marginBottom:4}} onClick={() => loadHistoryScan(s)}>
            <div style={{minWidth:0, flex:1}}>
              <div className="sb-hurl" style={{overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:120}}>
                {(s.target||'').replace(/https?:\/\//,'')}
              </div>
              <div className="sb-hmeta">{s.score != null ? `Score ${s.score}` : s.status}</div>
            </div>
            <span style={{fontFamily:'var(--mono)', fontSize:'.4rem', fontWeight:600, color:statusColor, flexShrink:0}}>
              {s.status === 'done' ? '✓' : s.status === 'error' ? '✕' : '•'}
            </span>
          </div>
        );
      })}
    </div>
  )}

</div>
{/* ── Connected block — sits above account footer ── */}
<div style={{
  flexShrink:0, borderTop:'1px solid var(--bd)',
  padding:'14px 14px',
  background:'#fff',
}}>
  <div style={{
    fontFamily:'var(--mono)', fontSize:'11px', fontWeight:600,
    letterSpacing:'.1em', textTransform:'uppercase',
    color:'var(--t3)', marginBottom:10,textAlign:'left',
  }}>
    Your platforms
  </div>
  {[
    { label:'myapp.lovable.app' },
    { label:'github.com/user/repo' },
  ].map(({ label }) => (
    <div key={label} style={{
      display:'flex', alignItems:'center', justifyContent:'space-between',
      marginBottom:8,
    }}>
      <span style={{
        fontFamily:'var(--mono)', fontSize:'12px', color:'var(--t2)',
        overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
        maxWidth:120, flexShrink:1,
      }}>
        {label}
      </span>
      <button style={{
        flexShrink:0, padding:'3px 10px', borderRadius:999,
        background:'var(--t1)', border:'none',
        fontFamily:'var(--mono)', fontSize:'10px', fontWeight:700,
        color:'#fff', cursor:'pointer', transition:'opacity .15s', marginLeft:8,
      }}
      >
        Connected
      </button>
    </div>
  ))}
</div>
{/* ── Account footer — pinned to bottom of left panel ── */}
<div style={{
  flexShrink: 0,
  borderTop: '1px solid var(--bd)',
  padding: '10px 12px',
  background: '#fff',
  borderRadius: '0 0 14px 14px',
}}>
<div style={{
  display: 'flex', alignItems: 'center', gap: 8,
  padding: '8px 10px', borderRadius: 10,
  flexWrap: 'nowrap',
    cursor: 'pointer', transition: 'background .15s',
    position: 'relative',
  }}
    onMouseEnter={e => e.currentTarget.style.background='var(--bg1)'}
    onMouseLeave={e => e.currentTarget.style.background='transparent'}
    onClick={() => setOpen(o => !o)}
  >
    {/* Avatar */}
    <div style={{
      width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
      background: '#dc2626',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: 'var(--mono)', fontSize: '11px', fontWeight: 700, color: '#fff',
    }}>
      JA
    </div>

    {/* Name + plan */}
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{
        fontFamily: 'var(--sans)', fontSize: '13px', fontWeight: 500,
        color: 'var(--t1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign:'left',
      }}>
        james
      </div>
<div style={{
  fontFamily: 'var(--mono)', fontSize: '10px', marginTop: 2,
  display: 'flex', alignItems: 'center',
}}>
  <span style={{
    fontFamily: 'var(--mono)', fontSize: '10px', fontWeight: 700,
    color: '#113ceaff', background: '#eef0ffff',
    padding: '2px 8px', borderRadius: 999,
    whiteSpace: 'nowrap',
  }}>
    Pay as you go
  </span>
</div>
    </div>
    {/* Dropdown chevron */}
    <div style={{
      flexShrink: 0, transition: 'transform .2s',
      transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
    }}>
      <svg width="10" height="10" viewBox="0 0 24 24" fill="none"
        stroke="var(--t3)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M6 9l6 6 6-6"/>
      </svg>
    </div>
  </div>

  {/* Popup menu */}
  {open && (
    <div style={{
      position: 'absolute', bottom: 70, left: 12, right: 12, zIndex: 200,
      background: '#fff', border: '1px solid var(--bd)', borderRadius: 12,
      boxShadow: '0 8px 32px rgba(0,0,0,.12), 0 2px 8px rgba(0,0,0,.06)',
      overflow: 'hidden',
      animation: 'blurAppear .18s cubic-bezier(.22,1,.36,1) both',
    }}>
  {[
  { label:'Profile' },
  { label:'Settings' },
  { label:'Billing' },
  { label:'API Keys' },
  { label:'Sign out', danger:true },
].map(({ label, danger }, i, arr) => (
  <button key={label}
    style={{
      width:'100%', padding:'9px 16px',
      background:'transparent', border:'none',
      borderBottom: i < arr.length - 1 ? '1px solid var(--bd)' : 'none',
      fontFamily:'var(--mono)', fontSize:'12px', fontWeight:500,
      color: danger ? '#dc2626' : 'var(--t1)',
      cursor:'pointer', transition:'background .12s', textAlign:'left',
      display:'block',
    }}
    onMouseEnter={e => e.currentTarget.style.background = danger ? '#fff5f5' : 'var(--bg1)'}
    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
  >
    {label}
  </button>
))}
    </div>
  )}
</div>
    </div>
  )}

          {/* ═══ AGENT TAB ════════════════════════════════ */}
{tab === 'agent' && (
  <>
    {/* 1. Recent sidebar — own card */}
{!scan && recentSidebarOpen && (
  <div style={{
    width:240, flexShrink:0,
    background:'#fff',
    borderRadius:14,
    border:'1px solid var(--bd)',
    boxShadow:'var(--shadow-md)',
    display:'flex', flexDirection:'column',
    overflow:'hidden',
    margin:0,           /* ← let sb-body padding + gap handle spacing */
    alignSelf:'stretch',
    animation:'sidebarIn .38s cubic-bezier(.22,1,.36,1) both',
  }}>
        <div className="sb-recent-sidebar-inner">
<div className="sb-recent-sidebar-hdr">
  <span className="sb-recent-sidebar-title">Recent Scans</span>
  <div style={{display:'flex', alignItems:'center', gap:6, position:'relative'}}>

    {/* Clear menu trigger */}
    <button
      onClick={() => setShowClearMenu(o => !o)}
      style={{
        width:25, height:25, borderRadius:50,
        background:'transparent', border:'1px solid var(--bd)',
        display:'flex', alignItems:'center', justifyContent:'center',
        cursor:'pointer', color:'var(--t3)', transition:'all .15s',
        fontFamily:'var(--mono)', fontSize:'.44rem',
      }}
      onMouseEnter={e => { e.currentTarget.style.background='var(--bg2)'; e.currentTarget.style.color='var(--t1)'; }}
      onMouseLeave={e => { e.currentTarget.style.background='transparent'; e.currentTarget.style.color='var(--t3)'; }}
      title="Clear history"
    >
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="3 6 5 6 21 6"/>
        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
        <path d="M10 11v6M14 11v6"/>
        <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
      </svg>
    </button>

    {/* Dropdown menu */}
    {showClearMenu && (
      <>
        {/* backdrop */}
        <div
          style={{position:'fixed', inset:0, zIndex:99}}
          onClick={() => setShowClearMenu(false)}
        />
        <div style={{
          position:'absolute', top:28, right:0, zIndex:100,
          background:'#fff', border:'1px solid var(--bd)',
          borderRadius:10, boxShadow:'var(--shadow-md)',
          overflow:'hidden', minWidth:160,
          animation:'blurAppear .15s ease both',
        }}>
          {[
            { label:'Clear today',     fn:() => {
              const today = new Date().toDateString();
              setRecent(r => r.filter(s => new Date(s.scannedAt).toDateString() !== today));
            }},
            { label:'Clear past 7 days', fn:() => {
              const cutoff = Date.now() - 7 * 24 * 60 * 60 * 1000;
              setRecent(r => r.filter(s => new Date(s.scannedAt).getTime() < cutoff));
            }},
            { label:'Clear past 30 days', fn:() => {
              const cutoff = Date.now() - 30 * 24 * 60 * 60 * 1000;
              setRecent(r => r.filter(s => new Date(s.scannedAt).getTime() < cutoff));
            }},
            { label:'Clear all', fn:() => setRecent([]), danger:true },
          ].map(({label, fn, danger}) => (
            <button
              key={label}
              onClick={() => { fn(); setShowClearMenu(false); }}
              style={{
                width:'100%', padding:'9px 14px',
                background:'transparent', border:'none',
                borderBottom:'1px solid var(--bd)',
                fontFamily:'var(--mono)', fontSize:'.48rem',
                color: danger ? 'var(--red)' : 'var(--t2)',
                cursor:'pointer', textAlign:'left',
                transition:'background .12s',
              }}
              onMouseEnter={e => e.currentTarget.style.background='var(--bg1)'}
              onMouseLeave={e => e.currentTarget.style.background='transparent'}
            >
              {label}
            </button>
          ))}
        </div>
      </>
    )}

    {/* Close sidebar button */}
    <button className="sb-recent-sidebar-close" onClick={() => setRecentSidebarOpen(false)}>
      <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
        <path d="M18 6 6 18M6 6l12 12"/>
      </svg>
    </button>

  </div>
</div>
          <div className="sb-recent-sidebar-body">
            {recent.length === 0 ? (
              <div style={{fontFamily:'var(--mono)', fontSize:'.5rem', color:'var(--t3)', padding:'24px 16px', textAlign:'left'}}>
                No scans yet
              </div>
            ) : recent.map(s => {
              const statusColor = {done:'var(--green)', stopped:'var(--red)', error:'var(--red)'}[s.status] || 'var(--t3)';
              return (
                <div key={s.scanId} className="sb-hcard" style={{marginBottom:4}}
                  onClick={() => { loadHistoryScan(s); setRecentSidebarOpen(false); }}
                >
                  <div style={{minWidth:0, flex:1, textAlign:'left'}}>
                    <div className="sb-hurl" style={{overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:150}}>
                      {(s.target||'').replace(/https?:\/\//,'')}
                    </div>
                    <div className="sb-hmeta">
                      {s.score != null ? `Score ${s.score}` : s.status}
                      {s.scannedAt ? ` · ${new Date(s.scannedAt).toLocaleDateString()}` : ''}
                    </div>
                  </div>
                  <span style={{fontFamily:'var(--mono)', fontSize:'.42rem', fontWeight:700, color:statusColor, flexShrink:0}}>
                    {s.status === 'done' ? '✓' : s.status === 'error' ? '✕' : '·'}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    )}

    {/* 2. Main agent col — own card */}
    <div className="sb-agent-col" style={{position:'relative'}}>

      {/* Recent scans button — top left, only when sidebar closed */}
{!scan && recent.length > 0 && !recentSidebarOpen && (
      <button
          onClick={() => setRecentSidebarOpen(true)}
              className="sb-recent-btn"
          style={{
            position:'absolute', top:14, left:14, zIndex:10,
            padding:'6px 12px 6px 10px',
            background:'var(--t1)', border:'none', borderRadius:999,
            fontFamily:'var(--mono)', fontSize:'.7rem', fontWeight:500,
            color:'#fff', cursor:'pointer', letterSpacing:'.04em',
            boxShadow:'0 2px 10px rgba(0,0,0,.18)',
            transition:'all .18s',
          }}
          onMouseEnter={e => { e.currentTarget.style.background='#333'; e.currentTarget.style.transform='translateY(-1px)'; }}
          onMouseLeave={e => { e.currentTarget.style.background='var(--t1)'; e.currentTarget.style.transform='none'; }}
        >
    <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="17" height="17" fill="none" viewBox="0 0 24 24">
      <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 11H4m15.5 5a.5.5 0 0 0 .5-.5V8a1 1 0 0 0-1-1h-3.75a1 1 0 0 1-.829-.44l-1.436-2.12a1 1 0 0 0-.828-.44H8a1 1 0 0 0-1 1M4 9v10a1 1 0 0 0 1 1h11a1 1 0 0 0 1-1v-7a1 1 0 0 0-1-1h-3.75a1 1 0 0 1-.829-.44L9.985 8.44A1 1 0 0 0 9.157 8H5a1 1 0 0 0-1 1Z"/>
    </svg>
    Recent Scans
  </button>
)}

      {/* Deploy overlay */}
{deploying && (
  <div className="sb-deploy-overlay">
    <div className="sb-deploy-ring"/>
    <div className="sb-deploy-label">Deploying security agent</div>
    <div className="sb-deploy-sub">{(target||'').replace(/https?:\/\//,'')}</div>
    {/* Reassurance ticker so users don't think it's frozen */}
    <DeployTicker/>
  </div>
)}
      {/* Browser frame */}
      {(browserFrame?.data || browserFrame?.loading) && (
        <div style={{
          margin:'10px 14px 0', borderRadius:8, overflow:'hidden',
          border:'1px solid var(--bd)', background:'var(--bg1)',
          boxShadow:'var(--shadow-sm)', animation:'fadeUp .25s ease both', flexShrink:0,
        }}>
          <div style={{
            height:26, background:'var(--bg2)', borderBottom:'1px solid var(--bd)',
            display:'flex', alignItems:'center', padding:'0 10px', gap:7,
          }}>
            <div style={{display:'flex', gap:3}}>
              {['rgba(252,80,80,.45)','rgba(251,150,60,.35)','rgba(80,200,80,.35)'].map((c,i) => (
                <div key={i} style={{width:6, height:6, borderRadius:'50%', background:c}}/>
              ))}
            </div>
            <div style={{
              flex:1, height:14, borderRadius:3, background:'var(--bg3)',
              display:'flex', alignItems:'center', padding:'0 7px',
              fontFamily:'var(--mono)', fontSize:'.4rem', color:'var(--t3)',
              overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
            }}>
              {browserFrame?.label || scan?.target?.replace(/https?:\/\//,'') || ''}
            </div>
            <div style={{display:'flex', alignItems:'center', gap:3}}>
              <div style={{width:4, height:4, borderRadius:'50%', background:'rgba(252,80,80,.8)', boxShadow:'0 0 4px rgba(252,80,80,.5)', animation:'pulse 1s infinite'}}/>
              <span style={{fontFamily:'var(--mono)', fontSize:'.36rem', color:'var(--t3)'}}>live</span>
            </div>
          </div>
          {browserFrame?.data
            ? <img key={browserFrame.ts} src={`data:image/jpeg;base64,${browserFrame.data}`}
                style={{width:'100%', maxHeight:160, display:'block', objectFit:'cover', objectPosition:'top', animation:'fadeIn .2s ease'}}/>
            : <div style={{height:90, background:'linear-gradient(90deg,var(--bg2) 25%,var(--bg3) 50%,var(--bg2) 75%)', backgroundSize:'200% 100%', animation:'skelMove 1.8s ease infinite'}}/>
          }
        </div>
      )}
{agentLiveThought && (
  <LiveThoughtBanner
    thought={agentLiveThought}
    liveThoughts={liveThoughts}
    open={liveThoughtOpen}
    onToggle={() => { setLiveThoughtOpen(p=>!p); setShowAllReasoning(false); }}
    showAll={showAllReasoning}
    onToggleAll={() => { setShowAllReasoning(p=>!p); setLiveThoughtOpen(false); }}
  />
)}
{subAgents.length > 0 && (
<SubAgentPanel
  agents={subAgents}
  liveThoughts={liveThoughts}
  agentThoughts={scan?.agentThoughts || {}}
  isRunning={isRunning}
  expanded={agentsExpanded}
  onToggleExpanded={() => setAgentsExpanded(p => !p)}
/>
)}
      {/* Stream */}
      <div className="sb-stream" ref={streamRef}>
        {thoughts.length === 0 && !isActive ? (
          <div className="sb-empty">
<h1 className="sb-empty-title">
  {['Scan', "what's", 'actually', 'broken.'].map((word, i) => (
    <span key={i} className="sb-empty-word" style={{animationDelay:`${i * 0.07}s`}}>
      {word}{i < 3 ? '\u00A0' : ''}
    </span>
  ))}
</h1>
<div className="sb-empty-subtitle-wrap">
  {[
    'SharkBase finds issues in your',
        'vibe-coded app before hackers do',
  ].map((line, i) => (
    <span key={i} className="sb-empty-subtitle-pill" style={{animationDelay:`${i * 0.08}s`}}>
      {line}
    </span>
  ))}
</div>
{githubStep !== 'idle' ? (
  <div style={{animation:'blurAppear .25s cubic-bezier(.22,1,.36,1) both'}}>

    {githubStep === 'ask-mode' && (
      <div style={{
        background:'#fff', border:'1px solid var(--bd)', borderRadius:28,
        padding:'30px 20px 18px', position:'relative',
      }}>
        <button
          onClick={() => { setGithubStep('idle'); setInputFading(false); }}
          style={{
            position:'absolute', top:14, right:14, width:26, height:26,
            borderRadius:'50%', border:'none', background:'#e6e6e6',
            cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center',
            transition:'all .15s',
          }}
          onMouseEnter={e => e.currentTarget.style.background='#d4d4d4'}
          onMouseLeave={e => e.currentTarget.style.background='#e6e6e6'}
        >
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <path d="M18 6L6 18M6 6l12 12"/>
          </svg>
        </button>

        <div style={{fontFamily:'var(--mono)', fontSize:'13px', fontWeight:800, color:'var(--t1)', marginBottom:4, paddingRight:32}}>
          How much to scan?
        </div>
        <div style={{fontFamily:'var(--mono)', fontSize:'12px', color:'var(--t3)', marginBottom:18, lineHeight:1.6}}>
          Scan everything or recent changes.
        </div>

        <div style={{display:'flex', gap:8, justifyContent:'center'}}>
          <button
            onClick={() => tos && launchGithubScan('full', '')}
            disabled={!tos}
            style={{
              width:140, padding:'10px 0', borderRadius:28,
              border:'1.5px solid #111', background:'#111',
              fontFamily:'var(--mono)', fontSize:'12px', fontWeight:700,
              color:'#fff', cursor: tos ? 'pointer' : 'default',
              opacity: tos ? 1 : 0.4, transition:'opacity .15s',
            }}
            onMouseEnter={e => { if(tos) e.currentTarget.style.opacity='.8'; }}
            onMouseLeave={e => e.currentTarget.style.opacity = tos ? '1' : '0.4'}
          >
            Full scan
          </button>
          <button
            onClick={() => setGithubStep('ask-branch')}
            style={{
              width:140, padding:'10px 0', borderRadius:28,
              border:'1px solid var(--bd)', background:'var(--bg1)',
              fontFamily:'var(--mono)', fontSize:'12px', fontWeight:600,
              color:'var(--t1)', cursor:'pointer',
              display:'flex', alignItems:'center', justifyContent:'center', gap:5,
              transition:'all .15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.background='#f4f4f4'; e.currentTarget.style.borderColor='rgba(24,23,15,.2)'; }}
            onMouseLeave={e => { e.currentTarget.style.background='var(--bg1)'; e.currentTarget.style.borderColor='var(--bd)'; }}
          >
            Recent code
          </button>
        </div>

        <div style={{marginTop:16, display:'flex', justifyContent:'center'}}>
          <div className="sb-empty-tos" onClick={() => setTos(p => !p)}>
            <div className={`sb-empty-tos-cb${tos ? ' on' : ''}`}>
              {tos && (
                <svg width="9" height="9" viewBox="0 0 24 24" fill="none"
                  stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              )}
            </div>
            <span style={{
              fontFamily:'var(--mono)', fontSize:'.70rem', fontWeight:700,
              color:'#1193eaff', background:'#eef5ffff',
              padding:'4.7px 10px', borderRadius:999,
            }}>
              <span className="sb-tos-full">I own this repo and authorise testing</span>
              <span className="sb-tos-short">I own this repo</span>
            </span>
          </div>
        </div>
      </div>
    )}

    {githubStep === 'ask-branch' && (
      <div style={{
        background:'#fff', border:'1px solid var(--bd)', borderRadius:28,
        padding:'20px 20px 18px',
        animation:'blurAppear .2s cubic-bezier(.22,1,.36,1) both',
      }}>
        <div style={{fontFamily:'var(--mono)', fontSize:'13px', fontWeight:800, color:'var(--t1)', marginBottom:4}}>
          Base branch
        </div>
        <div style={{fontFamily:'var(--mono)', fontSize:'12px', color:'var(--t3)', marginBottom:14, lineHeight:1.6}}>
          Only new code since this branch gets scanned.
        </div>
        <input
          style={{
            width:'100%', background:'var(--bg1)', boxSizing:'border-box',
            border:`1px solid ${branchError ? '#ff4d4d' : '#29a5f8ff'}`,
            borderRadius:14, padding:'9px 12px',
            fontFamily:'var(--mono)', fontSize:'12px', color:'var(--t1)',
            outline:'none',
          }}
          value={baseBranch}
          onChange={e => { setBaseBranch(e.target.value); setBranchError(''); }}
          onKeyDown={e => e.key === 'Enter' && baseBranch.trim() && launchGithubScan('recent', baseBranch)}
          placeholder="e.g. main, develop, v2"
          autoFocus
        />

        {branchError && (
          <div style={{
            marginTop:8, padding:'8px 12px', borderRadius:10, background:'#fff0f0',
            display:'flex', alignItems:'flex-start', gap:7,
          }}>
            <span style={{fontFamily:'var(--mono)', fontSize:'11px', fontWeight:700, color:'#ef4444', flexShrink:0, marginTop:1}}>⚠</span>
            <span style={{fontFamily:'var(--mono)', fontSize:'11px', color:'#ef4444', lineHeight:1.6}}>{branchError}</span>
          </div>
        )}

        {!branchError && ['main','master','dev','develop'].includes(baseBranch.trim().toLowerCase()) && (
          <div style={{
            marginTop:8, padding:'8px 12px', borderRadius:10, background:'#fffbeb',
            display:'flex', alignItems:'flex-start', gap:7,
          }}>
            <span style={{fontFamily:'var(--mono)', fontSize:'11px', fontWeight:700, color:'#f59e0b', flexShrink:0, marginTop:1}}>⚠</span>
            <span style={{fontFamily:'var(--mono)', fontSize:'11px', color:'#f59e0b', lineHeight:1.6}}>
              That's a default branch. Will run a full scan instead.
            </span>
          </div>
        )}

        <div style={{display:'flex', gap:8, marginTop:12, justifyContent:'center'}}>
          <button
            onClick={() => { setGithubStep('ask-mode'); setBranchError(''); }}
            style={{
              width:140, padding:'10px 0', borderRadius:28,
              border:'1px solid var(--bd)', background:'var(--bg1)',
              fontFamily:'var(--mono)', fontSize:'12px', fontWeight:600,
              color:'var(--t1)', cursor:'pointer',
              display:'flex', alignItems:'center', justifyContent:'center', gap:5,
              transition:'all .15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.background='#f4f4f4'; e.currentTarget.style.borderColor='rgba(24,23,15,.2)'; }}
            onMouseLeave={e => { e.currentTarget.style.background='var(--bg1)'; e.currentTarget.style.borderColor='var(--bd)'; }}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M11 6l-6 6 6 6"/>
            </svg>
            Back
          </button>
          <button
            onClick={() => launchGithubScan('recent', baseBranch)}
            disabled={!baseBranch.trim() || starting}
            style={{
              width:140, padding:'10px 0', borderRadius:28, border:'none',
              background: baseBranch.trim() ? '#111' : 'var(--bg2)',
              fontFamily:'var(--mono)', fontSize:'12px', fontWeight:700,
              color: baseBranch.trim() ? '#fff' : 'var(--t3)',
              cursor: baseBranch.trim() ? 'pointer' : 'default',
              transition:'all .15s',
            }}
            onMouseEnter={e => { if(baseBranch.trim()) e.currentTarget.style.opacity='.8'; }}
            onMouseLeave={e => e.currentTarget.style.opacity='1'}
          >
            {starting ? <Spin s={10} c="#fff"/> : 'Scan'}
          </button>
        </div>
      </div>
    )}
  </div>

) : showScanModeQ ? (
  <div style={{
    background:'#fff', border:'1px solid var(--bd)', borderRadius:20,
    padding:'28px 20px 28px', position:'relative',
    width:'100%', maxWidth:360,
    boxShadow:'0 4px 8px rgba(0,0,0,.05)',
    animation:'blurAppear .25s cubic-bezier(.22,1,.36,1) both',
  }}>
    {/* Close */}
    <button
      onClick={() => { setShowScanModeQ(false); setScanMode(null); }}
      style={{
        position:'absolute', top:12, right:12, width:24, height:24,
        borderRadius:'50%', border:'none', background:'#e6e6e6',
        cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center',
        transition:'all .15s',
      }}
      onMouseEnter={e => e.currentTarget.style.background='#d4d4d4'}
      onMouseLeave={e => e.currentTarget.style.background='#e6e6e6'}
    >
      <svg width="9" height="9" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
        <path d="M18 6L6 18M6 6l12 12"/>
      </svg>
    </button>

    <div style={{fontFamily:'var(--mono)', fontSize:'13px', fontWeight:800, color:'var(--t1)', marginBottom:3, paddingRight:28}}>
      How do you want to scan?
    </div>
    <div style={{fontFamily:'var(--mono)', fontSize:'11px', color:'var(--t3)', marginBottom:18, lineHeight:1.5, fontStyle: 'italic' }}>
      Authenticated testing finds 80% more vulnerabilities.
    </div>

    <div style={{display:'flex', gap:8, justifyContent:'center', marginBottom:16}}>
      {/* No login */}
      <button
        onClick={() => setScanMode(scanMode === 'unauth' ? null : 'unauth')}
        style={{
          width:140, padding:'10px 0', borderRadius:28,
          border: scanMode === 'unauth' ? '1.5px solid #111' : '1px solid var(--bd)',
          background: scanMode === 'unauth' ? '#111' : 'var(--bg1)',
          fontFamily:'var(--mono)', fontSize:'12px', fontWeight:700,
          color: scanMode === 'unauth' ? '#fff' : 'var(--t1)',
          cursor:'pointer', transition:'all .15s',
        }}>
        No login
      </button>

      {/* With login */}
      <button
        onClick={() => setScanMode(scanMode === 'auth' ? null : 'auth')}
        style={{
          width:140, padding:'10px 0', borderRadius:28,
          border: scanMode === 'auth' ? '1.5px solid #111' : '1px solid var(--bd)',
          background: scanMode === 'auth' ? '#111' : 'var(--bg1)',
          fontFamily:'var(--mono)', fontSize:'12px', fontWeight:700,
          color: scanMode === 'auth' ? '#fff' : 'var(--t1)',
          cursor:'pointer', transition:'all .15s',
          display:'flex', alignItems:'center', justifyContent:'center', gap:6,
        }}
      >
        With login

      </button>
    </div>
{/* Cookie — only when with login selected */}
{scanMode === 'auth' && (
  <div style={{
    padding:'10px 12px', marginBottom:14,
    background:'var(--bg1)', border:'1px solid var(--bd)',
    borderRadius:12, animation:'blurAppear .18s ease both',
  }}>

    {(() => {
      return showSteps ? (
        /* Steps view */
        <div style={{animation:'blurAppear .15s ease both'}}>
          <div style={{fontFamily:'var(--mono)', fontSize:'11px', fontWeight:700, color:'var(--t1)', marginBottom:10}}>
            How to find your session cookie
          </div>
          {[
            'Open the site in Chrome or Firefox',
            'Press F12 to open DevTools',
            'Click the Application tab',
            'In the left sidebar click Cookies',
            'Click on the site URL',
            'Find the session or auth cookie and copy its value',
          ].map((step, i) => (
            <div key={i} style={{
              display:'flex', alignItems:'flex-start', gap:8,
              marginBottom: i < 5 ? 7 : 12,
            }}>
              <span style={{
                fontFamily:'var(--mono)', fontSize:'10px', fontWeight:700,
                color:'var(--t3)', flexShrink:0, minWidth:14,
              }}>
                {i + 1}.
              </span>
              <span style={{fontFamily:'var(--mono)', fontSize:'11px', color:'var(--t2)', lineHeight:1.5}}>
                {step}
              </span>
            </div>
          ))}
          <button
            onClick={() => setShowSteps(false)}
            style={{
              width:'100%', padding:'8px 0', borderRadius:28,
              border:'1px solid var(--bd)', background:'var(--t1)',
              fontFamily:'var(--mono)', fontSize:'12px', fontWeight:700,
              color:'#fff', cursor:'pointer', transition:'opacity .15s',
            }}
            onMouseEnter={e => e.currentTarget.style.opacity='.8'}
            onMouseLeave={e => e.currentTarget.style.opacity='1'}
          >
            Got it
          </button>
        </div>
      ) : (
        /* Input view */
        <>
          <div style={{marginBottom:6}}>
            <button
              onClick={() => setShowSteps(true)}
              style={{
                fontFamily:'var(--mono)', fontSize:'10px', color:'#5d5d5dff',
                background:'transparent', border:'none', cursor:'pointer',
                padding:0, textDecoration:'underline', textUnderlineOffset:2,
              }}
            >
              How do I find this?
            </button>
          </div>
          <input
            style={{
              width:'100%', background:'#fff', boxSizing:'border-box',
              border:`1px solid ${sessionCookie.trim() ? 'rgba(17,147,234,.4)' : 'var(--bd)'}`,
              borderRadius:28, padding:'8px 10px',
              fontFamily:'var(--mono)', fontSize:'12px', color:'var(--t1)',
              outline:'none', transition:'border-color .18s',
            }}
            placeholder="session=abc123; csrf_token=xyz..."
            value={sessionCookie}
            onChange={e => setSessionCookie(e.target.value)}
            onFocus={e  => e.target.style.borderColor='rgba(17,147,234,.5)'}
            onBlur={e   => e.target.style.borderColor=sessionCookie.trim() ? 'rgba(17,147,234,.4)' : 'var(--bd)'}
            autoFocus
          />
          {sessionCookie.trim() && (
            <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginTop:5}}>
              <span style={{fontFamily:'var(--mono)', fontSize:'10px', color:'#16a34a', display:'flex', alignItems:'center', gap:4}}>
                <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                Cookie ready
              </span>
              <button onClick={() => setSessionCookie('')}
                style={{fontFamily:'var(--mono)', fontSize:'10px', color:'var(--t3)', background:'transparent', border:'none', cursor:'pointer', padding:0}}
                onMouseOver={e => e.target.style.color='var(--red)'}
                onMouseOut={e  => e.target.style.color='var(--t3)'}
              >Clear</button>
            </div>
          )}
        </>
      );
    })()}
  </div>
)}

    {/* TOS + Launch */}
    <div style={{display:'flex', alignItems:'center', justifyContent:'space-between'}}>
<div className="sb-empty-tos" onClick={() => setTos(p => !p)}>
  <div className={`sb-empty-tos-cb${tos ? ' on' : ''}`}>
    {tos && (
      <svg width="9" height="9" viewBox="0 0 24 24" fill="none"
        stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12"/>
      </svg>
    )}
  </div>
  <span style={{
          fontFamily:'var(--mono)', fontSize:'.70rem', fontWeight:700,
          color:'#1193eaff', background:'#eef5ffff',
          padding:'4.7px 10px', borderRadius:999,
  }}>
    I own this site
  </span>
</div>

      <button
        onClick={() => {
          if (!scanMode || !tos || starting) return;
          if (scanMode === 'unauth') setSessionCookie('');
          setScanMode(null);
          setShowScanModeQ(false);
          start();
        }}
        disabled={!scanMode || !tos || starting}
        style={{
          padding:'9px 20px', borderRadius:999, border:'none',
          background: scanMode && tos ? 'var(--t1)' : 'var(--bg2)',
          fontFamily:'var(--mono)', fontSize:'12px', fontWeight:700,
          color: scanMode && tos ? '#fff' : 'var(--t3)',
          cursor: scanMode && tos && !starting ? 'pointer' : 'default',
          transition:'all .18s', display:'flex', alignItems:'center', gap:6,
          opacity: starting ? 0.6 : 1,
        }}
        onMouseEnter={e => { if (scanMode && tos) e.currentTarget.style.background='#222'; }}
        onMouseLeave={e => { if (scanMode && tos) e.currentTarget.style.background='var(--t1)'; }}
      >
        {starting
          ? <><Spin s={11} c="#fff"/> Starting…</>
          : <> Scan
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 19V5m0 0-6 6m6-6 6 6"/>
              </svg>
            </>
        }
      </button>
    </div>
  </div>

) : (
<div
    className="sb-empty-input-wrap"
    style={{
      opacity:       inputFading ? 0 : 1,
      transform:     inputFading ? 'scale(.97)' : 'scale(1)',
      transition:    'opacity .25s ease, transform .25s ease',
      pointerEvents: inputFading ? 'none' : 'auto',
    }}
  >
    {/* Hidden file input — only APK and IPA */}
    <input
      id="sb-file-upload"
      type="file"
      accept=".apk,.ipa"
      style={{ display: 'none' }}
      onChange={e => {
        const file = e.target.files?.[0];
        if (!file) return;
        const ext = file.name.split('.').pop().toLowerCase();
        if (!['apk', 'ipa'].includes(ext)) {
          setUrlError('Only .apk and .ipa files are supported.');
          e.target.value = '';
          return;
        }
        setUrlError('');
        setTarget(file.name);
        setUploadedFile(file);
      }}
    />

    {/* Input row — shows filename pill when file selected, text input otherwise */}
{uploadedFile ? (
  <div style={{
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '2px 0 6px', minHeight: 28,
    textAlign: 'left',
  }}>

    {/* Badge — matches your exact blue pill style from the app */}
    <span style={{
      fontFamily: 'var(--mono)', fontSize: '10px', fontWeight: 700,
      color: '#1193eaff', background: '#eef5ffff',
      padding: '2px 9px', borderRadius: 999, flexShrink: 0,
    }}>
      {uploadedFile.name.endsWith('.apk') ? 'Android' : 'iOS'}
    </span>

    {/* Filename + size together, no flex:1 gap between them */}
    <span style={{
      fontFamily: 'var(--mono)', fontSize: '13px', color: 'var(--t1)',
      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      flexShrink: 1, minWidth: 0,
    }}>
      {uploadedFile.name}
    </span>

    {/* Size — sits right next to filename */}
    <span style={{
      fontFamily: 'var(--mono)', fontSize: '11px',
      color: 'var(--t3)', flexShrink: 0, whiteSpace: 'nowrap',
    }}>
      {(uploadedFile.size / 1024 / 1024).toFixed(1)}MB
    </span>

    {/* Spacer pushes X to the right */}
    <div style={{ flex: 1 }}/>

    {/* Clear */}
    <button
      onClick={() => {
        setUploadedFile(null);
        setTarget('');
        setUrlError('');
        document.getElementById('sb-file-upload').value = '';
      }}
      style={{
        width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
        background: 'var(--bg3)', border: '1px solid var(--bd)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: 'pointer', transition: 'background .15s',
      }}
    >
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
        stroke="#000" strokeWidth="2.5" strokeLinecap="round">
        <path d="M18 6L6 18M6 6l12 12"/>
      </svg>
    </button>
  </div>
) : (
      <input
        className="sb-empty-url"
        placeholder="Enter URL, GitHub repo, or upload .apk / .ipa"
        value={target}
        onChange={e => {
          setTarget(e.target.value);
          setUploadedFile(null);
          if (urlError) setUrlError('');
        }}
        onKeyDown={e => {
          if (e.key !== 'Enter' || !tos || !target.trim()) return;
          if (target.includes('github.com/')) { start(); return; }
          const v = validateTarget(target.trim());
          if (!v.valid) { setUrlError(v.error || 'Invalid URL'); return; }
          setShowScanModeQ(true);
        }}
        onBlur={() => {
          if (target.trim()) {
            const v = validateTarget(target);
            if (!v.valid && v.error) setUrlError(v.error);
          }
        }}
        autoComplete="off"
        spellCheck={false}
        style={urlError ? { color: 'var(--red)' } : {}}
      />
    )}

    {urlError && (
      <div style={{
        fontFamily: 'var(--mono)', fontSize: '11px', color: 'var(--red)',
        marginTop: 6, paddingTop: 6,
        borderTop: '1px solid rgba(220,38,38,0.15)',
        animation: 'fadeUp .15s ease both', textAlign: 'left',
      }}>
        {urlError}
      </div>
    )}

    <div className="sb-empty-footer">
      <div className="sb-empty-left" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>

        {/* Plus button — triggers file picker */}
        <button
          className="sb-empty-plus"
          onClick={() => document.getElementById('sb-file-upload').click()}
          title="Upload .apk or .ipa file"
          style={{ flexShrink: 0 }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
            stroke="#2b2b2bff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
            <polyline points="17 8 12 3 7 8"/>
            <line x1="12" y1="3" x2="12" y2="15"/>
          </svg>
        </button>

        <div className="sb-empty-tos" onClick={() => setTos(p => !p)}>
          <div className={`sb-empty-tos-cb${tos ? ' on' : ''}`}>
            {tos && (
              <svg width="9" height="9" viewBox="0 0 24 24" fill="none"
                stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
            )}
          </div>
          <span style={{
            fontFamily: 'var(--mono)', fontSize: '.70rem', fontWeight: 700,
            color: '#1193eaff', background: '#eef5ffff',
            padding: '4.7px 10px', borderRadius: 999,
          }}>
            <span className="sb-tos-full">I own this and authorise testing</span>
            <span className="sb-tos-short">I own this</span>
          </span>
        </div>
      </div>

<button
className={`sb-empty-submit${((target.trim() || uploadedFile) && tos && !urlError) || uploading ? ' active' : ''}`}
  onClick={() => {
    if (uploadedFile) {
      if (!tos) return;
      startMobileScan(uploadedFile);
      return;
    }
    if (!target.trim() || !tos || starting || urlError) return;
    if (target.includes('github.com/')) { start(); return; }
    const v = validateTarget(target.trim());
    if (!v.valid) { setUrlError(v.error || 'Invalid URL'); return; }
    setShowScanModeQ(true);
  }}
  disabled={(!target.trim() && !uploadedFile) || !tos || starting || uploading || !!urlError}
  title="Next"
>
  {starting || uploading
    ? <Spin s={11} c="var(--t3)"/>
    : <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg"
        width="24" height="24" fill="none" viewBox="0 0 24 24">
        <path stroke="currentColor" strokeLinecap="round"
          strokeLinejoin="round" strokeWidth="2"
          d="M12 6v13m0-13 4 4m-4-4-4 4"/>
      </svg>
  }
</button> 
    </div>
  </div>
)}

{githubStep === 'idle' && !showScanModeQ && (
  <>
    <div className="sb-empty-chips">
      {[
        { label:'Full scan',    hide:'',        icon:<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg> },
        { label:'Auth test',    hide:'',        icon:<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="18" height="11" x="3" y="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg> },
        { label:'IDOR check',   hide:'',        icon:<svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M4.62471 4.00001L4.56402 4.00001C4.04134 3.99993 3.70687 3.99988 3.4182 4.055C2.2379 4.28039 1.29846 5.17053 1.05815 6.33035C0.999538 6.61321 0.999604 6.93998 0.999703 7.43689L0.999711 7.50001L0.999703 7.56313C0.999604 8.06004 0.999538 8.38681 1.05815 8.66967C1.29846 9.8295 2.2379 10.7196 3.4182 10.945C3.70688 11.0001 4.04135 11.0001 4.56403 11L4.62471 11H5.49971C5.77585 11 5.99971 10.7762 5.99971 10.5C5.99971 10.2239 5.77585 10 5.49971 10H4.62471C4.02084 10 3.78907 9.99777 3.60577 9.96277C2.80262 9.8094 2.19157 9.21108 2.03735 8.46678C2.00233 8.29778 1.99971 8.08251 1.99971 7.50001C1.99971 6.91752 2.00233 6.70225 2.03735 6.53324C2.19157 5.78895 2.80262 5.19062 3.60577 5.03725C3.78907 5.00225 4.02084 5.00001 4.62471 5.00001H5.49971C5.77585 5.00001 5.99971 4.77615 5.99971 4.50001C5.99971 4.22387 5.77585 4.00001 5.49971 4.00001H4.62471ZM10.3747 5.00001C10.9786 5.00001 11.2104 5.00225 11.3937 5.03725C12.1968 5.19062 12.8079 5.78895 12.9621 6.53324C12.9971 6.70225 12.9997 6.91752 12.9997 7.50001C12.9997 8.08251 12.9971 8.29778 12.9621 8.46678C12.8079 9.21108 12.1968 9.8094 11.3937 9.96277C11.2104 9.99777 10.9786 10 10.3747 10H9.49971C9.22357 10 8.99971 10.2239 8.99971 10.5C8.99971 10.7762 9.22357 11 9.49971 11H10.3747L10.4354 11C10.9581 11.0001 11.2925 11.0001 11.5812 10.945C12.7615 10.7196 13.701 9.8295 13.9413 8.66967C13.9999 8.38681 13.9998 8.06005 13.9997 7.56314L13.9997 7.50001L13.9997 7.43688C13.9998 6.93998 13.9999 6.61321 13.9413 6.33035C13.701 5.17053 12.7615 4.28039 11.5812 4.055C11.2925 3.99988 10.9581 3.99993 10.4354 4.00001L10.3747 4.00001H9.49971C9.22357 4.00001 8.99971 4.22387 8.99971 4.50001C8.99971 4.77615 9.22357 5.00001 9.49971 5.00001H10.3747ZM5.00038 7C4.72424 7 4.50038 7.22386 4.50038 7.5C4.50038 7.77614 4.72424 8 5.00038 8H10.0004C10.2765 8 10.5004 7.77614 10.5004 7.5C10.5004 7.22386 10.2765 7 10.0004 7H5.00038Z" fill="currentColor" fill-rule="evenodd" clip-rule="evenodd"></path></svg> },
        { label:'Inject test',  hide:'hide-sm', icon:<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="4 17 10 11 4 5"/><line x1="12" y1="19" x2="20" y2="19"/></svg> },
        { label:'More',         hide:'hide-xs', icon:null },
      ].map(({label, icon, hide}, i) => (
        <button key={i} className={`sb-empty-chip${hide ? ' '+hide : ''}`} style={{animationDelay:`${.28 + i * .05}s`}}>
          {icon}{label}
        </button>
      ))}
    </div>

    {recent.length > 0 && (
      <button className="sb-mob-recent-btn" onClick={openMobRecent}>
        <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="17" height="17" fill="none" viewBox="0 0 24 24">
          <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 11H4m15.5 5a.5.5 0 0 0 .5-.5V8a1 1 0 0 0-1-1h-3.75a1 1 0 0 1-.829-.44l-1.436-2.12a1 1 0 0 0-.828-.44H8a1 1 0 0 0-1 1M4 9v10a1 1 0 0 0 1 1h11a1 1 0 0 0 1-1v-7a1 1 0 0 0-1-1h-3.75a1 1 0 0 1-.829-.44L9.985 8.44A1 1 0 0 0 9.157 8H5a1 1 0 0 0-1 1Z"/>
        </svg>
        Recent Scans
      </button>
    )}
  </>
)}

          </div>

        ) : (
          <>
{scan?.target && (
  <div className="sb-divider">
    <div className="sb-div-line"/>
    <div className="sb-div-lbl" style={{
      display: 'flex', alignItems: 'center', gap: 6,
    }}>
      {scan.target.match(/\.(ipa|apk)$/i) ? (
        <div style={{
          width: 18, height: 18, borderRadius: '50%',
          background: 'var(--bg3)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none"
            stroke="var(--t3)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="5" y="2" width="14" height="20" rx="2" ry="2"/>
            <line x1="12" y1="18" x2="12.01" y2="18"/>
          </svg>
        </div>
      ) : (
        <FaviconImg target={scan.target} size={14}/>
      )}
      {(scan.target||'').replace(/https?:\/\//,'').replace(/\/$/,'')}
    </div>
    <div className="sb-div-line"/>
  </div>
)}

{isActive && thoughts.length > 0 && (
  <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', padding:'6px 14px 2px', borderBottom:'1px solid var(--bd)'}}>
    {!allOpen
      ? <div/>
      : <div style={{display:'flex', alignItems:'center', gap:6}}>
          <Spin s={9} c="var(--t3)"/>
          <span style={{fontFamily:'var(--mono)', fontSize:'11px', color:'var(--t3)'}}>
           Scanning — {thoughts.filter(t=>t.type==='tool_call').length} tool call{thoughts.filter(t=>t.type==='tool_call').length!==1?'s':''} so far
          </span>
        </div>
    }
    <button
      onClick={() => setAllOpen(o => !o)}
      style={{fontFamily:'var(--mono)', fontSize:'11px', color:'#000000c8', background:'none', border:'none', cursor:'pointer', padding:'2px 6px'}}
      onMouseOver={e => e.currentTarget.style.color='var(--t1)'}
      onMouseOut={e => e.currentTarget.style.color='var(--t3)'}
    >
      {allOpen ? 'Collapse all ↑' : 'Expand all ↓'}
    </button>
  </div>
)}
{(() => {
  // ── Build raw segments ─────────────────────────────────────
  const rawSegments = [];
  let cur = null;

  thoughts.forEach((t, i) => {
    if (t.type === 'thinking' || t.type === 'report' || t.type === 'approval_request') {
      if (cur) rawSegments.push(cur);
      cur = { think: t, thinkIdx: i, tools: [] };
    } else if (t.type === 'tool_call' || t.type === 'tool_result') {
      if (!cur) cur = { think: null, thinkIdx: -1, tools: [] };
      cur.tools.push({ t, i });
    }
  });
  if (cur) rawSegments.push(cur);

  // ── Split startup tools from scan tools within same segment ──
  const splitSegments = [];
  for (const seg of rawSegments) {
    const startupItems    = seg.tools.filter(({ t }) => t.tool === 'startup');
    const nonStartupItems = seg.tools.filter(({ t }) => t.tool !== 'startup');
    if (startupItems.length > 0 && nonStartupItems.length > 0) {
      // Startup tools → their own segment
      splitSegments.push({ think: seg.think, thinkIdx: seg.thinkIdx, tools: startupItems });
      // Non-startup tools → new segment with no think
      splitSegments.push({ think: null, thinkIdx: -1, tools: nonStartupItems });
    } else {
      splitSegments.push(seg);
    }
  }

  // ── Merge tool-only segments into the previous segment ─────
  const merged = [];
  for (const seg of splitSegments) {
    const last = merged[merged.length - 1];
    const isStartupLast = last && last.tools.length > 0 && last.tools.every(({ t }) => t.tool === 'startup');
    const hasNonStartupTools = seg.tools.some(({ t }) => t.type === 'tool_call' && t.tool !== 'startup');
    const isToolOnlySeg = seg.think === null && seg.think?.type !== 'report' && seg.think?.type !== 'approval_request';

    if (last && isToolOnlySeg && !(isStartupLast && hasNonStartupTools)) {
      last.tools.push(...seg.tools);
    } else {
      merged.push({ think: seg.think, thinkIdx: seg.thinkIdx, tools: [...seg.tools] });
    }
  }
  // ── Separate startup from scan segments ───────────────────
  const isStartupSeg = (seg) =>
    seg.tools.length > 0 && seg.tools.every(({ t }) => t.tool === 'startup');

  // Build startup pairs — deduplicate by label, keep last result only
  const startupPairMap = new Map();
  for (const seg of merged.filter(isStartupSeg)) {
    const items = seg.tools;
    for (let k = 0; k < items.length; k++) {
      const { t } = items[k];
      if (t.type === 'tool_call') {
        const next   = items[k + 1];
        const result = next?.t?.type === 'tool_result' ? next.t : null;
        if (result) k++;
        const label = t.input?.stage || 'Setup';
        startupPairMap.set(label, { call: t, result });
      }
    }
  }
  const startupPairs = [...startupPairMap.values()];

  const scanSegs       = merged.filter(s => !isStartupSeg(s));
  const scanHasStarted = scanSegs.some(s => s.tools.length > 0);

  return (
    <>
      {/* ── Pre-scan block ─────────────────────────────────── */}
      {startupPairs.length > 0 && (
        <StageBlock
          key="startup"
          stageNum={0}
          think={null}
          thinkIdx={-1}
          pairs={startupPairs}
          warns={startupPairs.filter(p => p.result?.isWarning).length}
          isActive={isActive && !scanHasStarted}
          isLive={false}
          liveThought=""
          thoughts={thoughts}
          forceOpen={scanHasStarted ? false : undefined}
          isStartup={true}
        />
      )}

      {/* ── Scan stages ────────────────────────────────────── */}
      {scanSegs.map((seg, si) => {
        if (seg.think?.type === 'report' || seg.think?.type === 'approval_request') {
          return <div key={si}>{seg.think && renderThought(seg.think, seg.thinkIdx)}</div>;
        }
        if (!seg.think && seg.tools.length === 0) return null;

        const pairs = [];
        const toolItems = seg.tools;
        for (let k = 0; k < toolItems.length; k++) {
          const { t, i } = toolItems[k];
          if (t.type === 'tool_call') {
            const nextItem = toolItems[k + 1];
            const result   = nextItem?.t?.type === 'tool_result' ? nextItem.t : null;
            if (result) k++;
            pairs.push({ call: t, result, callIdx: i });
          }
        }

        if (pairs.length === 0 && !seg.think) return null;

const warns = pairs.filter(p =>
  p.result?.summary?.includes('⚠') ||
  p.result?.isWarning ||
  p.result?.finding?.severity === 'CRITICAL' ||
  p.result?.finding?.severity === 'HIGH'
).length;
        const isCurrentStage = si === scanSegs.length - 1 && isActive;

        return (
          <StageBlock
            key={si}
            stageNum={si + 1}
            think={seg.think}
            thinkIdx={seg.thinkIdx}
            pairs={pairs}
            warns={warns}
            isActive={isCurrentStage}
            isLive={liveIdx === seg.thinkIdx}
            liveThought={liveIdx === seg.thinkIdx ? liveThought : ''}
            thoughts={thoughts}
            forceOpen={allOpen || isDone}
          />
        );
      })}
    </>
  );
})()}

            {isRunning && !liveThought && thoughts.length > 0 && thoughts[thoughts.length - 1]?.type === 'tool_result' && (() => {
              const lastTool = [...thoughts].reverse().find(t => t.type === 'tool_call')?.tool;
              const hints = Object.keys(NEXT_HINT);
              const lastIdx = hints.indexOf(lastTool);
              const nextTool = hints[lastIdx + 1] || 'finish_report';
              const hintText = NEXT_HINT[nextTool] || 'Working…';
              return (
                <div style={{padding:'10px 18px 14px', borderBottom:'1px solid var(--bd)', textAlign:'left'}}>
                  <div style={{fontFamily:'var(--mono)', fontSize:'13px', marginBottom:10, background:'linear-gradient(90deg,var(--t3) 20%,var(--t1) 50%,var(--t3) 80%)', backgroundSize:'200% auto', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent', backgroundClip:'text', animation:'shimmer 1.8s linear infinite'}}>
                    {hintText}
                  </div>
                  <div className="sb-skel-line full"/>
                  <div className="sb-skel-line med"   style={{animationDelay:'.15s'}}/>
                  <div className="sb-skel-line short" style={{animationDelay:'.28s'}}/>
                </div>
              );
            })()}

{isRunning && thoughts.length === 0 && <WarmupSkeleton target={scan?.target || ''} />}

          </>
        )}
      </div>{/* end sb-stream */}

      {/* Sticky download bar */}
{isDone && score !== null && (
  <div style={{
    flexShrink: 0,
    padding: '10px 16px',
    background: 'rgba(255,255,255,.97)',
    marginBottom: window.innerWidth <= 768 ? 95 : 0,
    backdropFilter: 'blur(12px)',
    borderTop: '1px solid var(--bd)',
    display: 'flex',
    flexWrap: 'wrap',
    gap: 10,
    alignItems: 'center',
    animation: 'fadeUp .3s ease both',
  }}>

    {/* Left: Mini score badge + text */}
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: '1 1 160px', minWidth: 0 }}>

      {/* Inline score pill — no ScoreRing, fully controlled */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 40,
        height: 40,
        borderRadius: '50%',
        border: `2.5px solid ${scoreColor(score)}`,
        flexShrink: 0,
        background: `${scoreColor(score)}12`,
      }}>
        <span style={{
          fontFamily: 'var(--mono)',
          fontSize: '.83rem',
          fontWeight: 800,
          color: scoreColor(score),
          lineHeight: 1,
        }}>
          {score}%
        </span>
      </div>

      {/* Divider */}
      <div style={{ width: 2, height: 28, background: 'var(--bd, #5c5c5cff)', flexShrink: 0 }} />

      {/* Text — left aligned */}
      <div style={{ minWidth: 0, textAlign: 'left' }}>
        <div style={{
          fontFamily: 'var(--mono)',
          fontWeight: 700,
          fontSize: '.63rem',
          color: scoreColor(score),
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          letterSpacing: '.03em',
        }}>
          {scoreLabel(score)}
        </div>
        <div style={{
          fontFamily: 'var(--mono)',
          color: '#00000073',
          marginTop: 2,
          fontSize: '.57rem',
          whiteSpace: 'nowrap',
           fontWeight: 700,
        }}>
{af.length} issue{af.length !== 1 ? 's' : ''} found
{af.some(f => f.blastRadius?.totalRows > 0) && (
  <span style={{ color: '#dc2626', marginLeft: 4 }}>
    · {af.reduce((sum, f) => sum + (f.blastRadius?.totalRows || 0), 0).toLocaleString()} records exposed
  </span>
)}        </div>
      </div>
    </div>

{/* Right: Buttons */}
    <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
      <button
        className="sb-act-pri"
        onClick={exportPdf}
        disabled={pdfBusy}
        style={{ fontSize: '.62rem', padding: '8px 14px', whiteSpace: 'nowrap' }}
      >
        {pdfBusy ? <><Spin s={10} c="#fff" /> Generating…</> : 'Download Report'}
      </button>
      <button
        className="sb-act-ghost"
        onClick={() => setTab('report')}
        style={{ fontSize: '.64rem', padding: '8px 12px', fontWeight: 800, color: '#00000092', whiteSpace: 'nowrap' }}
      >
        View Report
      </button>
      {scan?.masterPrompt && (
        <CopyMasterPromptBtn prompt={scan.masterPrompt} />
      )}
    </div>
  </div>
)}

    </div>{/* end sb-agent-col */}

{/* 3. Right findings panel — own card */}
{scan && (
  <div
    className={`sb-panel${tab === 'findings' ? ' mob-show' : ''}`}
    style={{ animation: 'blurAppear .4s ease both' }}
  >
    {/* Findings header */}
    <div style={{ flexShrink: 0 }}>
      <div className="sb-panel-hdr">
        <div className="sb-panel-title">Live Findings</div>
        {af.length > 0 && (
          <div className="sb-panel-count">
            {af.length} issue{af.length !== 1 ? 's' : ''}
          </div>
        )}
      </div>
    </div>

    <div
      className={`sb-panel-body${isRunning && af.length === 0 ? ' sb-scan-overlay' : ''}`}
      style={{ flex: '1 1 0', minHeight: 120, overflowY: 'auto' }}
    >
      {isRunning && af.length === 0 && <div className="sb-scan-line" />}

      {/* Score block */}
      {score !== null && (
        <div className="sb-score-block">
          <ScoreRing score={score} size={52} />
          <div className="sb-score-right">
            <div className="sb-score-label" style={{ color: scoreColor(score) }}>
              {scoreLabel(score)}
            </div>
            <div className="sb-score-chips">
              {['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'].map(s => {
                const n = af.filter(f => f.severity === s).length;
                return n > 0 ? (
                  <span key={s} className="sb-sev-chip" style={{
                    color: 'rgba(20,20,20,.66)',
                    borderColor: 'rgba(127,127,127,.3)',
                    background: 'rgba(139,139,139,.04)',
                  }}>
                    {n} {s.slice(0, 3)}
                  </span>
                ) : null;
              })}
            </div>
          </div>
        </div>
      )}

      {/* Findings — collapsible main container */}
      {af.length === 0 ? (
        <div className="sb-no-findings">
          {isRunning ? (
            <>
              <Spin s={14} c="var(--t3)" />
              <div className="sb-no-txt">Agent is working…</div>
            </>
          ) : (
            <div className="sb-no-txt">{scan ? 'No findings yet.' : 'Start a scan.'}</div>
          )}
        </div>
      ) : (() => {
        const PREVIEW = 3;
        const shown = findingsExpanded ? af : af.slice(0, PREVIEW);
        const extra = af.length - PREVIEW;
        return (
          <div>
            {/* Main collapsible container */}
            <div style={{
              margin:'10px 10px 0',
              borderRadius:10,
              border:'1px solid var(--bd)',
              background:'#fff',
              overflow:'hidden',
              boxShadow:'var(--shadow-sm)',
            }}>
              {/* Container header */}
              <div
                onClick={() => setFindingsExpanded(o => !o)}
                style={{
                  padding:'9px 12px',
                  display:'flex', alignItems:'center', justifyContent:'space-between',
                  cursor:'pointer', background:'var(--bg1)',
                  borderBottom: findingsExpanded ? '1px solid var(--bd)' : 'none',
                  transition:'background .12s',
                }}
                onMouseEnter={e => e.currentTarget.style.background='var(--bg2)'}
                onMouseLeave={e => e.currentTarget.style.background='var(--bg1)'}
              >
                <div style={{display:'flex', alignItems:'center', gap:8}}>
                  <span style={{fontFamily:'var(--mono)', fontSize:'11px', fontWeight:700, color:'var(--t2)'}}>
                    Security Issues
                  </span>
                  <span style={{
                    fontFamily:'var(--mono)', fontSize:'10px', fontWeight:700,
                    color:'#ef4444', background:'#fff0f0',
                    padding:'1px 7px', borderRadius:999,
                    border:'1px solid rgba(220,38,38,.2)',
                  }}>
                    {af.length}
                  </span>
                </div>
                <div style={{
                  width:20, height:20, borderRadius:'50%',
                  background:'var(--bg3)',
                  display:'flex', alignItems:'center', justifyContent:'center',
                  flexShrink:0, transition:'transform .2s',
                  transform: findingsExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                }}>
                  <svg width="9" height="9" viewBox="0 0 24 24" fill="none"
                    stroke="var(--t2)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M6 9l6 6 6-6"/>
                  </svg>
                </div>
              </div>

              {/* Findings list — always show preview, expand on click */}
              {shown.map((f, i) => (
                <div
                  key={f.title + i}
                  style={{
                    animation: f.exiting
                      ? 'fpRemove 0.4s ease forwards'
                      : `blurAppear 0.3s ease ${i * 0.04}s both`,
                    overflow: 'hidden',
                    borderTop: i === 0 && !findingsExpanded ? 'none' : '1px solid rgba(0,0,0,.05)',
                  }}
                >
                  <FindingCard f={f} delay={i * 0.04} />
                </div>
              ))}

              {/* See more / see less button */}
              {extra > 0 && (
                <div
                  onClick={() => setFindingsExpanded(o => !o)}
                  style={{
                    padding:'8px 12px',
                    borderTop:'1px solid var(--bd)',
                    background:'var(--bg1)',
                    fontFamily:'var(--mono)', fontSize:'11px', fontWeight:600,
                    color:'var(--t3)', cursor:'pointer',
                    display:'flex', alignItems:'center', gap:6,
                    transition:'color .15s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.color='var(--t1)'}
                  onMouseLeave={e => e.currentTarget.style.color='var(--t3)'}
                >
                  {findingsExpanded
                    ? <>
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 15l-6-6-6 6"/></svg>
                        See less
                      </>
                    : <>
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6"/></svg>
                        {extra} more issue{extra !== 1 ? 's' : ''}
                      </>
                  }
                </div>
              )}
            </div>

            {/* False positive notice */}
            {removedCount > 0 && (
              <div style={{
                fontSize:11, color:'var(--t3)', padding:'6px 10px',
                textAlign:'center', fontFamily:'monospace',
                animation:'blurAppear 0.3s ease both',
              }}>
                {removedCount} false positive{removedCount !== 1 ? 's' : ''} removed
              </div>
            )}
          </div>
        );
      })()}

      {/* Scan progress — in right panel under findings */}
      {scan && (
        <div style={{
          marginTop:12, borderTop:'1px solid var(--bd)',
          animation:'fadeUp .3s ease both',
        }}>
          <div style={{
            padding:'0 14px', height:38,
            display:'flex', alignItems:'center', justifyContent:'space-between',
            borderBottom:'1px solid var(--bd)', background:'var(--bg1)',
          }}>
            <span style={{fontFamily:'var(--mono)', fontSize:'11px', fontWeight:600, letterSpacing:'.1em', textTransform:'uppercase', color:'var(--t3)'}}>
              Scan Progress
            </span>
            {isRunning && (
              <div style={{display:'flex', alignItems:'center', gap:5}}>
                <div style={{width:5, height:5, borderRadius:'50%', background:'var(--t1)', animation:'pulse 1.2s infinite'}}/>
                <span style={{fontFamily:'var(--mono)', fontSize:'11px', color:'var(--t3)'}}>live</span>
              </div>
            )}
          </div>
          <StepperProgress thoughts={thoughts} isRunning={isRunning} scanId={scanId} />
        </div>
      )}
    </div>
  </div>
)}
    {/* end sb-panel */}

  </>
)}
{/* ═══ DEEP DIVE TAB ════════════════════════════ */}
{tab === 'deepdive' && (
  !scan ? (
    <div style={{flex:1,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',gap:14}}>
      <div style={{fontFamily:'var(--serif)',fontSize:'1.8rem',color:'var(--t2)'}}>Nothing to show yet</div>
      <div style={{fontFamily:'var(--mono)',fontSize:'13px',color:'var(--t3)',maxWidth:260,lineHeight:1.8,textAlign:'center'}}>Run a scan first.</div>
      <button
        onClick={() => setTab('agent')}
        style={{display:'flex',alignItems:'center',gap:7,padding:'8px 16px',borderRadius:999,background:'var(--t1)',border:'none',fontFamily:'var(--mono)',fontSize:'12px',fontWeight:600,color:'#fff',cursor:'pointer',transition:'all .18s',boxShadow:'0 2px 8px rgba(0,0,0,.12)'}}
        onMouseEnter={e => e.currentTarget.style.background='#333'}
        onMouseLeave={e => e.currentTarget.style.background='var(--t1)'}
      >
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 5l-7 7 7 7"/></svg>
        Back to Agent
      </button>
    </div>
  ) : (
    <SecurityChat
      scan={scan}
      findings={af}
      score={score}
      summary={summary}
      thoughts={thoughts}
      isRunning={isRunning}
      scanId={scanId}
    />
  )
)}
{/* ═══ REPORT TAB ══════════════════════════════ */}
{tab === 'report' && (
  <div style={{
    flex: 1, overflowY: 'auto', background: '#fafafa',
    scrollbarWidth: 'thin', scrollbarColor: 'var(--bg3) transparent',
  }}>
    <div style={{ maxWidth: 680, margin: '0 auto', padding: '24px 20px 80px', textAlign: 'left' }}>

      {!summary && findings.length === 0 ? (
  <div style={{ 
    display: 'flex', 
    flexDirection: 'column', 
    alignItems: 'center', 
    justifyContent: 'center',
    textAlign: 'center',
    minHeight: '60vh',
    gap: 12 
  }}>
          <div style={{ fontFamily: 'var(--mono)', fontSize: '13px', fontWeight: 700, color: 'var(--t1)', fontStyle: 'italic' }}>
            Report Not Ready
          </div>
          <div style={{ fontFamily: 'var(--sans)', fontSize: '12px', color: 'var(--t3)', lineHeight: 1.6 }}>
            {isRunning ? 'Agent is still working…' : 'Run a scan first to generate a report.'}
          </div>
          <button
            onClick={() => setTab('agent')}
            style={{
              marginTop: 4, display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '7px 14px', borderRadius: 999, background: 'var(--t1)', border: 'none',
              fontFamily: 'var(--mono)', fontSize: '11px', fontWeight: 600, color: '#fff', cursor: 'pointer',
            }}
            onMouseEnter={e => e.currentTarget.style.opacity = '.8'}
            onMouseLeave={e => e.currentTarget.style.opacity = '1'}
          >
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 5l-7 7 7 7"/>
            </svg>
            Back to Agent
          </button>
        </div>

      ) : (
        <>
          {/* ── Hero ── */}
          <div style={{
            background: '#fff', borderRadius: 14, border: '1px solid var(--bd)',
            padding: '20px 22px', marginBottom: 12,
            display: 'flex', alignItems: 'flex-start', gap: 18,
          }}>
            {score !== null && <div style={{ flexShrink: 0 }}><ScoreRing score={score} size={62}/></div>}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                fontFamily: 'var(--mono)', fontSize: '13px', fontWeight: 700, color: 'var(--t1)',
                marginBottom: 5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {(scan?.target || '').replace(/https?:\/\//, '')}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: summary ? 10 : 0 }}>
                <span style={{ fontFamily: 'var(--mono)', fontSize: '10px', color: 'var(--t3)' }}>{scanDate}</span>
                {score !== null && (() => {
                  const c = scoreColor(score);
                  const r = parseInt(c.slice(1,3),16), g = parseInt(c.slice(3,5),16), b = parseInt(c.slice(5,7),16);
                  return (
                    <span style={{
                      fontFamily: 'var(--mono)', fontSize: '10px', fontWeight: 700,
                      color: c, background: `rgba(${r},${g},${b},0.12)`, padding: '1px 7px', borderRadius: 999,
                    }}>{scoreLabel(score)}</span>
                  );
                })()}
              </div>
              {summary && (
                <div style={{ fontFamily: 'var(--sans)', fontSize: '12px', color: 'var(--t2)', lineHeight: 1.7 }}>
                  {summary}
                </div>
              )}
            </div>
          </div>

          {/* ── Overview ── */}
          <div style={{ background: '#fff', borderRadius: 14, border: '1px solid var(--bd)', padding: '16px 22px', marginBottom: 12 }}>
            <div style={{ fontFamily: 'var(--mono)', fontSize: '10px', fontWeight: 700, color: 'var(--t3)', letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 14 }}>
              Overview
            </div>

            {/* Stat cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: af.length > 0 ? 16 : 0 }}>
              {[
                { l: 'Total',    v: af.length,                                       c: 'var(--t1)' },
                { l: 'Critical', v: af.filter(f => f.severity==='CRITICAL').length,  c: '#dc2626'   },
                { l: 'High',     v: af.filter(f => f.severity==='HIGH').length,      c: '#ea580c'   },
                { l: 'Medium',   v: af.filter(f => f.severity==='MEDIUM').length,    c: '#d97706'   },
              ].map(({ l, v, c }) => (
                <div key={l} style={{ background: '#fafafa', borderRadius: 10, border: '1px solid var(--bd)', padding: '12px 14px' }}>
                  <div style={{ fontFamily: 'var(--mono)', fontSize: '22px', fontWeight: 700, color: c, lineHeight: 1, marginBottom: 4 }}>{v}</div>
                  <div style={{ fontFamily: 'var(--mono)', fontSize: '10px', color: 'var(--t3)', textTransform: 'uppercase', letterSpacing: '.06em' }}>{l}</div>
                </div>
              ))}
            </div>

            {/* Severity breakdown table */}
            {af.length > 0 && (
              <div style={{ overflowX: 'auto', borderRadius: 8, border: '1px solid var(--bd)' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'var(--sans)', fontSize: '12px' }}>
                  <thead>
                    <tr style={{ background: '#f9fafb', borderBottom: '1px solid var(--bd)' }}>
                      {['Severity', 'Count', 'Findings'].map(h => (
                        <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontFamily: 'var(--mono)', fontSize: '10px', fontWeight: 700, color: 'var(--t2)', whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { s: 'CRITICAL', label: 'Critical', c: '#dc2626', bg: 'rgba(220,38,38,0.08)'  },
                      { s: 'HIGH',     label: 'High',     c: '#ea580c', bg: 'rgba(234,88,12,0.08)'  },
                      { s: 'MEDIUM',   label: 'Medium',   c: '#d97706', bg: 'rgba(217,119,6,0.08)'  },
                      { s: 'LOW',      label: 'Low',      c: '#16a34a', bg: 'rgba(22,163,74,0.08)'  },
                    ].map(({ s, label, c, bg }, ri) => {
                      const items = af.filter(f => f.severity === s);
                      if (!items.length) return null;
                      return (
                        <tr key={s} style={{ borderBottom: '1px solid var(--bd)', background: ri % 2 === 0 ? '#fff' : '#fafafa' }}>
                          <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>
                            <span style={{ fontFamily: 'var(--mono)', fontSize: '10px', fontWeight: 700, color: c, background: bg, padding: '1px 7px', borderRadius: 999 }}>{label}</span>
                          </td>
                          <td style={{ padding: '8px 12px', fontFamily: 'var(--mono)', fontWeight: 700, color: c }}>{items.length}</td>
                          <td style={{ padding: '8px 12px', color: 'var(--t2)', lineHeight: 1.6 }}>{items.map(f => f.title).join(', ')}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* ── Recommended Actions ── */}
          {recs.length > 0 && (
            <div style={{ background: '#fff', borderRadius: 14, border: '1px solid var(--bd)', padding: '16px 22px', marginBottom: 12 }}>
              <div style={{ fontFamily: 'var(--mono)', fontSize: '10px', fontWeight: 700, color: 'var(--t3)', letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 14 }}>
                Recommended Actions
              </div>
              <div style={{ borderRadius: 8, overflow: 'hidden', border: '1px solid var(--bd)' }}>
                {recs.map((r, i) => (
                  <div key={i} style={{
                    display: 'flex', alignItems: 'flex-start', gap: 14,
                    padding: '11px 14px', background: i % 2 === 0 ? '#fff' : '#fafafa',
                    borderBottom: i < recs.length - 1 ? '1px solid var(--bd)' : 'none',
                  }}>
                    <span style={{ fontFamily: 'var(--mono)', fontSize: '10px', fontWeight: 700, color: 'var(--t3)', flexShrink: 0, marginTop: 2, minWidth: 20 }}>
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <span style={{ fontFamily: 'var(--sans)', fontSize: '12px', color: 'var(--t2)', lineHeight: 1.65 }}>{r}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Attack Path ── */}
          {scan?.attackNarrative?.length > 0 && (
            <div style={{ background: '#fff', borderRadius: 14, border: '1px solid var(--bd)', marginBottom: 12, overflow: 'hidden' }}>
              <button
                onClick={() => setAttackOpen(o => !o)}
                style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '14px 22px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}
              >
                <div style={{ width: 26, height: 26, borderRadius: '50%', background: 'rgba(220,38,38,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                  </svg>
                </div>
                <span style={{ fontFamily: 'var(--mono)', fontSize: '11px', fontWeight: 700, color: 'var(--t1)', flex: 1 }}>Most Likely Attack Path</span>
                <span style={{ fontFamily: 'var(--mono)', fontSize: '10px', color: 'var(--t3)', background: 'var(--bg2)', padding: '1px 7px', borderRadius: 999 }}>
                  {scan.attackNarrative.length} steps
                </span>
                <div style={{ width: 20, height: 20, borderRadius: '50%', background: 'var(--bg2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'transform .2s', transform: attackOpen ? 'rotate(180deg)' : 'none' }}>
                  <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="var(--t2)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6"/></svg>
                </div>
              </button>
              {attackOpen && (
                <div style={{ borderTop: '1px solid var(--bd)', padding: '16px 22px', display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {scan.attackNarrative.map((step, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                      <span style={{
                        fontFamily: 'var(--mono)', fontSize: '10px', fontWeight: 700,
                        color: '#dc2626', background: 'rgba(220,38,38,0.08)',
                        width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>{i + 1}</span>
                      <span style={{ fontFamily: 'var(--sans)', fontSize: '12px', color: 'var(--t2)', lineHeight: 1.65, paddingTop: 1 }}>{step}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── All Findings table ── */}
          <div style={{ background: '#fff', borderRadius: 14, border: '1px solid var(--bd)', padding: '16px 22px', marginBottom: 12 }}>
            <div style={{
              fontFamily: 'var(--mono)', fontSize: '10px', fontWeight: 700, color: 'var(--t3)',
              letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 14,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <span>All Findings</span>
              <span style={{ fontFamily: 'var(--mono)', fontSize: '10px', fontWeight: 700, color: 'var(--t1)', background: 'var(--bg2)', padding: '1px 7px', borderRadius: 999 }}>{af.length}</span>
            </div>

            {af.length === 0 ? (
              <div style={{ fontFamily: 'var(--mono)', fontSize: '12px', color: 'var(--t3)', fontStyle: 'italic' }}>No issues found — site looks clean.</div>
            ) : (
              <div style={{ overflowX: 'auto', borderRadius: 8, border: '1px solid var(--bd)' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'var(--sans)', fontSize: '12px' }}>
                  <thead>
                    <tr style={{ background: '#f9fafb', borderBottom: '1px solid var(--bd)' }}>
                      {['#', 'Severity', 'Title', 'CVSS', 'Exposes'].map(h => (
                        <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontFamily: 'var(--mono)', fontSize: '10px', fontWeight: 700, color: 'var(--t2)', whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
{af.map((f, i) => {
  const sColor = f.severity==='CRITICAL' ? '#dc2626' : f.severity==='HIGH' ? '#ea580c' : f.severity==='MEDIUM' ? '#d97706' : '#16a34a';
  const sBg    = f.severity==='CRITICAL' ? 'rgba(220,38,38,0.08)' : f.severity==='HIGH' ? 'rgba(234,88,12,0.08)' : f.severity==='MEDIUM' ? 'rgba(217,119,6,0.08)' : 'rgba(22,163,74,0.08)';
  return (
    <tr key={i} style={{ borderBottom: '1px solid var(--bd)', background: i % 2 === 0 ? '#fff' : '#fafafa' }}>

      {/* # */}
      <td style={{ padding: '9px 12px', fontFamily: 'var(--mono)', fontSize: '10px', color: 'var(--t3)', whiteSpace: 'nowrap' }}>
        {String(i+1).padStart(2,'0')}
      </td>

      {/* Severity */}
      <td style={{ padding: '9px 12px', whiteSpace: 'nowrap' }}>
        <span style={{ fontFamily: 'var(--mono)', fontSize: '10px', fontWeight: 700, color: sColor, background: sBg, padding: '1px 7px', borderRadius: 999 }}>
          {f.severity.charAt(0) + f.severity.slice(1).toLowerCase()}
        </span>
      </td>

      {/* Title + description */}
      <td style={{ padding: '9px 12px', color: 'var(--t1)', fontWeight: 500, lineHeight: 1.5 }}>
        <div>{f.title}</div>
        {f.description && (
          <div style={{ fontSize: '11px', color: 'var(--t3)', fontWeight: 400, marginTop: 2 }}>
            {f.description.slice(0, 120)}{f.description.length > 120 ? '…' : ''}
          </div>
        )}
      </td>

      {/* CVSS */}
      <td style={{ padding: '9px 12px', fontFamily: 'var(--mono)', fontSize: '11px', color: 'var(--t2)', whiteSpace: 'nowrap' }}>
        {f.cvss ?? '—'}
      </td>

      {/* Exposes */}
      <td style={{ padding: '9px 12px' }}>
        {f.blastRadius?.totalRows > 0 ? (
          <span style={{
            fontFamily: 'var(--mono)', fontSize: '10px', fontWeight: 700,
            color: '#dc2626', background: 'rgba(220,38,38,0.07)',
            padding: '1px 7px', borderRadius: 999,
            border: '1px solid rgba(220,38,38,0.12)',
            whiteSpace: 'nowrap',
          }}>
            {f.blastRadius.totalRows.toLocaleString()} records
          </span>
        ) : (
          <span style={{ fontFamily: 'var(--mono)', fontSize: '10px', color: 'var(--t3)' }}>—</span>
        )}
      </td>

    </tr>
  );
})}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* ── Actions ── */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', paddingTop: 4 }}>
            <button
              onClick={exportPdf} disabled={pdfBusy}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '8px 16px', borderRadius: 999, background: 'var(--t1)', border: 'none',
                fontFamily: 'var(--mono)', fontSize: '11px', fontWeight: 600,
                color: '#fff', cursor: pdfBusy ? 'default' : 'pointer', opacity: pdfBusy ? 0.6 : 1,
              }}
            >
              {pdfBusy ? <><Spin s={10} c="#fff"/> Generating…</> : 'Download PDF'}
            </button>
            {[
              { label: 'Deep Dive', action: () => setTab('deepdive') },
              { label: 'New Scan',  action: reset },
              { label: '← Agent',  action: () => setTab('agent') },
            ].map(({ label, action }) => (
              <button key={label} onClick={action}
                style={{ padding: '8px 14px', borderRadius: 999, background: 'none', border: '1px solid var(--bd)', fontFamily: 'var(--mono)', fontSize: '11px', fontWeight: 600, color: 'var(--t2)', cursor: 'pointer' }}
                onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg2)'; e.currentTarget.style.color = 'var(--t1)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = 'var(--t2)'; }}
              >{label}</button>
            ))}
          </div>
        </>
      )}
    </div>
  </div>
)}

        </div>

        {/* Mobile bottom nav */}
<nav className={`sb-mobile-nav${scan ? ' scan-active' : ''}`}   style={{
              fontFamily:'var(--mono)', fontSize:'.72rem',
            }}>
  <div className="sb-mnav-inner">
    {(() => {
      const tabs = [
        { id:'agent',    lbl:'Agent',    },
        { id:'deepdive', lbl:'Deep Dive',},
        { id:'report',   lbl:'Report',}
      ];
      const activeIdx = tabs.findIndex(t => t.id === tab);
      return (
        <>
          <div className="sb-mnav-thumb" style={{
            width: `calc(${100/tabs.length}% - 8px)`,
            transform: `translateX(calc(${activeIdx * 100}% + ${activeIdx * 8 + 4}px))`,
          }}/>
          {tabs.map(({id, lbl, icon}) => (
            <button key={id} className={`sb-mnav-btn${tab===id?' on':''}`} onClick={() => setTab(id)}>
              <span className="sb-mnav-ico">{icon}</span>
              <span className="sb-mnav-lbl">{lbl}</span>
            </button>
          ))}
        </>
      );
    })()}
  </div>
</nav>
     
{mobRecentOpen && (
  <>
    <div
      className="sb-mob-recent-backdrop open"
      onClick={closeMobRecent}
    />
    <div className={`sb-mob-recent-sheet${mobRecentClosing ? ' closing' : ''}`}>
      <div className="sb-mob-recent-handle"/>
      <div className="sb-mob-recent-hdr">
        <span className="sb-mob-recent-title">Recent Scans</span>
        <button className="sb-mob-recent-close" onClick={closeMobRecent}>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <path d="M18 6 6 18M6 6l12 12"/>
          </svg>
        </button>
      </div>
      <div className="sb-mob-recent-body">
        {recent.length === 0 ? (
          <div style={{fontFamily:'var(--mono)', fontSize:'.52rem', color:'var(--t3)', padding:'20px 0', textAlign:'center'}}>
            No scans yet
          </div>
        ) : recent.map(s => {
          const statusColor = {done:'var(--green)', stopped:'var(--red)', error:'var(--red)'}[s.status] || 'var(--t3)';
          return (
            <div key={s.scanId} className="sb-hcard" onClick={() => { loadHistoryScan(s); closeMobRecent(); }}>
              <div>
                <div className="sb-hurl">{(s.target||'').replace(/https?:\/\//,'')}</div>
                <div className="sb-hmeta">
                  {s.status}{s.score != null ? ` · Score ${s.score}` : ''}
                  {s.scannedAt ? ` · ${new Date(s.scannedAt).toLocaleDateString()}` : ''}
                </div>
              </div>
              <span style={{fontFamily:'var(--mono)', fontSize:'.44rem', fontWeight:600, color:statusColor}}>
                {s.status === 'done' ? '✓' : s.status === 'error' ? '✕' : '·'}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  </>
)}

      </div>
    </>
  );
}